// components/Canvas.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Konva from 'konva';
import {
  Stage,
  Layer,
  Line,
  Arrow,
  Group,
  Image as KonvaImage,
  Text,
  Rect,
  Circle,
} from 'react-konva';
import useImage from 'use-image';
import EditableText from './EditableText';
import DxfParser from 'dxf-parser';
import fittingsData from './fittingsData';
import fastenersData from './fastenersData';
import { transformFastenerShapes, calculateFastenerBounds, scaleClampLayer } from '../utils/fastenerTransform';
import CylinderComponent from './canvas-objects/CylinderComponent';
import FittingComponent from './canvas-objects/FittingComponent';
import DimensionLineComponent from './canvas-objects/DimensionLineComponent';
import useSnapping from '../hooks/useSnapping';
import { useResponsiveZoom } from '../hooks/useResponsiveZoom';
import { getCylinderPoints, getFittingPoints } from '../utils/geometryUtils';
import ContextMenu from './ContextMenu';
import TransformableObject from './TransformableObject';
import { hasZerkaloCoating, hasMatteCoating } from './AutoConstructor/pipeThicknessUtils';
import { layoutProjection, getViewSnapPoints } from '../utils/topViewProjection';
import { projectTopView } from '../utils/igesExporter';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import FastenerConfigDialog from './FastenerConfigDialog';
import FastenersTable from './FastenersTable';
import WorkshopTable from './WorkshopTable';
import { getDrawingGabaritMm } from './AutoConstructor/hydraulicSeparatorTotalHeight';

function getFittingWidth(fitting) {
  return fitting.width;
}

function dimensionMillimeters(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const whole = Math.round(n);
  const snapped = Math.abs(n - whole) <= 0.05 ? whole : n;
  return snapped.toFixed(2);
}

function getFittingHeight(fitting) {
  return fitting.height;
}

function isCountableFittingForReferenceNote(fitting) {
  return fitting && fitting.type !== 'fastener' && !fitting.isHidden;
}

function getFittingCatalogKey(fitting) {
  return fitting.itemKey || fitting.key || '';
}

/** Вариант «Размер для справок» с пунктом про обрешётку (anno-init-8) */
function computeNeedsCratingReferenceNote(fittings, metadata) {
  const list = fittings || [];
  const hasFlanges = list.some(
    (f) =>
      isCountableFittingForReferenceNote(f) &&
      (f.key?.startsWith('Flanec_') || f.itemKey?.startsWith('Flanec_'))
  );
  if (hasFlanges) return true;

  const hasElbows = list.some((f) => {
    if (!isCountableFittingForReferenceNote(f)) return false;
    const key = getFittingCatalogKey(f);
    if (key.startsWith('Otvod_')) return true;
    return fittingsData[key]?.category === 'Отводы';
  });
  if (hasElbows) return true;

  return getDrawingGabaritMm(metadata) > 1000;
}

const convertDxfToKonvaShapes = (dxf) => {
  const shapes = [];
  let minY = Infinity;
  let maxY = -Infinity;

  // Первый проход для определения minY и maxY
  dxf.entities.forEach((entity) => {
    if (entity.type === 'LINE') {
      if (entity.start && entity.end) {
        const ys = [entity.start.y, entity.end.y];
        minY = Math.min(minY, ...ys);
        maxY = Math.max(maxY, ...ys);
      } else if (Array.isArray(entity.vertices) && entity.vertices.length >= 2) {
        entity.vertices.forEach((vertex) => {
          const y = vertex.y;
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        });
      } else {
        console.warn(`Сущность типа LINE не имеет start/end или vertices:`, entity);
      }
    } else if (entity.type === 'CIRCLE' || entity.type === 'ARC') {
      if (entity.center && typeof entity.radius === 'number') {
        const y = entity.center.y;
        const radius = entity.radius;
        const ys = [y - radius, y + radius];
        minY = Math.min(minY, ...ys);
        maxY = Math.max(maxY, ...ys);
      }
    } else if (entity.type === 'LWPOLYLINE' || entity.type === 'POLYLINE') {
      if (Array.isArray(entity.vertices)) {
        entity.vertices.forEach((vertex) => {
          const y = vertex.y;
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        });
      }
    }
    // Добавьте обработку других типов сущностей при необходимости
  });

  // Отладочное сообщение

  // Определяем функцию invertY после того, как minY и maxY известны
  const invertY = (y) => maxY - y;

  // Рекурсивная функция для обработки сущностей с учетом трансформаций
  const processEntities = (
    entities,
    parentTransform = { x: 0, y: 0 },
    depth = 0
  ) => {
    entities.forEach((entity, index) => {
      const indent = '  '.repeat(depth); // Отступ для логирования

      // Определяем, является ли слой масштабируемым
      const layerName = entity.layer ? entity.layer.toUpperCase() : 'DEFAULT_LAYER';
      const isThreadLayer = layerName === 'THREAD' || layerName === 'РЕЗЬБА';
      const isScalableLayer = !isThreadLayer; // THREAD/РЕЗЬБА масштабируем только по X отдельно

      if (entity.type === 'LINE') {
        if (entity.start && entity.end) {
          const startX = entity.start.x + parentTransform.x;
          const startY = invertY(entity.start.y + parentTransform.y);
          const endX = entity.end.x + parentTransform.x;
          const endY = invertY(entity.end.y + parentTransform.y);
          shapes.push({
            type: 'line',
            points: [startX, startY, endX, endY],
            scalable: isScalableLayer,
            scaleOnlyX: isThreadLayer,
            layer: layerName,
          });
        } else if (Array.isArray(entity.vertices) && entity.vertices.length >= 2) {
          const points = [];
          entity.vertices.forEach((vertex) => {
            const x = vertex.x + parentTransform.x;
            const y = invertY(vertex.y + parentTransform.y);
            points.push(x, y);
          });
          shapes.push({
            type: 'polyline',
            points: points,
            scalable: isScalableLayer,
            scaleOnlyX: isThreadLayer,
            layer: layerName,
          });
        } else {
          console.warn(
            `${indent}Сущность типа LINE не имеет start/end или vertices:`,
            entity
          );
        }
      } else if (entity.type === 'LWPOLYLINE' || entity.type === 'POLYLINE') {
        if (Array.isArray(entity.vertices) && entity.vertices.length >= 2) {
          const points = [];
          entity.vertices.forEach((vertex) => {
            const x = vertex.x + parentTransform.x;
            const y = invertY(vertex.y + parentTransform.y);
            points.push(x, y);
          });
          shapes.push({
            type: 'polyline',
            points: points,
            closed: entity.closed || false,
            scalable: isScalableLayer,
            scaleOnlyX: isThreadLayer,
            layer: layerName,
          });
        } else {
          console.warn(
            `${indent}Сущность типа ${entity.type} не имеет достаточного количества вершин:`,
            entity
          );
        }
      } else if (entity.type === 'CIRCLE') {
        if (entity.center && typeof entity.radius === 'number') {
          const centerX = entity.center.x + parentTransform.x;
          const centerY = invertY(entity.center.y + parentTransform.y);
          shapes.push({
            type: 'circle',
            x: centerX,
            y: centerY,
            radius: entity.radius,
            scalable: isScalableLayer,
            scaleOnlyX: isThreadLayer,
            layer: layerName,
          });
        } else {
          console.warn(
            `${indent}Сущность типа CIRCLE не имеет center или radius:`,
            entity
          );
        }
      } else if (entity.type === 'ARC') {
        if (
          entity.center &&
          typeof entity.radius === 'number' &&
          typeof entity.startAngle === 'number' &&
          typeof entity.endAngle === 'number'
        ) {
          const centerX = entity.center.x + parentTransform.x;
          const centerY = invertY(entity.center.y + parentTransform.y);
          
          // При инверсии Y нужно также инвертировать углы дуги
          // Зеркалим углы относительно оси X: новый угол = -старый угол
          const startAngle = -entity.endAngle; // меняем местами start и end
          const endAngle = -entity.startAngle;
          
          shapes.push({
            type: 'arc',
            x: centerX,
            y: centerY,
            radius: entity.radius,
            startAngle: startAngle,
            endAngle: endAngle,
            scalable: isScalableLayer,
            scaleOnlyX: isThreadLayer,
            layer: layerName,
          });
        } else {
          console.warn(
            `${indent}Сущность типа ARC не имеет необходимых свойств:`,
            entity
          );
        }
      }
      // Обработка других типов сущностей
    });
  };

  // Вызываем функцию processEntities
  processEntities(dxf.entities);

  return shapes;
};

function Canvas({
  onClearTrigger,
  resetClearTrigger,
  isDrawingEnabled,
  isAddAnnotationModeEnabled,
  isDimensionLineEnabled,
  setIsDimensionLineEnabled,
  isBalloonModeEnabled,
  isLandscape,
  zoom,
  setZoom,
  textZoom,
  setTextZoom,
  positionTextZoom,
  setPositionTextZoom,
  // gridSize удалён
  snapThreshold,
  snappingMode,
  setUndoRef,
  setRedoRef,
  setCanUndo,
  setCanRedo,
  onAddFittingTrigger,
  resetAddFittingTrigger,
  onApplyLinearPatternTrigger,
  linearPatternParams,
  resetApplyLinearPatternTrigger,
  setGetObjectsRef,
  onObjectsChanged,
  prices,
  setSaveDrawingRef,
  setCanvasMetadataRef,
  setLoadDrawingRef,
  loadDrawingData,
  setStageRef,
  showWeldMarkers = true,
  workshopInfo,
  weldingTotal = 0,
  userInitials = '',
  addViewRequest = null,
}) {
  // Хуки
  const [lines, setLines] = useState([]);
  const [cylinders, setCylinders] = useState([]);
  const cylindersRef = useRef([]);
  const [annotations, setAnnotations] = useState([]);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const selectedAnnotation = React.useMemo(() => {
    return annotations.find((a) => a.id === selectedAnnotationId) || null;
  }, [annotations, selectedAnnotationId]);
  
  // Состояние таблицы крепежей
  const [fastenersTableState, setFastenersTableState] = useState(null);
  const [isFastenersTableSelected, setIsFastenersTableSelected] = useState(false);
  const [workshopTableState, setWorkshopTableState] = useState(null);
  const [isWorkshopTableSelected, setIsWorkshopTableSelected] = useState(false);
  const [workshopLine, setWorkshopLine] = useState(null); // Линия под текстом цеха
  const [balloons, setBalloons] = useState([]);
  const [dimensionLines, setDimensionLines] = useState([]);
  const dimensionLinesRef = useRef([]);
  
  // Состояния для диалога настройки крепежа
  const [fastenerDialogOpen, setFastenerDialogOpen] = useState(false);
  const [pendingFastener, setPendingFastener] = useState(null); // { fastenerKey, x, y }
  
  const [isDrawing, setIsDrawing] = useState(false);
  const backgroundPath = isLandscape ? '/A3React(NPOV).png' : '/A3React(POV).png';
  const [backgroundImage] = useImage(
    process.env.PUBLIC_URL + backgroundPath
  );

  const [highlightedSide, setHighlightedSide] = useState(null);
  const [highlightedPoint, setHighlightedPoint] = useState(null);
  const [selectedElements, setSelectedElements] = useState([]);
  const [currentDimension, setCurrentDimension] = useState(null);
  const [selectedCylinderId, setSelectedCylinderId] = useState(null);
  const [selectedDimensionId, setSelectedDimensionId] = useState(null);
  const [selectedFittingId, setSelectedFittingId] = useState(null);
  const [isLKeyPressed, setIsLKeyPressed] = useState(false);
  const [pAttachmentMode, setPAttachmentMode] = useState('off'); // Режим привязки: 'off', 'points', 'edges'
  const isPKeyPressed = pAttachmentMode !== 'off'; // Для обратной совместимости
  const [pointAttachmentMode, setPointAttachmentMode] = useState(null); // { firstPoint: {objectId, objectType, pointIndex, x, y}, secondPoint: null }
  
  // Состояния для управления масштабом через клавиши + колесико
  const [isMKeyPressed, setIsMKeyPressed] = useState(false); // M - общий масштаб
  const [isCommaKeyPressed, setIsCommaKeyPressed] = useState(false); // < (Б) - масштаб текста размеров
  const [isDotKeyPressed, setIsDotKeyPressed] = useState(false); // > (Ю) - масштаб текста позиции
  const [objectConstraints, setObjectConstraints] = useState([]); // Массив связей между точками [{id, obj1: {objectId, objectType, pointIndex}, obj2: {objectId, objectType, pointIndex}}]
  const objectConstraintsRef = useRef([]); // Ref для быстрого доступа без пересоздания useEffect
  
  // Состояние для отслеживания сварок
  const [welds, setWelds] = useState([]); // Массив сварок [{id, obj1: {objectId, objectType, edgeType}, obj2: {objectId, objectType, edgeType}, x, y}]
  
  // Состояние для метаданных чертежа (параметры из автоконструктора)
  const [canvasMetadata, setCanvasMetadata] = useState({});
  
  // Состояние для диалога предупреждения о зеркальном покрытии
  const [mirrorWarningDialog, setMirrorWarningDialog] = useState({
    open: false,
    unavailablePipes: [], // [{diameter, count}]
  });
  
  const [squareProfileWarningDialog, setSquareProfileWarningDialog] = useState(false);
  
  // Синхронизируем state с ref
  useEffect(() => {
    objectConstraintsRef.current = objectConstraints;
  }, [objectConstraints]);
  
  // Получаем текущий scale для адаптивного масштабирования Canvas
  const responsiveScale = useResponsiveZoom();

  // Эффективный zoom = пользовательский zoom * адаптивный scale
  // Используется для компенсации масштаба в размерах элементов и координатах
  const effectiveZoom = zoom * responsiveScale;

  // Определяем размер холста с учётом адаптивного масштаба
  const basePortraitWidth = 793;
  const basePortraitHeight = 1122;
  const canvasWidth = (isLandscape ? basePortraitWidth * 2 : basePortraitWidth) * responsiveScale;
  const canvasHeight = basePortraitHeight * responsiveScale;
  
  // Функция для получения абсолютных координат точки объекта
  const getObjectPointAbsolutePosition = useCallback((objectId, objectType, pointIndex) => {
    const stage = stageRef.current;
    if (!stage) return null;
    
    // Находим Group объекта
    const group = stage.findOne(`#${objectId}`);
    if (!group) return null;
    
    // Получаем трансформацию Group
    const transform = group.getAbsoluteTransform();
    
    // Находим объект в массивах
    let obj = null;
    if (objectType === 'fitting') {
      obj = fittingsRef.current.find(f => f.id === objectId);
    } else if (objectType === 'cylinder') {
      obj = cylindersRef.current.find(c => c.id === objectId);
    }
    
    if (!obj) return null;
    
    // Получаем локальные координаты точки
    let localPoint = null;
    if (objectType === 'fitting') {
      const points = getFittingPoints({
        x: obj.x || 0,
        y: obj.y || 0,
        width: obj.width,
        height: obj.height,
        snapPoints: obj.snapPoints,
      });
      localPoint = points[pointIndex];
    } else if (objectType === 'cylinder') {
      const points = getCylinderPoints({
        x: obj.x || 0,
        y: obj.y || 0,
        width: obj.width,
        height: obj.height,
      });
      localPoint = points[pointIndex];
    }
    
    if (!localPoint) return null;
    
    // Преобразуем локальные координаты в абсолютные
    const absPoint = transform.point(localPoint);
    return absPoint;
  }, []);
  
  // Функция для получения абсолютных координат центра стенки объекта
  const getObjectEdgeCenter = useCallback((objectId, objectType, edgeType) => {
    const stage = stageRef.current;
    if (!stage) return null;
    
    // Находим Group объекта
    const group = stage.findOne(`#${objectId}`);
    if (!group) return null;
    
    // Получаем трансформацию Group
    const transform = group.getAbsoluteTransform();
    
    // Находим объект в массивах
    let obj = null;
    if (objectType === 'fitting') {
      obj = fittingsRef.current.find(f => f.id === objectId);
    } else if (objectType === 'cylinder') {
      obj = cylindersRef.current.find(c => c.id === objectId);
    }
    
    if (!obj) return null;
    
    // edgeType теперь уже локальный (из constraint), не нужно преобразование
    // Вычисляем локальные координаты центра стенки
    const x = obj.x || 0;
    const y = obj.y || 0;
    const width = obj.width;
    const height = obj.height;
    
    let localCenter = null;
    switch (edgeType) {
      case 'top':
        localCenter = { x: x + width / 2, y: y };
        break;
      case 'bottom':
        localCenter = { x: x + width / 2, y: y + height };
        break;
      case 'left':
        localCenter = { x: x, y: y + height / 2 };
        break;
      case 'right':
        localCenter = { x: x + width, y: y + height / 2 };
        break;
      default:
        return null;
    }
    
    // Преобразуем локальные координаты в абсолютные
    const absCenter = transform.point(localCenter);
    return absCenter;
  }, []);
  
  // Функция для проверки, привязана ли точка объекта
  const isPointConstrained = useCallback((objectId, objectType, pointIndex) => {
    const constraints = objectConstraintsRef.current;
    return constraints.some(c => 
      c.type === 'point' &&
      ((c.obj1.objectId === objectId && c.obj1.objectType === objectType && c.obj1.pointIndex === pointIndex) ||
       (c.obj2.objectId === objectId && c.obj2.objectType === objectType && c.obj2.pointIndex === pointIndex))
    );
  }, []);
  
  // Функция для проверки, привязана ли стенка объекта
  const isEdgeConstrained = useCallback((objectId, objectType, edgeType) => {
    const constraints = objectConstraintsRef.current;
    return constraints.some(c => 
      c.type === 'edge' &&
      ((c.obj1.objectId === objectId && c.obj1.objectType === objectType && c.obj1.edgeType === edgeType) ||
       (c.obj2.objectId === objectId && c.obj2.objectType === objectType && c.obj2.edgeType === edgeType))
    );
  }, []);
  
  // Функция для получения глобальной позиции края и его направления (для определения сварки)
  // Функция для получения всех отрезков объекта (в глобальных координатах)
  const getObjectSegments = useCallback((objectId, objectType) => {
    const obj = objectType === 'cylinder' 
      ? cylindersRef.current.find(c => c.id === objectId)
      : fittingsRef.current.find(f => f.id === objectId);
    
    if (!obj) return [];
    
    const { x, y, width, height, rotation = 0 } = obj;
    // Получаем параметры Group - offset и позицию
    const offsetX = obj.offsetX || 0;
    const offsetY = obj.offsetY || 0;
    const groupX = obj.groupX || 0;
    const groupY = obj.groupY || 0;
    
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    
    const segments = [];
    
    // Функция для преобразования локальных координат в глобальные
    // Учитывает offset (центр вращения) и groupX/groupY (позиция Group)
    const localToGlobal = (localX, localY) => {
      // Вычитаем offset чтобы получить координаты относительно центра вращения
      const dx = localX - offsetX;
      const dy = localY - offsetY;
      
      // Вращаем вокруг центра вращения
      const rotatedX = dx * cos - dy * sin;
      const rotatedY = dx * sin + dy * cos;
      
      // Добавляем позицию Group
      return {
        x: groupX + rotatedX,
        y: groupY + rotatedY,
      };
    };
    
    if (objectType === 'fitting' && obj.shapes && Array.isArray(obj.shapes)) {
      // Для фитингов: извлекаем реальные линии из shapes
      // Координаты shapes заданы относительно внутренней Group (x, y),
      // поэтому добавляем x, y к координатам
      obj.shapes.forEach((shape, shapeIdx) => {
        if (shape.type === 'line' && shape.points && shape.points.length >= 4) {
          // Line/Polyline: points = [x1, y1, x2, y2, x3, y3, ...]
          for (let i = 0; i < shape.points.length - 2; i += 2) {
            const localStart = { x: x + shape.points[i], y: y + shape.points[i + 1] };
            const localEnd = { x: x + shape.points[i + 2], y: y + shape.points[i + 3] };
            
            segments.push({
              start: localToGlobal(localStart.x, localStart.y),
              end: localToGlobal(localEnd.x, localEnd.y),
              name: `line_${shapeIdx}_${i / 2}`,
            });
          }
          
          // Если closed, добавляем замыкающий отрезок
          if (shape.closed && shape.points.length >= 4) {
            const lastIdx = shape.points.length - 2;
            const localStart = { x: x + shape.points[lastIdx], y: y + shape.points[lastIdx + 1] };
            const localEnd = { x: x + shape.points[0], y: y + shape.points[1] };
            
            segments.push({
              start: localToGlobal(localStart.x, localStart.y),
              end: localToGlobal(localEnd.x, localEnd.y),
              name: `line_${shapeIdx}_close`,
            });
          }
        } else if (shape.type === 'polyline' && shape.points && shape.points.length >= 4) {
          // Polyline: аналогично line
          for (let i = 0; i < shape.points.length - 2; i += 2) {
            const localStart = { x: x + shape.points[i], y: y + shape.points[i + 1] };
            const localEnd = { x: x + shape.points[i + 2], y: y + shape.points[i + 3] };
            
            segments.push({
              start: localToGlobal(localStart.x, localStart.y),
              end: localToGlobal(localEnd.x, localEnd.y),
              name: `polyline_${shapeIdx}_${i / 2}`,
            });
          }
          
          if (shape.closed && shape.points.length >= 4) {
            const lastIdx = shape.points.length - 2;
            const localStart = { x: x + shape.points[lastIdx], y: y + shape.points[lastIdx + 1] };
            const localEnd = { x: x + shape.points[0], y: y + shape.points[1] };
            
            segments.push({
              start: localToGlobal(localStart.x, localStart.y),
              end: localToGlobal(localEnd.x, localEnd.y),
              name: `polyline_${shapeIdx}_close`,
            });
          }
        }
        // TODO: можно добавить поддержку circle и arc, если нужно
      });
    }
    
    // Для цилиндров или фитингов без shapes: используем прямоугольник
    // Для фитингов с shapes: ДОБАВЛЯЕМ стороны прямоугольника (они могут быть не в DXF)
    if (objectType === 'cylinder' || segments.length === 0 || 
        (objectType === 'fitting' && obj.shapes && obj.shapes.length > 0)) {
      // Локальные углы прямоугольника (относительно внутренней Group)
      const localCorners = [
        { x: x, y: y },                      // top-left
        { x: x + width, y: y },              // top-right
        { x: x + width, y: y + height },     // bottom-right
        { x: x, y: y + height },             // bottom-left
      ];
      
      const globalCorners = localCorners.map(corner => 
        localToGlobal(corner.x, corner.y)
      );
      
      // Проверяем, является ли объект листом (squareType === 'sheet')
      const isSheet = objectType === 'cylinder' && obj.pipeSpec?.squareType === 'sheet';
      
      if (isSheet) {
        // Для листа добавляем только верхнюю стенку (лист - плоская пластина без нижней стенки)
        segments.push(
          { start: globalCorners[0], end: globalCorners[1], name: 'rect_top' }
        );
      } else {
        // Для профилей и круглых труб добавляем все 4 стенки
        segments.push(
          { start: globalCorners[0], end: globalCorners[1], name: 'rect_top' },
          { start: globalCorners[1], end: globalCorners[2], name: 'rect_right' },
          { start: globalCorners[2], end: globalCorners[3], name: 'rect_bottom' },
          { start: globalCorners[3], end: globalCorners[0], name: 'rect_left' }
        );
      }
    }
    
    return segments;
  }, []);
  
  // Функция для вычисления минимального расстояния между двумя отрезками
  const segmentToSegmentDistance = useCallback((seg1Start, seg1End, seg2Start, seg2End) => {
    // Вычисляем расстояния от концов одного отрезка до другого отрезка
    const pointToSegment = (point, segStart, segEnd) => {
      const dx = segEnd.x - segStart.x;
      const dy = segEnd.y - segStart.y;
      const lengthSquared = dx * dx + dy * dy;
      
      if (lengthSquared === 0) {
        return Math.hypot(point.x - segStart.x, point.y - segStart.y);
      }
      
      let t = ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / lengthSquared;
      t = Math.max(0, Math.min(1, t));
      
      const projX = segStart.x + t * dx;
      const projY = segStart.y + t * dy;
      
      return Math.hypot(point.x - projX, point.y - projY);
    };
    
    // 4 расстояния: от каждого конца одного отрезка до другого отрезка
    const dist1 = pointToSegment(seg1Start, seg2Start, seg2End);
    const dist2 = pointToSegment(seg1End, seg2Start, seg2End);
    const dist3 = pointToSegment(seg2Start, seg1Start, seg1End);
    const dist4 = pointToSegment(seg2End, seg1Start, seg1End);
    
    return Math.min(dist1, dist2, dist3, dist4);
  }, []);
  
  // Функция для проверки пересечения двух отрезков
  const getSegmentIntersection = useCallback((seg1Start, seg1End, seg2Start, seg2End, threshold = 1) => {
    const x1 = seg1Start.x, y1 = seg1Start.y;
    const x2 = seg1End.x, y2 = seg1End.y;
    const x3 = seg2Start.x, y3 = seg2Start.y;
    const x4 = seg2End.x, y4 = seg2End.y;
    
    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    
    // Сначала проверяем точное пересечение
    if (Math.abs(denom) > 0.0001) {
      const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
      const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
      
      // Проверяем, что пересечение происходит внутри обоих отрезков
      if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
        return {
          x: x1 + t * (x2 - x1),
          y: y1 + t * (y2 - y1),
          distance: 0,
        };
      }
    }
    
    // Если точного пересечения нет, проверяем близость
    const minDist = segmentToSegmentDistance(seg1Start, seg1End, seg2Start, seg2End);
    
    if (minDist <= threshold) {
      // Вычисляем среднюю точку между ближайшими точками отрезков
      return {
        x: (x1 + x2 + x3 + x4) / 4,
        y: (y1 + y2 + y3 + y4) / 4,
        distance: minDist,
      };
    }
    
    return null;
  }, [segmentToSegmentDistance]);
  
  
  // Функция для получения абсолютных точек привязки объекта (с учётом трансформации)
  const getObjectSnapPoints = useCallback((objectId, objectType) => {
    const obj = objectType === 'cylinder' 
      ? cylindersRef.current.find(c => c.id === objectId)
      : fittingsRef.current.find(f => f.id === objectId);
    
    if (!obj) return [];
    
    const { x, y, width, height, rotation = 0, snapPoints } = obj;
    const offsetX = obj.offsetX || 0;
    const offsetY = obj.offsetY || 0;
    const groupX = obj.groupX || 0;
    const groupY = obj.groupY || 0;
    
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    
    // Функция для преобразования локальных координат в глобальные
    const localToGlobal = (localX, localY) => {
      const dx = localX - offsetX;
      const dy = localY - offsetY;
      const rotatedX = dx * cos - dy * sin;
      const rotatedY = dx * sin + dy * cos;
      return {
        x: groupX + rotatedX,
        y: groupY + rotatedY,
      };
    };
    
    // Получаем локальные точки привязки
    let localPoints;
    if (objectType === 'fitting' && snapPoints && Array.isArray(snapPoints)) {
      localPoints = getFittingPoints({ x, y, width, height, snapPoints });
    } else {
      localPoints = getCylinderPoints({ x, y, width, height });
    }
    
    // Преобразуем в глобальные координаты
    return localPoints.map(p => localToGlobal(p.x, p.y));
  }, []);
  
  // Функция для обнаружения всех сварок между объектами
  const detectWelds = useCallback(() => {
    const detectedWelds = [];
    const allObjects = [];
    
    // Собираем все объекты (цилиндры и фитинги)
    cylindersRef.current.forEach(cyl => {
      allObjects.push({ id: cyl.id, type: 'cylinder', obj: cyl });
    });
    
    fittingsRef.current.forEach(fit => {
      // Исключаем крепежи из определения сварок
      if (fit.type === 'fastener') {
        return;
      }
      // Исключаем скрытые фитинги (фиктивные junction-фитинги для IGES)
      if (fit.isHidden) {
        return;
      }
      allObjects.push({ id: fit.id, type: 'fitting', obj: fit });
    });
    
    // Проверяем все пары объектов
    for (let i = 0; i < allObjects.length; i++) {
      for (let j = i + 1; j < allObjects.length; j++) {
        const obj1 = allObjects[i];
        const obj2 = allObjects[j];
        
        // Пропускаем, если это один и тот же объект (не должно быть, но на всякий случай)
        if (obj1.id === obj2.id && obj1.type === obj2.type) {
          continue;
        }
        
        // === ПРОВЕРКА СПЕЦИАЛЬНЫХ ТИПОВ ФИТИНГОВ ===
        // Получаем itemKey для определения типа фитинга
        const itemKey1 = obj1.type === 'fitting' ? obj1.obj.itemKey : null;
        const itemKey2 = obj2.type === 'fitting' ? obj2.obj.itemKey : null;
        
        // Функция для определения типа фитинга по itemKey
        const getFittingWeldRule = (itemKey) => {
          if (!itemKey) return null;
          
          // Свободные оцинкованные фланцы - никогда не имеют сварки
          if (itemKey.includes('_galv')) return 'noWeld';
          
          // Свободные нержавеющие фланцы (но не штампованные и не приварные)
          if (itemKey.includes('_ss') && !itemKey.includes('_ss_stamped') && !itemKey.includes('_welded')) return 'noWeld';
          
          // Свободные штампованные нержавеющие фланцы - никогда не имеют сварки
          if (itemKey.includes('_ss_stamped')) return 'noWeld';
          
          // Накидные гайки - никогда не имеют сварки
          if (itemKey.startsWith('Nak_Gaika')) return 'noWeld';
          
          // Приварные нержавеющие фланцы - всегда 2 прямые сварки
          if (itemKey.includes('_welded')) return 'twoStraight';
          
          // Шайбы с наружной резьбой - всегда 1 прямая сварка
          if (itemKey.startsWith('Shaib_Nar_Rezba')) return 'oneStraight';
          
          return null;
        };
        
        // Функция для получения диаметра объекта
        const getObjectDiameter = (obj) => {
          if (obj.type === 'cylinder') {
            // Для круглых труб берём диаметр из pipeSpec
            const ps = obj.obj.pipeSpec;
            if (ps?.shape === 'round' && ps.diameter) {
              return Number(ps.diameter);
            }
            // Для квадратных труб вычисляем эквивалентный диаметр через периметр
            // Формула: периметр = 2*(a+b), эквивалентный диаметр = периметр / π
            if (ps?.shape === 'square' && ps.profile) {
              const parts = String(ps.profile).split('x');
              if (parts.length === 2) {
                const a = Number(parts[0]) || 0;
                const b = Number(parts[1]) || 0;
                const perimeter = 2 * (a + b); // Периметр профиля
                const equivalentDiameter = perimeter / Math.PI; // Эквивалентный диаметр
                return equivalentDiameter;
              }
            }
            // Если нет pipeSpec, пробуем взять меньший размер (диаметр)
            const w = obj.obj.width || 0;
            const h = obj.obj.height || 0;
            return Math.min(w, h) || Math.max(w, h); // Возвращаем ненулевое значение
          } else if (obj.type === 'fitting') {
            // Для фитингов берём диаметр из fittingsData
            const itemKey = obj.obj.itemKey;
            const fittingInfo = fittingsData[itemKey];
            
            // Специальная логика для квадратных прямых заглушек (Zaglushka_PR_NxN)
            // Диаметр = периметр / π = 4 * сторона / 3.14
            if (itemKey && itemKey.startsWith('Zaglushka_PR_')) {
              const sizePart = itemKey.replace('Zaglushka_PR_', '');
              // Проверяем, квадратная ли это заглушка (содержит "x" или "х")
              if (sizePart.includes('x') || sizePart.includes('х')) {
                const parts = sizePart.split(/x|х/);
                if (parts.length === 2) {
                  const a = Number(parts[0]) || 0;
                  const b = Number(parts[1]) || 0;
                  // Периметр = 2*(a+b), эквивалентный диаметр = периметр / π
                  const perimeter = 2 * (a + b);
                  const equivalentDiameter = perimeter / Math.PI;
                  return equivalentDiameter;
                }
              }
            }
            
            if (fittingInfo?.diameter) {
              return Number(fittingInfo.diameter);
            }
            // Проверяем selfFacing диаметр
            if (obj.obj.isSelfFacing && fittingInfo?.selfFacing?.diameter) {
              return Number(fittingInfo.selfFacing.diameter);
            }
            // Если нет явного диаметра, используем ширину объекта
            const w = obj.obj.width || 0;
            const h = obj.obj.height || 0;
            return Math.min(w, h) || Math.max(w, h);
          }
          return 0;
        };
        
        // Функция для получения отображаемого диаметра (для UI)
        const getDisplayDiameter = (obj) => {
          if (obj.type === 'cylinder') {
            const ps = obj.obj.pipeSpec;
            // Для квадратных труб показываем профиль как "80х80"
            if (ps?.shape === 'square' && ps.profile) {
              return String(ps.profile).replace('x', 'х'); // Заменяем английскую x на русскую х
            }
          }
          if (obj.type === 'fitting') {
            const itemKey = obj.obj.itemKey;
            // Для квадратных прямых заглушек показываем размер как "80х80"
            if (itemKey && itemKey.startsWith('Zaglushka_PR_')) {
              const sizePart = itemKey.replace('Zaglushka_PR_', '');
              if (sizePart.includes('x') || sizePart.includes('х')) {
                return sizePart; // Возвращаем "80х80", "20х20" и т.д.
              }
            }
          }
          // Для остальных возвращаем null - будет использоваться числовой диаметр
          return null;
        };
        
        // Получаем диаметры объектов и выбираем меньший (но не 0)
        const diameter1 = getObjectDiameter(obj1);
        const diameter2 = getObjectDiameter(obj2);
        // Выбираем меньший ненулевой диаметр
        let weldDiameter;
        if (diameter1 > 0 && diameter2 > 0) {
          weldDiameter = Math.min(diameter1, diameter2);
        } else {
          weldDiameter = diameter1 || diameter2 || 0;
        }
        
        // Получаем отображаемый диаметр (для квадратных заглушек - "80х80")
        const displayDiam1 = getDisplayDiameter(obj1);
        const displayDiam2 = getDisplayDiameter(obj2);
        // Выбираем тот, у которого меньший расчётный диаметр
        let weldDisplayDiameter;
        if (diameter1 > 0 && diameter2 > 0) {
          weldDisplayDiameter = diameter1 <= diameter2 ? (displayDiam1 || diameter1) : (displayDiam2 || diameter2);
        } else {
          weldDisplayDiameter = diameter1 ? (displayDiam1 || diameter1) : (displayDiam2 || diameter2);
        }
        
        
        const rule1 = getFittingWeldRule(itemKey1);
        const rule2 = getFittingWeldRule(itemKey2);
        
        // Если любой из объектов - "без сварки", пропускаем эту пару
        if (rule1 === 'noWeld' || rule2 === 'noWeld') {
          continue;
        }
        
        // === ПРОВЕРКА ПРОФИЛЬ + ЛИСТ ===
        // Если один объект - профиль, а другой - лист, то фиксированная сварка 100₽
        const isProfile1 = obj1.type === 'cylinder' && obj1.obj.pipeSpec?.squareType === 'profile';
        const isProfile2 = obj2.type === 'cylinder' && obj2.obj.pipeSpec?.squareType === 'profile';
        const isSheet1 = obj1.type === 'cylinder' && obj1.obj.pipeSpec?.squareType === 'sheet';
        const isSheet2 = obj2.type === 'cylinder' && obj2.obj.pipeSpec?.squareType === 'sheet';
        
        const isProfileSheetPair = (isProfile1 && isSheet2) || (isProfile2 && isSheet1);
        
        // Вспомогательная функция для получения world bounding box объекта
        const getWorldBoundingBox = (objData) => {
          const offsetX = objData.offsetX || 0;
          const offsetY = objData.offsetY || 0;
          const groupX = objData.groupX || 0;
          const groupY = objData.groupY || 0;
          const rotation = objData.rotation || 0;
          const rad = (rotation * Math.PI) / 180;
          const cos = Math.cos(rad);
          const sin = Math.sin(rad);
          
          // Углы bounding box в локальных координатах
          const corners = [
            { x: objData.x, y: objData.y },
            { x: objData.x + objData.width, y: objData.y },
            { x: objData.x + objData.width, y: objData.y + objData.height },
            { x: objData.x, y: objData.y + objData.height },
          ].map(corner => {
            const dx = corner.x - offsetX;
            const dy = corner.y - offsetY;
            return {
              x: groupX + dx * cos - dy * sin,
              y: groupY + dx * sin + dy * cos,
            };
          });
          
          return {
            minX: Math.min(...corners.map(c => c.x)),
            maxX: Math.max(...corners.map(c => c.x)),
            minY: Math.min(...corners.map(c => c.y)),
            maxY: Math.max(...corners.map(c => c.y)),
          };
        };
        
        // Функция проверки пересечения/соприкосновения двух bounding box'ов
        const boundingBoxesTouch = (bb1, bb2, threshold = 2) => {
          // Проверяем пересечение с небольшим порогом для "соприкосновения"
          return !(bb1.maxX + threshold < bb2.minX || 
                   bb2.maxX + threshold < bb1.minX || 
                   bb1.maxY + threshold < bb2.minY || 
                   bb2.maxY + threshold < bb1.minY);
        };
        
        // === Обработка ПРОФИЛЬ + ЛИСТ ===
        if (isProfileSheetPair) {
          // Проверяем соприкосновение
          const bb1 = getWorldBoundingBox(obj1.obj);
          const bb2 = getWorldBoundingBox(obj2.obj);
          
          if (boundingBoxesTouch(bb1, bb2)) {
            // Определяем профиль и лист
            const profileObj = isProfile1 ? obj1 : obj2;
            const sheetObj = isProfile1 ? obj2 : obj1;
            
            // Получаем центр профиля
            const profData = profileObj.obj;
            const profOffsetX = profData.offsetX || 0;
            const profOffsetY = profData.offsetY || 0;
            const profGroupX = profData.groupX || 0;
            const profGroupY = profData.groupY || 0;
            const profRotation = profData.rotation || 0;
            const profRad = (profRotation * Math.PI) / 180;
            const profCos = Math.cos(profRad);
            const profSin = Math.sin(profRad);
            
            const profCenterLocalX = profData.x + profData.width / 2;
            const profCenterLocalY = profData.y + profData.height / 2;
            const profDx = profCenterLocalX - profOffsetX;
            const profDy = profCenterLocalY - profOffsetY;
            const profCenterX = profGroupX + profDx * profCos - profDy * profSin;
            const profCenterY = profGroupY + profDx * profSin + profDy * profCos;
            
            // Добавляем сварку типа "ПрофильЛист" с фиксированной ценой
            detectedWelds.push({
              id: `weld_profileSheet_${profileObj.id}_${sheetObj.id}`,
              obj1: { objectId: profileObj.id, objectType: profileObj.type },
              obj2: { objectId: sheetObj.id, objectType: sheetObj.type },
              x: profCenterX,
              y: profCenterY,
              type: 'ПрофильЛист',
              length: 0, // Не используется для этого типа
              diameter: 0, // Не используется
              source: 'profileSheet',
              fixedPrice: 100, // Фиксированная цена 100₽
            });
          }
          
          continue; // Профиль+лист обрабатывается только этим условием
        }
        
        // Если есть объект с правилом "2 прямые сварки" (приварной фланец)
        if (rule1 === 'twoStraight' || rule2 === 'twoStraight') {
          // Определяем фланец и другой объект
          const flangeObj = rule1 === 'twoStraight' ? obj1 : obj2;
          const otherObj = rule1 === 'twoStraight' ? obj2 : obj1;
          
          // Проверяем соприкосновение bounding box'ов
          const flangeBB = getWorldBoundingBox(flangeObj.obj);
          const otherBB = getWorldBoundingBox(otherObj.obj);
          
          if (!boundingBoxesTouch(flangeBB, otherBB)) {
            // Не соприкасаются - пропускаем эту пару
            continue;
          }
          
          // Получаем центр фланца
          const flangeData = flangeObj.obj;
          const flangeOffsetX = flangeData.offsetX || 0;
          const flangeOffsetY = flangeData.offsetY || 0;
          const flangeGroupX = flangeData.groupX || 0;
          const flangeGroupY = flangeData.groupY || 0;
          const flangeRotation = flangeData.rotation || 0;
          const flangeRad = (flangeRotation * Math.PI) / 180;
          const flangeCos = Math.cos(flangeRad);
          const flangeSin = Math.sin(flangeRad);
          
          const flangeCenterLocalX = flangeData.x + flangeData.width / 2;
          const flangeCenterLocalY = flangeData.y + flangeData.height / 2;
          const flangeDx = flangeCenterLocalX - flangeOffsetX;
          const flangeDy = flangeCenterLocalY - flangeOffsetY;
          const flangeCenterX = flangeGroupX + flangeDx * flangeCos - flangeDy * flangeSin;
          const flangeCenterY = flangeGroupY + flangeDx * flangeSin + flangeDy * flangeCos;
          
          // Длина шва = диаметр приваренного объекта (меньший из двух)
          const weldLength = weldDiameter;
          
          // Добавляем 2 прямые сварки (с небольшим смещением для визуализации)
          detectedWelds.push({
            id: `weld_welded_${flangeObj.id}_${otherObj.id}_1`,
            obj1: { objectId: flangeObj.id, objectType: flangeObj.type },
            obj2: { objectId: otherObj.id, objectType: otherObj.type },
            x: flangeCenterX - 5,
            y: flangeCenterY,
            type: 'Прямая',
            length: weldLength,
            diameter: weldDiameter,
            displayDiameter: weldDisplayDiameter,
            source: 'weldedFlange',
          });
          
          detectedWelds.push({
            id: `weld_welded_${flangeObj.id}_${otherObj.id}_2`,
            obj1: { objectId: flangeObj.id, objectType: flangeObj.type },
            obj2: { objectId: otherObj.id, objectType: otherObj.type },
            x: flangeCenterX + 5,
            y: flangeCenterY,
            type: 'Прямая',
            length: weldLength,
            diameter: weldDiameter,
            displayDiameter: weldDisplayDiameter,
            source: 'weldedFlange',
          });
          
          continue;
        }
        
        // Если есть объект с правилом "1 прямая сварка" (шайба с наружной резьбой)
        if (rule1 === 'oneStraight' || rule2 === 'oneStraight') {
          // Определяем шайбу и другой объект
          const washerObj = rule1 === 'oneStraight' ? obj1 : obj2;
          const otherObj = rule1 === 'oneStraight' ? obj2 : obj1;
          
          // Проверяем соприкосновение bounding box'ов
          const washerBB = getWorldBoundingBox(washerObj.obj);
          const otherBB = getWorldBoundingBox(otherObj.obj);
          
          if (!boundingBoxesTouch(washerBB, otherBB)) {
            // Не соприкасаются - пропускаем эту пару
            continue;
          }
          
          // Получаем центр шайбы
          const washerData = washerObj.obj;
          const washerOffsetX = washerData.offsetX || 0;
          const washerOffsetY = washerData.offsetY || 0;
          const washerGroupX = washerData.groupX || 0;
          const washerGroupY = washerData.groupY || 0;
          const washerRotation = washerData.rotation || 0;
          const washerRad = (washerRotation * Math.PI) / 180;
          const washerCos = Math.cos(washerRad);
          const washerSin = Math.sin(washerRad);
          
          const washerCenterLocalX = washerData.x + washerData.width / 2;
          const washerCenterLocalY = washerData.y + washerData.height / 2;
          const washerDx = washerCenterLocalX - washerOffsetX;
          const washerDy = washerCenterLocalY - washerOffsetY;
          const washerCenterX = washerGroupX + washerDx * washerCos - washerDy * washerSin;
          const washerCenterY = washerGroupY + washerDx * washerSin + washerDy * washerCos;
          
          // Длина шва = диаметр приваренного объекта (меньший из двух)
          const weldLength = weldDiameter;
          
          // Добавляем 1 прямую сварку
          detectedWelds.push({
            id: `weld_washer_${washerObj.id}_${otherObj.id}`,
            obj1: { objectId: washerObj.id, objectType: washerObj.type },
            obj2: { objectId: otherObj.id, objectType: otherObj.type },
            x: washerCenterX,
            y: washerCenterY,
            type: 'Прямая',
            length: weldLength,
            diameter: weldDiameter,
            displayDiameter: weldDisplayDiameter,
            source: 'washerWeld',
          });
          
          continue;
        }
        
        // === ПРОВЕРКА ДОПОЛНИТЕЛЬНЫХ ВЫХОДОВ ГИДРОРАЗДЕЛИТЕЛЯ ===
        // Для горизонтальных выходов гидроразделителя используем явную связь через igesParentCylinderId
        const isHydraulicOutlet1 = obj1.type === 'fitting' && obj1.obj.isHydraulicSeparatorOutlet === true;
        const isHydraulicOutlet2 = obj2.type === 'fitting' && obj2.obj.isHydraulicSeparatorOutlet === true;
        
        if (isHydraulicOutlet1 || isHydraulicOutlet2) {
          // Определяем фитинг-выход и потенциальный родительский цилиндр
          const outletObj = isHydraulicOutlet1 ? obj1 : obj2;
          const otherObj = isHydraulicOutlet1 ? obj2 : obj1;
          
          // Проверяем, что другой объект - это цилиндр с нужным ID
          const parentCylinderId = outletObj.obj.igesParentCylinderId;
          
          if (otherObj.type === 'cylinder' && otherObj.id === parentCylinderId) {
            // Получаем центр/позицию фитинга для сварки
            const outletX = outletObj.obj.groupX || 0;
            const outletY = outletObj.obj.groupY || 0;
            
            // Определяем тип сварки
            const isSquarePipe = otherObj.obj.pipeSpec?.shape === 'square';
            const weldType = isSquarePipe ? 'Прямая' : 'Сферическая';
            
            detectedWelds.push({
              id: `weld_hydraulicOutlet_${outletObj.id}_${otherObj.id}`,
              obj1: { objectId: outletObj.id, objectType: outletObj.type },
              obj2: { objectId: otherObj.id, objectType: otherObj.type },
              x: outletX,
              y: outletY,
              type: weldType,
              length: weldDiameter,
              diameter: weldDiameter,
              displayDiameter: weldDisplayDiameter,
              source: 'hydraulicSeparatorOutlet',
            });
          }
          
          // Пропускаем дальнейшие проверки для этой пары
          continue;
        }
        
        // === ПРОВЕРКА ФИТИНГОВ "НА СЕБЯ" (isSelfFacing) ===
        // Для таких фитингов работает ТОЛЬКО логика "центр внутри контейнера"
        // Никаких точек привязки и пересечений стенок!
        const isSelfFacing1 = obj1.type === 'fitting' && obj1.obj.isSelfFacing === true;
        const isSelfFacing2 = obj2.type === 'fitting' && obj2.obj.isSelfFacing === true;
        
        if (isSelfFacing1 || isSelfFacing2) {
          // === ИСКЛЮЧЕНИЕ: Не создаём сварку между двумя центральными фитингами ===
          // Когда выбрана опция "с 2-х сторон", два фитинга находятся друг на друге,
          // но физически не касаются друг друга (смотрят в разные стороны)
          const isCenterFitting1 = obj1.type === 'fitting' && obj1.obj.isCenterFitting === true;
          const isCenterFitting2 = obj2.type === 'fitting' && obj2.obj.isCenterFitting === true;
          
          if (isCenterFitting1 && isCenterFitting2) {
            // Оба объекта - центральные фитинги, пропускаем создание сварки между ними
            continue;
          }
          
          // Определяем какой объект - selfFacing, а какой - "контейнер"
          const selfFacingObj = isSelfFacing1 ? obj1 : obj2;
          const containerObj = isSelfFacing1 ? obj2 : obj1;
          
          // Получаем центр selfFacing фитинга в мировых координатах
          const sfObj = selfFacingObj.obj;
          const sfOffsetX = sfObj.offsetX || 0;
          const sfOffsetY = sfObj.offsetY || 0;
          const sfGroupX = sfObj.groupX || 0;
          const sfGroupY = sfObj.groupY || 0;
          const sfRotation = sfObj.rotation || 0;
          const sfRad = (sfRotation * Math.PI) / 180;
          const sfCos = Math.cos(sfRad);
          const sfSin = Math.sin(sfRad);
          
          // Центр фитинга в локальных координатах
          const sfLocalCenterX = sfObj.x + sfObj.width / 2;
          const sfLocalCenterY = sfObj.y + sfObj.height / 2;
          
          // Преобразуем в мировые координаты
          const sfDx = sfLocalCenterX - sfOffsetX;
          const sfDy = sfLocalCenterY - sfOffsetY;
          const sfWorldCenterX = sfGroupX + sfDx * sfCos - sfDy * sfSin;
          const sfWorldCenterY = sfGroupY + sfDx * sfSin + sfDy * sfCos;
          
          // Получаем bounding box контейнера в мировых координатах
          const contObj = containerObj.obj;
          const contOffsetX = contObj.offsetX || 0;
          const contOffsetY = contObj.offsetY || 0;
          const contGroupX = contObj.groupX || 0;
          const contGroupY = contObj.groupY || 0;
          const contRotation = contObj.rotation || 0;
          const contRad = (contRotation * Math.PI) / 180;
          const contCos = Math.cos(contRad);
          const contSin = Math.sin(contRad);
          
          // Углы bounding box контейнера
          const contCorners = [
            { x: contObj.x, y: contObj.y },
            { x: contObj.x + contObj.width, y: contObj.y },
            { x: contObj.x + contObj.width, y: contObj.y + contObj.height },
            { x: contObj.x, y: contObj.y + contObj.height },
          ].map(corner => {
            const dx = corner.x - contOffsetX;
            const dy = corner.y - contOffsetY;
            return {
              x: contGroupX + dx * contCos - dy * contSin,
              y: contGroupY + dx * contSin + dy * contCos,
            };
          });
          
          // Находим bounding box контейнера в мировых координатах
          const contMinX = Math.min(...contCorners.map(c => c.x));
          const contMaxX = Math.max(...contCorners.map(c => c.x));
          const contMinY = Math.min(...contCorners.map(c => c.y));
          const contMaxY = Math.max(...contCorners.map(c => c.y));
          
          // Проверяем, находится ли центр selfFacing фитинга внутри bounding box контейнера
          const isInsideContainer = 
            sfWorldCenterX >= contMinX && sfWorldCenterX <= contMaxX &&
            sfWorldCenterY >= contMinY && sfWorldCenterY <= contMaxY;
          
          if (isInsideContainer) {
            const weldId = `weld_selfFacing_${selfFacingObj.id}_${containerObj.id}`;
            // Длина шва = диаметр приваренного объекта (меньший из двух)
            const weldLength = weldDiameter;
            
            // Если контейнер - квадратная труба, то сварка прямая
            const isContainerSquarePipe = containerObj.type === 'cylinder' && 
              containerObj.obj.pipeSpec?.shape === 'square';
            const weldType = isContainerSquarePipe ? 'Прямая' : 'Сферическая';
            
            detectedWelds.push({
              id: weldId,
              obj1: { objectId: selfFacingObj.id, objectType: selfFacingObj.type },
              obj2: { objectId: containerObj.id, objectType: containerObj.type },
              x: sfWorldCenterX,
              y: sfWorldCenterY,
              type: weldType,
              length: weldLength,
              diameter: weldDiameter,
              displayDiameter: weldDisplayDiameter,
              source: 'selfFacing',
            });
          }
          
          // Для selfFacing фитингов пропускаем ВСЕ остальные проверки
          continue;
        }
        
        // === ПРОВЕРКА ТОЧЕК ПРИВЯЗКИ ===
        // Если несколько точек привязки одного объекта совпадают с точками другого,
        // это одна сварка (например, ниппель одинакового диаметра сверху трубы)
        const snapPoints1 = getObjectSnapPoints(obj1.id, obj1.type);
        const snapPoints2 = getObjectSnapPoints(obj2.id, obj2.type);
        
        const SNAP_THRESHOLD = 5; // Порог близости точек
        const matchingPoints = [];
        
        snapPoints1.forEach((p1, idx1) => {
          snapPoints2.forEach((p2, idx2) => {
            const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
            if (dist < SNAP_THRESHOLD) {
              matchingPoints.push({
                p1, p2, idx1, idx2,
                center: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }
              });
            }
          });
        });
        
        // Если есть совпадающие точки, группируем их по линии (по Y или X)
        if (matchingPoints.length >= 2) {
          // Группируем точки по Y (горизонтальная линия) или X (вертикальная линия)
          const LINE_THRESHOLD = 10; // Порог для определения "одной линии"
          const lineGroups = [];
          
          matchingPoints.forEach(mp => {
            let foundGroup = null;
            
            for (const group of lineGroups) {
              // Проверяем, находится ли точка на той же линии (по Y или X)
              const avgY = group.reduce((sum, p) => sum + p.center.y, 0) / group.length;
              const avgX = group.reduce((sum, p) => sum + p.center.x, 0) / group.length;
              
              const isOnHorizontalLine = Math.abs(mp.center.y - avgY) < LINE_THRESHOLD;
              const isOnVerticalLine = Math.abs(mp.center.x - avgX) < LINE_THRESHOLD;
              
              if (isOnHorizontalLine || isOnVerticalLine) {
                foundGroup = group;
                break;
              }
            }
            
            if (foundGroup) {
              foundGroup.push(mp);
            } else {
              lineGroups.push([mp]);
            }
          });
          
          // Каждая группа из 2+ точек на одной линии = одна сварка
          lineGroups.forEach((group, groupIdx) => {
            if (group.length >= 2) {
              const centerX = group.reduce((sum, p) => sum + p.center.x, 0) / group.length;
              const centerY = group.reduce((sum, p) => sum + p.center.y, 0) / group.length;
              
              const weldId = `weld_snap_${obj1.id}_${obj2.id}_${groupIdx}`;
              // Длина шва = диаметр приваренного объекта (меньший из двух)
              const weldLength = weldDiameter;
              
              detectedWelds.push({
                id: weldId,
                obj1: { objectId: obj1.id, objectType: obj1.type },
                obj2: { objectId: obj2.id, objectType: obj2.type },
                x: centerX,
                y: centerY,
                type: 'Прямая',
                length: weldLength,
                diameter: weldDiameter,
                displayDiameter: weldDisplayDiameter,
                source: 'snapPoints', // Помечаем что это сварка от точек привязки
              });
            }
          });
          
          // Если нашли сварки по точкам привязки, пропускаем проверку пересечений для этой пары
          if (lineGroups.some(g => g.length >= 2)) {
            continue;
          }
        }
        
        // === ПРОВЕРКА ПЕРЕСЕЧЕНИЙ СТЕНОК ===
        // Получаем все стороны (отрезки) обоих объектов
        const segments1 = getObjectSegments(obj1.id, obj1.type);
        const segments2 = getObjectSegments(obj2.id, obj2.type);
        
        if (segments1.length === 0 || segments2.length === 0) {
          continue;
        }
        
        // Ищем все пересечения между сторонами двух объектов
        const intersections = [];
        
        let minDistFound = Infinity;
        
        segments1.forEach(seg1 => {
          segments2.forEach(seg2 => {
            
            // Для сварки учитываем ТОЛЬКО границы прямоугольников (rect_*)
            // Внутренние линии DXF (polyline_*, line_*) игнорируем
            const isRect1 = seg1.name.startsWith('rect_');
            const isRect2 = seg2.name.startsWith('rect_');
            
            // Пропускаем если хотя бы один сегмент - не rect_*
            if (!isRect1 || !isRect2) {
              return; // skip this pair
            }
            
            let threshold = 0;
            if (isRect1 && isRect2) {
              // Проверяем, что сегменты параллельны (не перпендикулярны)
              const dx1 = seg1.end.x - seg1.start.x;
              const dy1 = seg1.end.y - seg1.start.y;
              const dx2 = seg2.end.x - seg2.start.x;
              const dy2 = seg2.end.y - seg2.start.y;
              
              const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
              const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
              
              if (len1 > 0 && len2 > 0) {
                // Нормализованные направления
                const dir1 = { x: dx1 / len1, y: dy1 / len1 };
                const dir2 = { x: dx2 / len2, y: dy2 / len2 };
                
                // Скалярное произведение
                const dotProduct = dir1.x * dir2.x + dir1.y * dir2.y;
                
                // Параллельны, если |dotProduct| близко к 1
                const isParallel = Math.abs(Math.abs(dotProduct) - 1) < 0.1;
                
                if (isParallel) {
                  threshold = 1;
                }
              }
            }
            
            const intersection = getSegmentIntersection(
              seg1.start, seg1.end,
              seg2.start, seg2.end,
              threshold
            );
            
            if (intersection) {
              intersections.push({
                point: { x: intersection.x, y: intersection.y },
                seg1Name: seg1.name,
                seg2Name: seg2.name,
                distance: intersection.distance,
              });
            } else if (threshold === 0) {
              // Вычисляем минимальное расстояние между отрезками для отладки
              const dist = segmentToSegmentDistance(seg1.start, seg1.end, seg2.start, seg2.end);
              if (dist < minDistFound) {
                minDistFound = dist;
              }
            }
          });
        });
        
        
        // Группируем пересечения по стенке объекта (не по расстоянию!)
        // Логика: если несколько отрезков одного объекта пересекают одну и ту же стенку 
        // другого объекта - это одна сварка.
        // Например: ниппель входит в трубу сбоку - левый и правый края ниппеля 
        // пересекают ОДНУ стенку трубы = 1 сварка.
        // Но если ниппель проходит СКВОЗЬ трубу (top и bottom) = 2 сварки.
        
        // Собираем все уникальные стенки rect_*, которые участвуют в пересечениях
        const wallIntersections = new Map(); // key = "obj1:rect_left" или "obj2:rect_top", value = array of intersections
        
        intersections.forEach(inter => {
          const isRect1 = inter.seg1Name.startsWith('rect_');
          const isRect2 = inter.seg2Name.startsWith('rect_');
          
          // Добавляем пересечение к стенке obj1 (если rect_*)
          if (isRect1) {
            const key1 = `1:${inter.seg1Name}`;
            if (!wallIntersections.has(key1)) {
              wallIntersections.set(key1, []);
            }
            wallIntersections.get(key1).push(inter);
          }
          
          // Добавляем пересечение к стенке obj2 (если rect_*)
          if (isRect2) {
            const key2 = `2:${inter.seg2Name}`;
            if (!wallIntersections.has(key2)) {
              wallIntersections.set(key2, []);
            }
            wallIntersections.get(key2).push(inter);
          }
          
        });
        
        // Теперь создаём кластеры: каждая стенка = одна сварка
        const processedIntersections = new Set();
        const clusters = [];
        
        // Вычисляем площади объектов для приоритезации
        // Группируем по стенкам БОЛЬШЕГО объекта (трубы), а не меньшего (ниппеля)
        const area1 = obj1.obj.width * obj1.obj.height;
        const area2 = obj2.obj.width * obj2.obj.height;
        
        // Сортируем ключи стенок:
        // 1. ПРИОРИТЕТ стенке с БОЛЬШИМ количеством пересечений (главный критерий!)
        // 2. При равном количестве - по площади объекта
        const sortedWallKeys = [...wallIntersections.keys()]
          .sort((a, b) => {
            const aCount = wallIntersections.get(a).length;
            const bCount = wallIntersections.get(b).length;
            
            // Сначала по количеству пересечений (больше = приоритетнее)
            if (aCount !== bCount) {
              return bCount - aCount;
            }
            
            // При равном количестве - по площади объекта
            const aPrefix = a.startsWith('1:') ? '1' : '2';
            const bPrefix = b.startsWith('1:') ? '1' : '2';
            const aArea = aPrefix === '1' ? area1 : area2;
            const bArea = bPrefix === '1' ? area1 : area2;
            
            return bArea - aArea;
          });
        
        sortedWallKeys.forEach(wallKey => {
          const wallPoints = wallIntersections.get(wallKey);
          
          // Фильтруем только ещё не обработанные пересечения
          const newPoints = wallPoints.filter(p => !processedIntersections.has(p));
          
          if (newPoints.length > 0) {
            // Создаём кластер из всех пересечений с этой стенкой
            const sumX = newPoints.reduce((sum, p) => sum + p.point.x, 0);
            const sumY = newPoints.reduce((sum, p) => sum + p.point.y, 0);
            
            clusters.push({
              key: wallKey,
              center: {
                x: sumX / newPoints.length,
                y: sumY / newPoints.length,
              },
              points: newPoints,
            });
            
            // Отмечаем все точки как обработанные
            newPoints.forEach(p => processedIntersections.add(p));
          }
        });
        
        // Создаем сварки для каждого кластера пересечений
        clusters.forEach((cluster, idx) => {
          const weldId = `weld_${obj1.id}_${obj2.id}_${idx}`;
          
          // Длина шва = диаметр приваренного объекта (меньший из двух)
          const weldLength = weldDiameter;
          
          // Определяем тип сварки:
          // - Если один из объектов - квадратная труба → прямая сварка
          // - Иначе → сферическая сварка
          const isSquarePipe1 = obj1.type === 'cylinder' && obj1.obj.pipeSpec?.shape === 'square';
          const isSquarePipe2 = obj2.type === 'cylinder' && obj2.obj.pipeSpec?.shape === 'square';
          const weldType = (isSquarePipe1 || isSquarePipe2) ? 'Прямая' : 'Сферическая';
          
          detectedWelds.push({
            id: weldId,
            obj1: { objectId: obj1.id, objectType: obj1.type },
            obj2: { objectId: obj2.id, objectType: obj2.type },
            x: cluster.center.x,
            y: cluster.center.y,
            type: weldType,
            length: weldLength,
            diameter: weldDiameter,
            displayDiameter: weldDisplayDiameter,
            source: 'intersection',
          });
        });
      }
    }
    
    
    return detectedWelds;
  }, [getObjectSegments, getObjectSnapPoints, getSegmentIntersection, segmentToSegmentDistance]);
  
  // Функция для удаления привязки точки по клику на индикатор
  const handleRemoveConstraint = useCallback((objectId, objectType, pointIndex) => {
    setObjectConstraints(prev => {
      const filtered = prev.filter(c => 
        !(c.type === 'point' &&
          ((c.obj1.objectId === objectId && c.obj1.objectType === objectType && c.obj1.pointIndex === pointIndex) ||
           (c.obj2.objectId === objectId && c.obj2.objectType === objectType && c.obj2.pointIndex === pointIndex)))
      );
      return filtered;
    });
  }, []);
  
  // Функция для удаления привязки стенки по клику на индикатор
  const handleRemoveEdgeConstraint = useCallback((objectId, objectType, edgeType) => {
    setObjectConstraints(prev => {
      const filtered = prev.filter(c => 
        !(c.type === 'edge' &&
          ((c.obj1.objectId === objectId && c.obj1.objectType === objectType && c.obj1.edgeType === edgeType) ||
           (c.obj2.objectId === objectId && c.obj2.objectType === objectType && c.obj2.edgeType === edgeType)))
      );
      return filtered;
    });
  }, []);
  
  // Функция для рекурсивной проверки, привязан ли объект к заблокированному объекту
  const isConstrainedToLockedObject = useCallback((objectId, objectType, visited = new Set()) => {
    // Защита от бесконечной рекурсии
    const key = `${objectType}:${objectId}`;
    if (visited.has(key)) return false;
    visited.add(key);
    
    const constraints = objectConstraintsRef.current;
    
    // Находим все объекты, с которыми связан текущий объект
    const relatedObjects = [];
    constraints.forEach(constraint => {
      if (constraint.obj1.objectId === objectId && constraint.obj1.objectType === objectType) {
        relatedObjects.push({ id: constraint.obj2.objectId, type: constraint.obj2.objectType });
      } else if (constraint.obj2.objectId === objectId && constraint.obj2.objectType === objectType) {
        relatedObjects.push({ id: constraint.obj1.objectId, type: constraint.obj1.objectType });
      }
    });
    
    // Проверяем каждый связанный объект
    for (const related of relatedObjects) {
      // Находим объект в массивах
      let relatedObj = null;
      if (related.type === 'cylinder') {
        relatedObj = cylindersRef.current.find(c => c.id === related.id);
      } else if (related.type === 'fitting') {
        relatedObj = fittingsRef.current.find(f => f.id === related.id);
      }
      
      // Если связанный объект заблокирован - возвращаем true
      if (relatedObj && relatedObj.locked) {
        return true;
      }
      
      // Рекурсивно проверяем цепочку привязок
      if (isConstrainedToLockedObject(related.id, related.type, visited)) {
        return true;
      }
    }
    
    return false;
  }, []);
  
  // Функция для обновления связанных объектов (с поддержкой каскадных обновлений)
  const updateConstrainedObjects = useCallback((movedObjectId, movedObjectType, processedObjects = new Set()) => {
    // Если в данный момент обрабатывается изменение размерной линии, не выполняем обновление
    // (оно уже обработано внутри handleDimensionChange с правильными смещениями)
    if (isProcessingDimensionChangeRef.current) {
      return;
    }
    
    // Защита от бесконечной рекурсии
    const objectKey = `${movedObjectType}:${movedObjectId}`;
    if (processedObjects.has(objectKey)) {
      return;
    }
    processedObjects.add(objectKey);
    
    const constraints = objectConstraintsRef.current;
    if (constraints.length === 0) return;
    
    
    // Сначала проверяем, есть ли хотя бы одна привязка к заблокированному объекту
    const hasLockedConstraint = constraints.some(constraint => {
      let targetObjectId = null;
      let targetObjectType = null;
      
      if (constraint.obj1.objectId === movedObjectId && constraint.obj1.objectType === movedObjectType) {
        targetObjectId = constraint.obj2.objectId;
        targetObjectType = constraint.obj2.objectType;
      } else if (constraint.obj2.objectId === movedObjectId && constraint.obj2.objectType === movedObjectType) {
        targetObjectId = constraint.obj1.objectId;
        targetObjectType = constraint.obj1.objectType;
      }
      
      if (!targetObjectId) return false;
      
      // Проверяем, заблокирован ли целевой объект
      let targetObject = null;
      if (targetObjectType === 'fitting') {
        targetObject = fittingsRef.current.find(f => f.id === targetObjectId);
      } else if (targetObjectType === 'cylinder') {
        targetObject = cylindersRef.current.find(c => c.id === targetObjectId);
      }
      
      return targetObject && targetObject.locked;
    });
    
    if (hasLockedConstraint) {
      return; // Не обрабатываем привязки, если хотя бы одна ведет к заблокированному объекту
    }
    
    // Находим все связи, где участвует перемещенный объект
    constraints.forEach(constraint => {
      let targetObjectId = null;
      let targetObjectType = null;
      let sourcePointIndex = null;
      let targetPointIndex = null;
      let sourceEdgeType = null;
      let targetEdgeType = null;
      const constraintType = constraint.type || 'point'; // 'point' или 'edge'
      
      // Определяем, какой объект нужно обновить
      if (constraint.obj1.objectId === movedObjectId && constraint.obj1.objectType === movedObjectType) {
        // Двигаем obj2 следом за obj1
        targetObjectId = constraint.obj2.objectId;
        targetObjectType = constraint.obj2.objectType;
        if (constraintType === 'edge') {
          sourceEdgeType = constraint.obj1.edgeType;
          targetEdgeType = constraint.obj2.edgeType;
        } else {
          sourcePointIndex = constraint.obj1.pointIndex;
          targetPointIndex = constraint.obj2.pointIndex;
        }
      } else if (constraint.obj2.objectId === movedObjectId && constraint.obj2.objectType === movedObjectType) {
        // Двигаем obj1 следом за obj2
        targetObjectId = constraint.obj1.objectId;
        targetObjectType = constraint.obj1.objectType;
        if (constraintType === 'edge') {
          sourceEdgeType = constraint.obj2.edgeType;
          targetEdgeType = constraint.obj1.edgeType;
        } else {
          sourcePointIndex = constraint.obj2.pointIndex;
          targetPointIndex = constraint.obj1.pointIndex;
        }
      }
      
      if (!targetObjectId) return;
      
      // Для привязок стенок определяем, какую ось синхронизировать
      let axisLocked = null; // 'x' | 'y' | null
      if (constraintType === 'edge' && sourceEdgeType) {
        // sourceEdgeType теперь локальный, нужно преобразовать в глобальный
        const movedObj = movedObjectType === 'fitting' 
          ? fittingsRef.current.find(f => f.id === movedObjectId)
          : cylindersRef.current.find(c => c.id === movedObjectId);
        const movedRotation = movedObj?.rotation || 0;
        const movedNormalizedRotation = ((movedRotation % 360) + 360) % 360;
        const movedRotationSteps = Math.round(movedNormalizedRotation / 90) % 4;
        
        const edgeRotationMap = {
          'top': ['top', 'right', 'bottom', 'left'],
          'right': ['right', 'bottom', 'left', 'top'],
          'bottom': ['bottom', 'left', 'top', 'right'],
          'left': ['left', 'top', 'right', 'bottom']
        };
        
        const globalSourceEdgeType = edgeRotationMap[sourceEdgeType]?.[movedRotationSteps] || sourceEdgeType;
        
        // Если привязаны горизонтальные стенки (top/bottom), блокируется ось Y
        // Если привязаны вертикальные стенки (left/right), блокируется ось X
        axisLocked = (globalSourceEdgeType === 'top' || globalSourceEdgeType === 'bottom') ? 'y' : 'x';
      }
      
      // Получаем текущие абсолютные позиции
      let sourcePos, targetPos;
      
      if (constraintType === 'edge') {
        // Для стенок используем центр стенки
        sourcePos = getObjectEdgeCenter(movedObjectId, movedObjectType, sourceEdgeType);
        targetPos = getObjectEdgeCenter(targetObjectId, targetObjectType, targetEdgeType);
      } else {
        // Для точек используем позицию точки
        sourcePos = getObjectPointAbsolutePosition(movedObjectId, movedObjectType, sourcePointIndex);
        targetPos = getObjectPointAbsolutePosition(targetObjectId, targetObjectType, targetPointIndex);
      }
      
      if (!sourcePos || !targetPos) return;
      
      // Вычисляем смещение в абсолютных координатах Stage
      let dxStage = sourcePos.x - targetPos.x;
      let dyStage = sourcePos.y - targetPos.y;
      
      // Для привязок стенок применяем смещение только по нужной оси
      if (constraintType === 'edge' && axisLocked) {
        if (axisLocked === 'x') {
          dyStage = 0; // Блокируем движение по Y, синхронизируем только X
        } else if (axisLocked === 'y') {
          dxStage = 0; // Блокируем движение по X, синхронизируем только Y
        }
      }
      
      // ВАЖНО: Делим на effectiveZoom, чтобы получить смещение в координатах объектов!
      const dx = dxStage / (effectiveZoom || 1);
      const dy = dyStage / (effectiveZoom || 1);
      
      // Игнорируем микросмещения (меньше 0.1 пикселя в координатах объектов)
      if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) return;
      
      
      // Проверяем, есть ли РЕАЛЬНАЯ привязка через P между перемещаемым объектом и целевым
      // Это важно, чтобы не изменять размер трубы, если она не привязана, а просто имеет размерную линию
      const hasDirectConstraint = objectConstraintsRef.current.some(c => 
        (c.obj1.objectId === movedObjectId && c.obj2.objectId === targetObjectId) ||
        (c.obj2.objectId === movedObjectId && c.obj1.objectId === targetObjectId)
      );
      
      
      // Применяем смещение к целевому объекту
      if (targetObjectType === 'fitting') {
        setFittings(prev => {
          const updated = prev.map(f => {
            if (f.id === targetObjectId) {
              if (f.groupX !== undefined || f.groupY !== undefined) {
                return { ...f, groupX: (f.groupX || 0) + dx, groupY: (f.groupY || 0) + dy };
              } else {
                return { ...f, x: f.x + dx, y: f.y + dy };
              }
            }
            return f;
          });
          // Обновляем ref сразу
          fittingsRef.current = updated;
          
          // РЕКУРСИВНО обновляем привязки для целевого объекта (каскадное обновление)
          setTimeout(() => {
            updateConstrainedObjects(targetObjectId, targetObjectType, processedObjects);
          }, 0);
          
          return updated;
        });
      } else if (targetObjectType === 'cylinder') {
        setCylinders(prev => {
          const updated = prev.map(c => {
            if (c.id === targetObjectId) {
              // ТОЛЬКО если есть прямая привязка через P - изменяем размер трубы
              if (hasDirectConstraint) {
                // Для цилиндров (труб) проверяем, привязана ли точка на конце
                // Точки: 4-top, 5-bottom, 6-left, 7-right
                const isEndPoint = [4, 5, 6, 7].includes(targetPointIndex);
                
                if (isEndPoint) {
                  // Если привязана точка на конце трубы, изменяем размер трубы
                  const isVertical = c.height > c.width;
                  const isHorizontal = c.width > c.height;
                  
                  if (isVertical && (targetPointIndex === 5)) {
                    // Bottom point - увеличиваем height
                    return { ...c, height: c.height + dy };
                  } else if (isVertical && (targetPointIndex === 4)) {
                    // Top point - увеличиваем height в другую сторону
                    return { ...c, y: c.y + dy, height: c.height - dy };
                  } else if (isHorizontal && (targetPointIndex === 7)) {
                    // Right point - увеличиваем width
                    return { ...c, width: c.width + dx };
                  } else if (isHorizontal && (targetPointIndex === 6)) {
                    // Left point - увеличиваем width в другую сторону
                    return { ...c, x: c.x + dx, width: c.width - dx };
                  }
                }
              } else {
              }
              
              // Для других точек или если нет привязки - перемещаем как обычно
              if (c.groupX !== undefined || c.groupY !== undefined) {
                return { ...c, groupX: (c.groupX || 0) + dx, groupY: (c.groupY || 0) + dy };
              } else {
                return { ...c, x: c.x + dx, y: c.y + dy };
              }
            }
            return c;
          });
          // Обновляем ref сразу
          cylindersRef.current = updated;
          
          // РЕКУРСИВНО обновляем привязки для целевого объекта (каскадное обновление)
          setTimeout(() => {
            updateConstrainedObjects(targetObjectId, targetObjectType, processedObjects);
          }, 0);
          
          return updated;
        });
      }
    });
  }, [getObjectPointAbsolutePosition, getObjectEdgeCenter, effectiveZoom]);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [currentLine, setCurrentLine] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [, setCanUndoLocal] = useState(false);
  const [, setCanRedoLocal] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [fittings, setFittings] = useState([]);
  const [projectedViews, setProjectedViews] = useState([]);
  const projectedViewsRef = useRef([]);
  const [selectedProjectedViewId, setSelectedProjectedViewId] = useState(null);
  const fittingsRef = useRef([]);
  const stageRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [rotateTargetId, setRotateTargetId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedBalloonId, setSelectedBalloonId] = useState(null);
  const [selectedBalloonTarget, setSelectedBalloonTarget] = useState(null); // { balloonId, targetIndex }
  const [selectionRect, setSelectionRect] = useState(null);
  const [selectionStart, setSelectionStart] = useState(null);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [isMiddlePanning, setIsMiddlePanning] = useState(false);
  const middlePanLastRef = useRef(null);
  const updateAllDimensionLinesRef = useRef(null);
  const [selectedPatternObjects, setSelectedPatternObjects] = useState([]); // [{type:'cylinder'|'fitting', id}]
  const [patterns, setPatterns] = useState([]);
  const [selectedPatternId, setSelectedPatternId] = useState(null);
  const clipboardRef = useRef(null); // Буфер обмена для копирования объектов (используем ref чтобы избежать пересоздания useEffect)
  const [lastClickPosition, setLastClickPosition] = useState({ x: 100, y: 100 }); // Последняя позиция клика мыши
  
  // Обработчик клика по стенке в режиме P
  const handleEdgeClick = useCallback((objectId, objectType, edgeType, absX, absY) => {
    if (!isPKeyPressed) return;
    
    if (!pointAttachmentMode) {
      // Первая стенка
      setPointAttachmentMode({
        firstPoint: { objectId, objectType, edgeType, x: absX, y: absY, isEdge: true }
      });
    } else if (pointAttachmentMode.firstPoint) {
      const firstIsEdge = pointAttachmentMode.firstPoint.isEdge;
      const secondIsEdge = true;
      
      // Проверяем совместимость: можно привязывать только стенку к стенке или точку к точке
      if (firstIsEdge !== secondIsEdge) {
        console.warn('⚠️ Нельзя привязать стенку к точке или наоборот!');
        setPointAttachmentMode(null);
        return;
      }
      
      // Создаём привязку стенок
      const constraint = {
        id: `constraint_${Date.now()}`,
        type: 'edge',
        obj1: pointAttachmentMode.firstPoint,
        obj2: { objectId, objectType, edgeType, x: absX, y: absY, isEdge: true }
      };
      
      setObjectConstraints(prev => [...prev, constraint]);
      
      // Преобразуем локальный тип первой стенки в глобальный для определения оси
      const firstObject = pointAttachmentMode.firstPoint.objectType === 'fitting' 
        ? fittingsRef.current.find(f => f.id === pointAttachmentMode.firstPoint.objectId)
        : cylindersRef.current.find(c => c.id === pointAttachmentMode.firstPoint.objectId);
      const firstRotation = firstObject?.rotation || 0;
      const firstNormalizedRotation = ((firstRotation % 360) + 360) % 360;
      const firstRotationSteps = Math.round(firstNormalizedRotation / 90) % 4;
      
      const edgeRotationMap = {
        'top': ['top', 'right', 'bottom', 'left'],
        'right': ['right', 'bottom', 'left', 'top'],
        'bottom': ['bottom', 'left', 'top', 'right'],
        'left': ['left', 'top', 'right', 'bottom']
      };
      
      const firstLocalEdge = pointAttachmentMode.firstPoint.edgeType;
      const firstGlobalEdge = edgeRotationMap[firstLocalEdge]?.[firstRotationSteps] || firstLocalEdge;
      
      // Определяем ось синхронизации на основе глобального типа
      const axisLocked = (firstGlobalEdge === 'top' || firstGlobalEdge === 'bottom') ? 'y' : 'x';
      
      // Определяем какой объект залочен
      const firstObjLocked = firstObject?.locked === true;
      const secondObj = objectType === 'fitting' 
        ? fittingsRef.current.find(f => f.id === objectId)
        : cylindersRef.current.find(c => c.id === objectId);
      const secondObjLocked = secondObj?.locked === true;
      
      // Если оба залочены - не перемещаем
      if (firstObjLocked && secondObjLocked) {
        console.warn('⚠️ Оба объекта залочены! Перемещение невозможно.');
        setPointAttachmentMode(null);
        return;
      }
      
      // Определяем какой объект перемещать:
      // - Если первый залочен → перемещаем второй
      // - Если второй залочен → перемещаем первый
      // - Если оба не залочены → перемещаем второй (стандартное поведение)
      const moveSecondObject = !secondObjLocked || firstObjLocked;
      
      // Вычисляем смещение
      let dx, dy;
      if (moveSecondObject) {
        // Перемещаем второй объект к первому
        dx = axisLocked === 'x' ? (pointAttachmentMode.firstPoint.x - absX) / (effectiveZoom || 1) : 0;
        dy = axisLocked === 'y' ? (pointAttachmentMode.firstPoint.y - absY) / (effectiveZoom || 1) : 0;
      } else {
        // Перемещаем первый объект ко второму (инвертируем смещение)
        dx = axisLocked === 'x' ? (absX - pointAttachmentMode.firstPoint.x) / (effectiveZoom || 1) : 0;
        dy = axisLocked === 'y' ? (absY - pointAttachmentMode.firstPoint.y) / (effectiveZoom || 1) : 0;
      }
      
      const targetObjectId = moveSecondObject ? objectId : pointAttachmentMode.firstPoint.objectId;
      const targetObjectType = moveSecondObject ? objectType : pointAttachmentMode.firstPoint.objectType;
      
      
      if (targetObjectType === 'fitting') {
        setFittings(prev => prev.map(f => {
          if (f.id === targetObjectId) {
            if (f.groupX !== undefined || f.groupY !== undefined) {
              return {
                ...f,
                groupX: (f.groupX || 0) + dx,
                groupY: (f.groupY || 0) + dy,
              };
            } else {
              return {
                ...f,
                x: f.x + dx,
                y: f.y + dy,
              };
            }
          }
          return f;
        }));
      } else if (targetObjectType === 'cylinder') {
        setCylinders(prev => prev.map(c => {
          if (c.id === targetObjectId) {
            if (c.groupX !== undefined || c.groupY !== undefined) {
              return {
                ...c,
                groupX: (c.groupX || 0) + dx,
                groupY: (c.groupY || 0) + dy,
              };
            } else {
              return {
                ...c,
                x: c.x + dx,
                y: c.y + dy,
              };
            }
          }
          return c;
        }));
      }
      
      setPointAttachmentMode(null);
    }
  }, [isPKeyPressed, pointAttachmentMode, effectiveZoom]);

  // Обработчик клика по точке привязки в режиме P
  const handleSnapPointClick = useCallback((objectId, objectType, pointIndex, absX, absY) => {
    if (!isPKeyPressed) return;
    
    
    if (!pointAttachmentMode) {
      // Первая точка
      setPointAttachmentMode({
        firstPoint: { objectId, objectType, pointIndex, x: absX, y: absY, isEdge: false }
      });
    } else if (pointAttachmentMode.firstPoint) {
      const firstIsEdge = pointAttachmentMode.firstPoint.isEdge;
      const secondIsEdge = false;
      
      // Проверяем совместимость
      if (firstIsEdge !== secondIsEdge) {
        console.warn('⚠️ Нельзя привязать стенку к точке или наоборот!');
        setPointAttachmentMode(null);
        return;
      }
      // Вторая точка
      const constraint = {
        id: `constraint_${Date.now()}`,
        type: 'point',
        obj1: pointAttachmentMode.firstPoint,
        obj2: { objectId, objectType, pointIndex, x: absX, y: absY, isEdge: false }
      };
      
      setObjectConstraints(prev => [...prev, constraint]);
      
      // Определяем какой объект залочен
      const firstObject = pointAttachmentMode.firstPoint.objectType === 'fitting' 
        ? fittingsRef.current.find(f => f.id === pointAttachmentMode.firstPoint.objectId)
        : cylindersRef.current.find(c => c.id === pointAttachmentMode.firstPoint.objectId);
      const secondObject = objectType === 'fitting'
        ? fittingsRef.current.find(f => f.id === objectId)
        : cylindersRef.current.find(c => c.id === objectId);
      
      const firstObjLocked = firstObject?.locked === true;
      const secondObjLocked = secondObject?.locked === true;
      
      // Если оба залочены - не перемещаем
      if (firstObjLocked && secondObjLocked) {
        console.warn('⚠️ Оба объекта залочены! Перемещение невозможно.');
        setPointAttachmentMode(null);
        return;
      }
      
      // Определяем какой объект перемещать
      const moveSecondObject = !secondObjLocked || firstObjLocked;
      
      // Вычисляем смещение в абсолютных координатах Stage
      let dxStage, dyStage;
      if (moveSecondObject) {
        dxStage = pointAttachmentMode.firstPoint.x - absX;
        dyStage = pointAttachmentMode.firstPoint.y - absY;
      } else {
        dxStage = absX - pointAttachmentMode.firstPoint.x;
        dyStage = absY - pointAttachmentMode.firstPoint.y;
      }
      
      // ВАЖНО: Делим на effectiveZoom, чтобы получить смещение в координатах объектов!
      const dx = dxStage / (effectiveZoom || 1);
      const dy = dyStage / (effectiveZoom || 1);
      
      const targetObjectId = moveSecondObject ? objectId : pointAttachmentMode.firstPoint.objectId;
      const targetObjectType = moveSecondObject ? objectType : pointAttachmentMode.firstPoint.objectType;
      
      
      // Применяем привязку
      if (targetObjectType === 'fitting') {
        setFittings(prev => prev.map(f => {
          if (f.id === targetObjectId) {
            if (f.groupX !== undefined || f.groupY !== undefined) {
              return {
                ...f,
                groupX: (f.groupX || 0) + dx,
                groupY: (f.groupY || 0) + dy,
              };
            } else {
              return {
                ...f,
                x: f.x + dx,
                y: f.y + dy,
              };
            }
          }
          return f;
        }));
      } else if (targetObjectType === 'cylinder') {
        setCylinders(prev => prev.map(c => {
          if (c.id === targetObjectId) {
            if (c.groupX !== undefined || c.groupY !== undefined) {
              return {
                ...c,
                groupX: (c.groupX || 0) + dx,
                groupY: (c.groupY || 0) + dy,
              };
            } else {
              return {
                ...c,
                x: c.x + dx,
                y: c.y + dy,
              };
            }
          }
          return c;
        }));
      }
      
      setPointAttachmentMode(null);
    }
  }, [isPKeyPressed, pointAttachmentMode, effectiveZoom]);
  const [patternEditCount, setPatternEditCount] = useState(1);
  const [patternEditDx, setPatternEditDx] = useState(0);
  const [patternEditDy, setPatternEditDy] = useState(0);
  const isObjectDraggingRef = useRef(false);
  const isProcessingDimensionChangeRef = useRef(false);
  const dimensionLineStateBeforePKeyRef = useRef(null); // Сохраняем состояние размерных линий до нажатия P
  const selectedElementsBeforePKeyRef = useRef(null); // Сохраняем выделенные объекты до нажатия P

  // Вспомогательные функции для доступа к группе и вычисления абсолютного угла поворота
  // Синхронизация refs с состояниями для избежания циклических зависимостей
  useEffect(() => {
    cylindersRef.current = cylinders;
  }, [cylinders]);

  useEffect(() => {
    fittingsRef.current = fittings;
  }, [fittings]);

  useEffect(() => {
    dimensionLinesRef.current = dimensionLines;
  }, [dimensionLines]);

  // Автоматическое обнаружение сварок при изменении объектов
  useEffect(() => {
    const newWelds = detectWelds();
    setWelds(newWelds);
  }, [cylinders, fittings, detectWelds]);

  // Объявляем здесь, чтобы использовать выше по коду
  const getGroupNodeById = useCallback((nodeId) => {
    const stage = stageRef.current;
    if (!stage) return null;
    let node = stage.findOne(`#${nodeId}`);
    if (!node) return null;
    return node.getClassName && node.getClassName() === 'Group' ? node : node.getParent();
  }, [stageRef]);

  const getObjectAbsRotationDegById = useCallback((nodeId) => {
    const group = getGroupNodeById(nodeId);
    if (!group) return 0;
    const t = group.getAbsoluteTransform();
    const m = t.getMatrix();
    const angleRad = Math.atan2(m[1], m[0]);
    let deg = (angleRad * 180) / Math.PI;
    deg = ((deg % 360) + 360) % 360;
    return deg;
  }, [getGroupNodeById]);

  // Вспомогательные функции для управления клон-объектами паттернов
  const rebuildClonesForPattern = useCallback((patternArg) => {
    if (!patternArg || !patternArg.id) return;
    const count = Math.max(1, Number(patternArg.count) || 1);
    const dx = Number(patternArg.dx) || 0;
    const dy = Number(patternArg.dy) || 0;


    // Удаляем старые клоны этого паттерна и создаём новые
    setCylinders((prev) => {
      const filtered = prev.filter((c) => !(c.isPatternClone && c.patternId === patternArg.id));
      const clones = [];
      patternArg.items.forEach((it) => {
        if (it.type !== 'cylinder') return;
        // ИСПРАВЛЕНИЕ: Используем cylindersRef.current вместо filtered, чтобы получить актуальные размеры
        const base = cylindersRef.current.find((c) => c.id === it.id);
        if (!base) return;
        // Получаем поворот базового объекта
        const baseRotation = getObjectAbsRotationDegById(base.id) || 0;
        const group = getGroupNodeById(base.id);
        const baseOffsetX = group && typeof group.offsetX === 'function' ? group.offsetX() : 0;
        const baseOffsetY = group && typeof group.offsetY === 'function' ? group.offsetY() : 0;
        const baseGroupX = group && typeof group.x === 'function' ? group.x() : 0;
        const baseGroupY = group && typeof group.y === 'function' ? group.y() : 0;
        
        // ИСПРАВЛЕНИЕ: Для повернутых объектов меняем оси в зависимости от направления поворота
        // чтобы dx всегда означал движение вправо, а dy - вниз
        const normalizedRotation = ((baseRotation % 360) + 360) % 360; // Нормализуем угол в диапазон 0-360
        
        
        const isRotated90 = Math.abs(normalizedRotation - 90) < 1; // Угол 90°
        const isRotated180 = Math.abs(normalizedRotation - 180) < 1; // Угол 180°
        const isRotated270 = Math.abs(normalizedRotation - 270) < 1; // Угол 270°
        
        let effectiveDx, effectiveDy;
        if (isRotated90) {
          // Угол 90°: меняем оси и инвертируем DY
          effectiveDx = dy;
          effectiveDy = -dx;
        } else if (isRotated180) {
          // Угол 180°: инвертируем обе оси
          effectiveDx = -dx;
          effectiveDy = -dy;
        } else if (isRotated270) {
          // Угол 270°: меняем оси и инвертируем DY
          effectiveDx = -dy;
          effectiveDy = dx;
        } else {
          // Не повернут (0°)
          effectiveDx = dx;
          effectiveDy = dy;
        }
        
        for (let k = 1; k < count; k++) {
          clones.push({
            id: `${base.id}__p__${patternArg.id}__k__${k}`,
            x: base.x + effectiveDx * k,
            y: base.y + effectiveDy * k,
            groupX: baseGroupX,
            groupY: baseGroupY,
            width: base.width,
            height: base.height,
            locked: true,
            isPatternClone: true,
            patternId: patternArg.id,
            baseId: base.id,
            instanceIndex: k,
            rotation: baseRotation,
            offsetX: baseOffsetX,
            offsetY: baseOffsetY,
            pipeSpec: base.pipeSpec, // Копируем спецификацию трубы для расчета стоимости
          });
        }
      });
      return [...filtered, ...clones];
    });

    setFittings((prev) => {
      const filtered = prev.filter((f) => !(f.isPatternClone && f.patternId === patternArg.id));
      const clones = [];
      patternArg.items.forEach((it) => {
        if (it.type !== 'fitting') return;
        // ИСПРАВЛЕНИЕ: Используем fittingsRef.current вместо filtered, чтобы получить актуальные размеры
        const base = fittingsRef.current.find((f) => f.id === it.id);
        if (!base) return;
        const baseRotation = getObjectAbsRotationDegById(base.id) || 0;
        const group = getGroupNodeById(base.id);
        const baseOffsetX = group && typeof group.offsetX === 'function' ? group.offsetX() : 0;
        const baseOffsetY = group && typeof group.offsetY === 'function' ? group.offsetY() : 0;
        const baseGroupX = group && typeof group.x === 'function' ? group.x() : 0;
        const baseGroupY = group && typeof group.y === 'function' ? group.y() : 0;
        
        // ИСПРАВЛЕНИЕ: Для повернутых объектов меняем оси в зависимости от направления поворота
        // чтобы dx всегда означал движение вправо, а dy - вниз
        const normalizedRotation = ((baseRotation % 360) + 360) % 360; // Нормализуем угол в диапазон 0-360
        
        
        const isRotated90 = Math.abs(normalizedRotation - 90) < 1; // Угол 90°
        const isRotated180 = Math.abs(normalizedRotation - 180) < 1; // Угол 180°
        const isRotated270 = Math.abs(normalizedRotation - 270) < 1; // Угол 270°
        
        let effectiveDx, effectiveDy;
        if (isRotated90) {
          // Угол 90°: меняем оси и инвертируем DY
          effectiveDx = dy;
          effectiveDy = -dx;
        } else if (isRotated180) {
          // Угол 180°: инвертируем обе оси
          effectiveDx = -dx;
          effectiveDy = -dy;
        } else if (isRotated270) {
          // Угол 270°: меняем оси и инвертируем DY
          effectiveDx = -dy;
          effectiveDy = dx;
        } else {
          // Не повернут (0°)
          effectiveDx = dx;
          effectiveDy = dy;
        }
        
        for (let k = 1; k < count; k++) {
          clones.push({
            id: `${base.id}__p__${patternArg.id}__k__${k}`,
            x: base.x + effectiveDx * k,
            y: base.y + effectiveDy * k,
            groupX: baseGroupX,
            groupY: baseGroupY,
            width: base.width,
            height: base.height,
            locked: true,
            isPatternClone: true,
            patternId: patternArg.id,
            baseId: base.id,
            instanceIndex: k,
            // Копируем DXF-поля, чтобы визуально совпадать
            type: base.type, // Копируем тип (например, 'fastener') для применения специальной логики
            shapes: base.shapes,
            originalWidth: base.originalWidth,
            originalHeight: base.originalHeight,
            isDiameterFixed: base.isDiameterFixed,
            isLengthAdjustable: base.isLengthAdjustable,
            rotation: baseRotation,
            offsetX: baseOffsetX,
            offsetY: baseOffsetY,
            itemKey: base.itemKey, // Копируем ключ фитинга для расчета стоимости
            snapPoints: base.snapPoints, // Копируем кастомные точки привязки
            // Копируем специфичные для крепежей поля
            ...(base.type === 'fastener' ? (
              base.isFloorFastener 
                ? {
                    // Напольные крепежи
                    fastenerHeight: base.fastenerHeight,
                    rangeOffset: base.rangeOffset,
                    isFloorFastener: true,
                    pipeDiameter: base.pipeDiameter,
                    includedInPrice: base.includedInPrice,
                  }
                : {
                    // Настенные крепежи
                    clampDiameter: base.clampDiameter,
                    axialDistance: base.axialDistance,
                    overhang: base.overhang,
                    isFloorFastener: false,
                    includedInPrice: base.includedInPrice,
                  }
            ) : {}),
          });
        }
      });
      return [...filtered, ...clones];
    });
  }, [getObjectAbsRotationDegById, getGroupNodeById]);

  const rebuildClonesForBase = useCallback((type, baseId, processedIds = new Set()) => {
    // ИСПРАВЛЕНИЕ: Рекурсивное обновление всей цепочки клонов (любой глубины вложенности)
    
    // Защита от бесконечной рекурсии (циклических зависимостей)
    const key = `${type}:${baseId}`;
    if (processedIds.has(key)) {
      return;
    }
    processedIds.add(key);
    
    
    // 1. Перестраиваем все паттерны, где базовая сущность участвует напрямую
    const related = patterns.filter((p) => p.items.some((it) => it.type === type && it.id === baseId));
    related.forEach((p) => rebuildClonesForPattern(p));
    
    // 2. Находим все клоны этого базового объекта
    const allClones = type === 'cylinder'
      ? cylindersRef.current.filter((c) => c.isPatternClone && c.baseId === baseId)
      : fittingsRef.current.filter((f) => f.isPatternClone && f.baseId === baseId);
    
    
    // 3. РЕКУРСИВНО обновляем паттерны для каждого клона (вся цепочка, любой глубины)
    if (allClones.length > 0) {
      const cascadeRecursive = () => {
        allClones.forEach((clone) => {
          rebuildClonesForBase(type, clone.id, processedIds);
        });
      };
      
      // Выполняем после следующего кадра, чтобы клоны успели перерисоваться
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        requestAnimationFrame(() => requestAnimationFrame(cascadeRecursive));
      } else {
        setTimeout(cascadeRecursive, 0);
      }
    }
  }, [patterns, rebuildClonesForPattern]);

  const handleFittingResize = (e, fitting) => {
    const target = e.target;
    // Переводим абсолютные координаты маркера в локальные координаты группы (устойчиво к zoom)
    const group = target.getParent();
    const t = group.getAbsoluteTransform();
    const inv = t.copy().invert();
    const abs = target.getAbsolutePosition();
    const pointerLocal = inv.point({ x: abs.x, y: abs.y });

    setFittings((prevFittings) => {
      const index = prevFittings.findIndex((f) => f.id === fitting.id);
      if (index === -1) return prevFittings;
      const updated = [...prevFittings];
      const fit = updated[index];

      const canAdjustLength = fit.isLengthAdjustable !== false;
      const canAdjustWidth = fit.isDiameterFixed === false;

      const next = { ...fit };
      if (canAdjustLength) {
        next.height = Math.max(20, pointerLocal.y - fit.y);
      }
      if (canAdjustWidth) {
        next.width = Math.max(20, pointerLocal.x - fit.x);
      }
      updated[index] = next;
      // Обновляем ref сразу
      fittingsRef.current = updated;
      return updated;
    });
    try { if (updateAllDimensionLinesRef.current) updateAllDimensionLinesRef.current(); } catch (_) {}
  };  

  const handleResize = useCallback((e, id) => {
    // Локальные координаты маркера относительно группы
    const target = e.target;
    const group = target.getParent();
    const t = group.getAbsoluteTransform();
    const inv = t.copy().invert();
    const abs = target.getAbsolutePosition();
    const local = inv.point({ x: abs.x, y: abs.y });

    setCylinders((prevCylinders) => {
      const index = prevCylinders.findIndex((c) => c.id === id);
      if (index === -1) return prevCylinders;
      const updated = [...prevCylinders];
      const cyl = updated[index];

      // Растягиваем только вниз и вправо от (x, y), мин. 20 по каждой оси
      const newWidth = Math.max(20, local.x - cyl.x);
      const newHeight = Math.max(20, local.y - cyl.y);
      updated[index] = { ...cyl, width: newWidth, height: newHeight };
      // Обновляем ref сразу
      cylindersRef.current = updated;
      
      // Обновляем линейный массив для этой трубы (если она базовый объект)
      if (!cyl.isPatternClone) {
        setTimeout(() => {
          rebuildClonesForBase('cylinder', id);
          // Обновляем привязанные объекты после изменения размера
          updateConstrainedObjects(id, 'cylinder');
        }, 0);
      }
      
      return updated;
    });
    try { if (updateAllDimensionLinesRef.current) updateAllDimensionLinesRef.current(); } catch (_) {}
  }, [rebuildClonesForBase, updateConstrainedObjects]);

  // Обработка перетаскивания фитинга/крепежа из панели на холст
  const handleDrop = (e) => {
    e.preventDefault();

    const plain = e.dataTransfer.getData('text/plain');
    const fittingKey = e.dataTransfer.getData('fittingKey') || (fittingsData[plain] ? plain : '');
    const fastenerKey = e.dataTransfer.getData('fastenerKey') || (fastenersData[plain] ? plain : '');

    const stage = stageRef.current;
    if (!stage) return;
    const containerRect = stage.container().getBoundingClientRect();
    const z = (effectiveZoom || 1);
    const sheetW = canvasWidth / z;
    const sheetH = canvasHeight / z;
    let pointerX = (e.clientX - containerRect.left) / z;
    let pointerY = (e.clientY - containerRect.top) / z;
    pointerX = Math.min(Math.max(pointerX, 30), Math.max(30, sheetW - 30));
    pointerY = Math.min(Math.max(pointerY, 30), Math.max(30, sheetH - 30));
  
    if (fittingKey) {
      loadDxf(fittingKey, pointerX, pointerY, 'fitting');
      // Сбросим мультивыбор паттерна при добавлении нового фитинга
      setSelectedPatternObjects([]);
      // Перестроим клоны для всех паттернов, в которых фигурирует этот фитинг как база (если такой есть)
      // Прямо сейчас мы не знаем id до загрузки DXF, поэтому перестроим все паттерны позже при завершении drag/resize/добавления
    } else if (fastenerKey) {
      // Для крепежа открываем диалог настройки
      setPendingFastener({
        fastenerKey,
        x: pointerX,
        y: pointerY,
      });
      setFastenerDialogOpen(true);
      // Сбросим мультивыбор паттерна при добавлении нового крепежа
      setSelectedPatternObjects([]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };

  const MAX_STACK_SIZE = 50; // Максимальный размер стека

  const saveState = useCallback(() => {
    const currentState = {
      lines,
      cylinders,
      annotations,
      balloons,
      dimensionLines,
      fittings,
      patterns,
    };

    // Проверяем, отличается ли текущее состояние от последнего сохраненного
    const lastState = undoStack[undoStack.length - 1];
    if (JSON.stringify(currentState) !== JSON.stringify(lastState)) {
      setUndoStack((prevStack) => {
        const newStack = [...prevStack, currentState];
        // Ограничиваем размер стека
        if (newStack.length > MAX_STACK_SIZE) {
          newStack.shift(); // Удаляем самый старый элемент
        }
        return newStack;
      });
      // Очищаем redoStack, так как началась новая ветка действий
      setRedoStack([]);
    }
  }, [undoStack, lines, cylinders, annotations, balloons, dimensionLines, fittings, patterns]);

  // На случай, если кнопку отпустят вне холста — завершаем панорамирование
  useEffect(() => {
    const handleWindowMouseUp = (ev) => {
      if (!isMiddlePanning) return;
      if (ev.button !== 1) return;
      setIsMiddlePanning(false);
      try {
        const stage = stageRef.current;
        if (stage) stage.container().style.cursor = 'default';
      } catch (_) {}
      saveState();
    };
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => window.removeEventListener('mouseup', handleWindowMouseUp);
  }, [isMiddlePanning, saveState]);

  // Ref для хранения начальной позиции при drag (для блокировки осей)
  const dragStartPositionRef = useRef({});

  const handleDragStart = useCallback(
    (e) => {
      // Блокируем перетаскивание объектов при использовании средней кнопки мыши (панорамирование)
      if (e.evt && e.evt.button === 1) {
        e.target.stopDrag();
        return;
      }
      
      // Получаем ID перетаскиваемого объекта
      const id = e?.target?.id?.();
      
      if (id) {
        // Сохраняем начальную позицию для использования при блокировке осей
        const cylinder = cylindersRef.current.find(c => c.id === id);
        const fitting = fittingsRef.current.find(f => f.id === id);
        
        if (cylinder) {
          dragStartPositionRef.current[id] = { x: cylinder.x, y: cylinder.y };
        } else if (fitting) {
          dragStartPositionRef.current[id] = { x: fitting.x, y: fitting.y };
        }
      }
      
      // Сохраняем состояние перед началом перетаскивания
      saveState();
      // При старте перетаскивания сбрасываем подсветку/выбор для размерных линий
      isObjectDraggingRef.current = true;
      setHighlightedSide(null);
      setHighlightedPoint(null);
      // Если уже была выбрана первая точка/сторона для размера — отменяем выбор
      setSelectedElements((prev) => (prev && prev.length > 0 ? [] : prev));
    },
    [saveState]
  );
  
  // Функция для получения заблокированных осей объекта (из-за привязок к заблокированным объектам)
  const getLockedAxes = useCallback((objectId, objectType) => {
    const constraints = objectConstraintsRef.current;
    let lockedX = false;
    let lockedY = false;
    
    // Получаем текущий объект и его rotation
    let currentObject = null;
    if (objectType === 'fitting') {
      currentObject = fittingsRef.current.find(f => f.id === objectId);
    } else if (objectType === 'cylinder') {
      currentObject = cylindersRef.current.find(c => c.id === objectId);
    }
    
    const rotation = currentObject?.rotation || 0;
    
    // Находим все привязки к заблокированным объектам
    constraints.forEach(constraint => {
      let targetObjectId = null;
      let targetObjectType = null;
      let sourceEdgeType = null;
      const constraintType = constraint.type || 'point';
      
      if (constraint.obj1.objectId === objectId && constraint.obj1.objectType === objectType) {
        targetObjectId = constraint.obj2.objectId;
        targetObjectType = constraint.obj2.objectType;
        if (constraintType === 'edge') {
          sourceEdgeType = constraint.obj1.edgeType;
        }
      } else if (constraint.obj2.objectId === objectId && constraint.obj2.objectType === objectType) {
        targetObjectId = constraint.obj1.objectId;
        targetObjectType = constraint.obj1.objectType;
        if (constraintType === 'edge') {
          sourceEdgeType = constraint.obj2.edgeType;
        }
      }
      
      if (!targetObjectId) return;
      
      // Проверяем, заблокирован ли целевой объект
      let targetObject = null;
      if (targetObjectType === 'fitting') {
        targetObject = fittingsRef.current.find(f => f.id === targetObjectId);
      } else if (targetObjectType === 'cylinder') {
        targetObject = cylindersRef.current.find(c => c.id === targetObjectId);
      }
      
      if (targetObject && targetObject.locked) {
        // Определяем, какие оси блокируются
        if (constraintType === 'edge') {
          // sourceEdgeType - это ЛОКАЛЬНЫЙ тип стенки (из constraint)
          // Нужно преобразовать его в глобальный с учетом ТЕКУЩЕГО rotation объекта
          
          const normalizedRotation = ((rotation % 360) + 360) % 360;
          const rotationSteps = Math.round(normalizedRotation / 90) % 4;
          
          // Карта преобразований: из локального типа в глобальный
          const edgeRotationMap = {
            'top': ['top', 'right', 'bottom', 'left'],
            'right': ['right', 'bottom', 'left', 'top'],
            'bottom': ['bottom', 'left', 'top', 'right'],
            'left': ['left', 'top', 'right', 'bottom']
          };
          
          const globalEdgeType = edgeRotationMap[sourceEdgeType]?.[rotationSteps] || sourceEdgeType;
          
          // Глобальные стенки блокируют оси так:
          // top/bottom (горизонтальные в мировых координатах) блокируют Y
          // left/right (вертикальные в мировых координатах) блокируют X
          
          if (globalEdgeType === 'top' || globalEdgeType === 'bottom') {
            lockedY = true;
          } else if (globalEdgeType === 'left' || globalEdgeType === 'right') {
            lockedX = true;
          }
          
        } else {
          // Для точек блокируются обе оси
          lockedX = true;
          lockedY = true;
        }
      }
    });
    
    return { lockedX, lockedY };
  }, []);

  // Функция для корректировки позиции объекта, чтобы соблюсти привязки к заблокированным объектам
  const correctPositionForLockedConstraints = useCallback((objectId, objectType) => {
    const constraints = objectConstraintsRef.current;
    
    // Находим все привязки к заблокированным объектам
    const lockedConstraints = constraints.filter(constraint => {
      let targetObjectId = null;
      let targetObjectType = null;
      
      if (constraint.obj1.objectId === objectId && constraint.obj1.objectType === objectType) {
        targetObjectId = constraint.obj2.objectId;
        targetObjectType = constraint.obj2.objectType;
      } else if (constraint.obj2.objectId === objectId && constraint.obj2.objectType === objectType) {
        targetObjectId = constraint.obj1.objectId;
        targetObjectType = constraint.obj1.objectType;
      }
      
      if (!targetObjectId) return false;
      
      // Проверяем, заблокирован ли целевой объект
      let targetObject = null;
      if (targetObjectType === 'fitting') {
        targetObject = fittingsRef.current.find(f => f.id === targetObjectId);
      } else if (targetObjectType === 'cylinder') {
        targetObject = cylindersRef.current.find(c => c.id === targetObjectId);
      }
      
      return targetObject && targetObject.locked;
    });
    
    if (lockedConstraints.length === 0) return; // Нет привязок к заблокированным объектам
    
    
    // Обрабатываем каждую привязку и корректируем позицию
    lockedConstraints.forEach(constraint => {
      // Определяем какой объект заблокирован и параметры привязки
      let lockedObjectId, lockedObjectType;
      let sourceEdgeType, targetEdgeType;
      let sourcePointIndex, targetPointIndex;
      const constraintType = constraint.type || 'point';
      
      if (constraint.obj1.objectId === objectId) {
        lockedObjectId = constraint.obj2.objectId;
        lockedObjectType = constraint.obj2.objectType;
        if (constraintType === 'edge') {
          sourceEdgeType = constraint.obj1.edgeType;
          targetEdgeType = constraint.obj2.edgeType;
        } else {
          sourcePointIndex = constraint.obj1.pointIndex;
          targetPointIndex = constraint.obj2.pointIndex;
        }
      } else {
        lockedObjectId = constraint.obj1.objectId;
        lockedObjectType = constraint.obj1.objectType;
        if (constraintType === 'edge') {
          sourceEdgeType = constraint.obj2.edgeType;
          targetEdgeType = constraint.obj1.edgeType;
        } else {
          sourcePointIndex = constraint.obj2.pointIndex;
          targetPointIndex = constraint.obj1.pointIndex;
        }
      }
      
      // Вычисляем смещение для восстановления привязки
      let sourcePos, targetPos;
      if (constraintType === 'edge') {
        sourcePos = getObjectEdgeCenter(objectId, objectType, sourceEdgeType);
        targetPos = getObjectEdgeCenter(lockedObjectId, lockedObjectType, targetEdgeType);
      } else {
        sourcePos = getObjectPointAbsolutePosition(objectId, objectType, sourcePointIndex);
        targetPos = getObjectPointAbsolutePosition(lockedObjectId, lockedObjectType, targetPointIndex);
      }
      
      if (!sourcePos || !targetPos) return;
      
      let dxStage = targetPos.x - sourcePos.x;
      let dyStage = targetPos.y - sourcePos.y;
      
      // Для привязок стенок применяем смещение только по нужной оси
      if (constraintType === 'edge') {
        // sourceEdgeType - локальный, нужно преобразовать в глобальный
        const obj = objectType === 'fitting'
          ? fittingsRef.current.find(f => f.id === objectId)
          : cylindersRef.current.find(c => c.id === objectId);
        const rotation = obj?.rotation || 0;
        const normalizedRotation = ((rotation % 360) + 360) % 360;
        const rotationSteps = Math.round(normalizedRotation / 90) % 4;
        
        const edgeRotationMap = {
          'top': ['top', 'right', 'bottom', 'left'],
          'right': ['right', 'bottom', 'left', 'top'],
          'bottom': ['bottom', 'left', 'top', 'right'],
          'left': ['left', 'top', 'right', 'bottom']
        };
        
        const globalSourceEdgeType = edgeRotationMap[sourceEdgeType]?.[rotationSteps] || sourceEdgeType;
        
        // Если привязаны горизонтальные стенки (top/bottom), блокируется только ось Y
        // Если привязаны вертикальные стенки (left/right), блокируется только ось X
        const axisLocked = (globalSourceEdgeType === 'top' || globalSourceEdgeType === 'bottom') ? 'y' : 'x';
        
        if (axisLocked === 'x') {
          dyStage = 0; // Разрешаем движение по Y, блокируем только X
        } else if (axisLocked === 'y') {
          dxStage = 0; // Разрешаем движение по X, блокируем только Y
        }
        
      }
      
      const dx = dxStage / (effectiveZoom || 1);
      const dy = dyStage / (effectiveZoom || 1);
      
      // Игнорируем микросмещения
      if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) return;
      
      // Корректируем позицию объекта
      if (objectType === 'fitting') {
        setFittings(prev => {
          const updated = prev.map(f => {
            if (f.id === objectId) {
              if (f.groupX !== undefined || f.groupY !== undefined) {
                return { ...f, groupX: (f.groupX || 0) + dx, groupY: (f.groupY || 0) + dy };
              } else {
                return { ...f, x: f.x + dx, y: f.y + dy };
              }
            }
            return f;
          });
          fittingsRef.current = updated; // Обновляем ref
          return updated;
        });
      } else if (objectType === 'cylinder') {
        setCylinders(prev => {
          const updated = prev.map(c => {
            if (c.id === objectId) {
              if (c.groupX !== undefined || c.groupY !== undefined) {
                return { ...c, groupX: (c.groupX || 0) + dx, groupY: (c.groupY || 0) + dy };
              } else {
                return { ...c, x: c.x + dx, y: c.y + dy };
              }
            }
            return c;
          });
          cylindersRef.current = updated; // Обновляем ref
          return updated;
        });
      }
    });
  }, [getObjectEdgeCenter, getObjectPointAbsolutePosition, effectiveZoom]);

  const handleDragEnd = useCallback(
    (e) => {
      // Сохраняем состояние после завершения перетаскивания
      saveState();
      isObjectDraggingRef.current = false;
      const id = e?.target?.id?.();
      if (id) {
        // Очищаем сохраненную начальную позицию
        if (dragStartPositionRef.current[id]) {
          delete dragStartPositionRef.current[id];
        }
        
        const cylinderMatch = cylindersRef.current.find((c) => c.id === id);
        if (cylinderMatch) {
          rebuildClonesForBase('cylinder', id);
          // Сначала корректируем позицию для привязок к заблокированным объектам
          correctPositionForLockedConstraints(id, 'cylinder');
          // Затем обновляем привязанные объекты после перемещения
          setTimeout(() => updateConstrainedObjects(id, 'cylinder'), 0);
        }
        const fittingMatch = fittingsRef.current.find((f) => f.id === id);
        if (fittingMatch) {
          rebuildClonesForBase('fitting', id);
          // Сначала корректируем позицию для привязок к заблокированным объектам
          correctPositionForLockedConstraints(id, 'fitting');
          // Затем обновляем привязанные объекты после перемещения
          setTimeout(() => updateConstrainedObjects(id, 'fitting'), 0);
        }
      }
    },
    [saveState, rebuildClonesForBase, updateConstrainedObjects, correctPositionForLockedConstraints]
  );  

  const handleResizeStart = useCallback(
    (e) => {
      // Блокируем изменение размера объектов при использовании средней кнопки мыши (панорамирование)
      if (e.evt && e.evt.button === 1) {
        e.target.stopDrag();
        return;
      }
      
      // Получаем ID объекта (маркер resize - это дочерний элемент)
      const id = e?.target?.getParent?.()?.id?.();
      
      if (id) {
        // Определяем тип объекта
        let objectType = null;
        if (cylindersRef.current.find(c => c.id === id)) {
          objectType = 'cylinder';
        } else if (fittingsRef.current.find(f => f.id === id)) {
          objectType = 'fitting';
        }
        
        // Проверяем, привязан ли объект к заблокированному объекту
        if (objectType && isConstrainedToLockedObject(id, objectType)) {
          // Отменяем drag маркера resize
          if (e.target.stopDrag) {
            e.target.stopDrag();
          }
          return;
        }
      }
      
      saveState();
    },
    [saveState, isConstrainedToLockedObject]
  );
  
  const handleResizeEnd = useCallback(
    (e) => {
      saveState();
      const id = e?.target?.getParent?.()?.id?.();
      if (id) {
        const cylinderMatch = cylindersRef.current.find((c) => c.id === id);
        if (cylinderMatch) {
          rebuildClonesForBase('cylinder', id);
          // Обновляем привязанные объекты после изменения размера
          setTimeout(() => updateConstrainedObjects(id, 'cylinder'), 0);
        }
        const fittingMatch = fittingsRef.current.find((f) => f.id === id);
        if (fittingMatch) {
          rebuildClonesForBase('fitting', id);
          // Обновляем привязанные объекты после изменения размера
          setTimeout(() => updateConstrainedObjects(id, 'fitting'), 0);
        }
      }
    },
    [saveState, rebuildClonesForBase, updateConstrainedObjects]
  );  


  const getTextWidth = (text, fontSizeArg = 16, fontFamilyArg = 'Arial', fontStyleArg = 'normal') => {
    const tempText = new Konva.Text({
      text: text,
      fontSize: fontSizeArg,
      fontFamily: fontFamilyArg,
      fontStyle: fontStyleArg,
    });
    return tempText.width();
  };
  
  const getTextHeight = (text, fontSizeArg = 16, fontFamilyArg = 'Arial', fontStyleArg = 'normal', lineHeightArg = 1.2) => {
    const lines = (text || '').split('\n');
    const tempText = new Konva.Text({
      text: 'M',
      fontSize: fontSizeArg,
      fontFamily: fontFamilyArg,
      fontStyle: fontStyleArg,
      lineHeight: lineHeightArg,
    });
    const singleLineH = tempText.height();
    return Math.max(fontSizeArg, Math.round(lines.length * singleLineH));
  };

  // Начальные тексты (подстроите координаты/размеры под себя)
  const getInitialAnnotations = useCallback((landscape, needsCratingReferenceNote) => {
    const initialsText = userInitials || 'Инициалы';
    // Определяем, какую аннотацию показывать (7 или 8)
    const dimensionAnnoId = needsCratingReferenceNote ? 'anno-init-8' : 'anno-init-7';
    
    if (landscape) {
      const baseAnnotations = [
        { id: 'anno-init-1',  x: 1340,  y: 925,  text: 'Артикул', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 28, align: 'left', fill: '#000000' },
        { id: 'anno-init-2',  x: 1247,  y: 1000,  text: 'Оборудование\nдля обвязки\nкотельной', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 24, align: 'center', fill: '#000000' },
        { id: 'anno-init-3',  x: 976,  y: 1000,  text: initialsText, fontFamily: 'Arial', fontStyle: 'italic', fontSize: 14, align: 'left', fill: '#000000' },
        { id: 'anno-init-4',  x: 1475,  y: 1075,  text: 'Зеркало', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 28, align: 'left', fill: '#000000' },
        { id: 'anno-init-5',  x: 1466,  y: 1000,  text: 'Вес', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 20, align: 'center', fill: '#000000' },
        { id: 'anno-init-6',  x: 1190,  y: 1065,  text: 'AISI304(2мм)', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 20, align: 'center', fill: '#000000' },
      ];
      
      // Добавляем ОБЕ аннотации, но одна будет скрыта
      baseAnnotations.push(
        { id: 'anno-init-7',  x: 870,  y: 810,  text: 'Размер для справок:\n1. Размер упаковки ШхДхВ\n2. Неуказанные отклонения размеров межосевых\nрасстояний +-1.5мм\n3. Неуказанные отклонения остальных размеров +-3мм', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 14, align: 'left', fill: '#000000', isHidden: dimensionAnnoId !== 'anno-init-7' },
        { id: 'anno-init-8',  x: 870,  y: 815,  text: 'Размер для справок:\n1. Размер упаковки ШхДхВ\n2. Неуказанные отклонения размеров межосевых расстояний +-1.5мм.\n3. Неуказанные отклонения остальных размеров +-3мм.\n4. Для отправки изделия необходима обрешетка. Стоимость обрешетки\nне входит в стоимость изделия и назначается транспортной компанией.', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 11, align: 'left', fill: '#000000', isHidden: dimensionAnnoId !== 'anno-init-8' }
      );
      
      return baseAnnotations;
    }
    
    const baseAnnotations = [
      { id: 'anno-init-1',  x: 620,  y: 982,  text: 'Артикул', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 20, align: 'left', fill: '#000000' },
      { id: 'anno-init-2',  x: 550,  y: 1037,  text: 'Оборудование\nдля обвязки\nкотельной', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 16, align: 'center', fill: '#000000' },
      { id: 'anno-init-3',  x: 362,  y: 1035,  text: initialsText, fontFamily: 'Arial', fontStyle: 'italic', fontSize: 10, align: 'left', fill: '#000000' },
      { id: 'anno-init-4',  x: 710,  y: 1090,  text: 'Зеркало', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 20, align: 'left', fill: '#000000' },
      { id: 'anno-init-5',  x: 708,  y: 1037,  text: 'Вес', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 16, align: 'center', fill: '#000000' },
      { id: 'anno-init-6',  x: 510,  y: 1080,  text: 'AISI304(2мм)', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 16, align: 'center', fill: '#000000' },
    ];
    
    // Добавляем ОБЕ аннотации, но одна будет скрыта
    baseAnnotations.push(
      { id: 'anno-init-7',  x: 290,  y: 875,  text: 'Размер для справок:\n1. Размер упаковки ШхДхВ\n2. Неуказанные отклонения размеров межосевых\nрасстояний +-1.5мм\n3. Неуказанные отклонения остальных размеров +-3мм', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 14, align: 'left', fill: '#000000', isHidden: dimensionAnnoId !== 'anno-init-7' },
      { id: 'anno-init-8',  x: 290,  y: 880,  text: 'Размер для справок:\n1. Размер упаковки ШхДхВ\n2. Неуказанные отклонения размеров межосевых расстояний +-1.5мм.\n3. Неуказанные отклонения остальных размеров +-3мм.\n4. Для отправки изделия необходима обрешетка. Стоимость обрешетки\nне входит в стоимость изделия и назначается транспортной компанией.', fontFamily: 'Arial', fontStyle: 'italic', fontSize: 11, align: 'left', fill: '#000000', isHidden: dimensionAnnoId !== 'anno-init-8' }
    );
    
    return baseAnnotations;
  }, [userInitials]);

  // Функция расчета веса трубы (материал AISI 304, плотность 7.93 г/см³)
  const calculatePipeWeight = useCallback((cylinder) => {
    const ps = cylinder.pipeSpec;
    if (!ps) return 0;

    const density = 0.00000793; // кг/мм³ для AISI 304 (7.93 г/см³ = 7930 кг/м³)
    
    if (ps.shape === 'round' && ps.diameter && ps.thickness) {
      const D = Number(ps.diameter); // наружный диаметр (мм)
      const thicknessStr = String(ps.thickness).replace('x', ''); // убираем 'x'
      const t = Number(thicknessStr); // толщина стенки (мм)
      
      // Определяем длину трубы (перпендикулярная размеру диаметра)
      const dw = Math.abs((cylinder.width || 0) - D);
      const dh = Math.abs((cylinder.height || 0) - D);
      const L = dw <= dh ? (cylinder.height || 0) : (cylinder.width || 0); // длина в мм
      
      if (!Number.isFinite(D) || !Number.isFinite(t) || !Number.isFinite(L)) return 0;
      
      // Точная формула: Вес (кг) = π/4 × (D² - d²) × L × density, где d = D - 2t
      const d = D - 2 * t; // внутренний диаметр
      const crossSectionArea = (Math.PI / 4) * (D * D - d * d); // площадь сечения (мм²)
      const weight = crossSectionArea * L * density;
      return weight;
    }
    
    if (ps.shape === 'square' && ps.profile && ps.thickness) {
      const profileParts = String(ps.profile).split('x');
      if (profileParts.length !== 2) return 0;
      
      const a = Number(profileParts[0]); // размер стороны (мм)
      const b = Number(profileParts[1]); // размер второй стороны (мм)
      const thicknessStr = String(ps.thickness);
      const t = Number(thicknessStr); // толщина стенки (мм)
      
      // Определяем длину
      const L = Math.max(cylinder.width || 0, cylinder.height || 0); // длина в мм
      
      if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(t) || !Number.isFinite(L)) return 0;
      
      // Проверяем тип: лист или профиль
      if (ps.squareType === 'sheet') {
        // ЛИСТ - плоский лист металла, не труба с полостью
        // Ширина листа зависит от профиля:
        // 60x30 -> ширина 112.3 мм
        // 80x40 -> ширина 152.3 мм
        let sheetWidth;
        if (a === 60 && b === 30) {
          sheetWidth = 112.3;
        } else if (a === 80 && b === 40) {
          sheetWidth = 152.3;
        } else {
          // Для других размеров используем приблизительную развёртку
          sheetWidth = 2 * (a + b) - 4 * t;
        }
        
        // Формула для листа: ширина × длина × толщина × плотность
        const weight = sheetWidth * L * t * density;
        return weight;
      } else {
        // ПРОФИЛЬ - квадратная/прямоугольная труба с полостью
        // Точная формула для прямоугольного профиля: 
        // Площадь сечения = (a × b) - ((a - 2t) × (b - 2t))
        const outerArea = a * b;
        const innerArea = (a - 2*t) * (b - 2*t);
        const crossSectionArea = outerArea - innerArea; // площадь сечения (мм²)
        const weight = crossSectionArea * L * density;
        return weight;
      }
    }
    
    return 0;
  }, []);

  // Функция расчета веса фитинга (материал AISI 304)
  const calculateFittingWeight = useCallback((fitting) => {
    const itemKey = fitting.itemKey;
    if (!itemKey) return 0;
    
    // Если у фитинга уже есть рассчитанный вес (например, для KRGB), используем его
    if (fitting.weight !== undefined && fitting.weight !== null) {
      return Number(fitting.weight) || 0;
    }
    
    const fittingData = fittingsData[itemKey];
    if (!fittingData) return 0;

    const density = 0.00000793; // кг/мм³ для AISI 304
    
    // Для ниппелей и муфт рассчитываем вес по формуле
    if (itemKey.startsWith('Nippel_') || itemKey.startsWith('Mufta_')) {
      const D = Number(fittingData.diameter); // наружный диаметр (мм)
      const t = 3; // толщина стенки 3 мм для всех фитингов
      const L = Number(fitting.height || fittingData.length || 0); // длина (мм)
      
      if (!Number.isFinite(D) || !Number.isFinite(L)) return 0;
      
      // Формула для круглой трубы: Вес (кг) = π/4 × (D² - d²) × L × density
      const d = D - 2 * t; // внутренний диаметр
      const crossSectionArea = (Math.PI / 4) * (D * D - d * d); // площадь сечения (мм²)
      const weight = crossSectionArea * L * density;
      return weight;
    }
    
    // Для остальных фитингов берем фиксированный вес из fittingsData (если есть)
    return Number(fittingData.weight) || 0;
  }, []);

  // Функция для определения максимальной толщины труб в проекте
  const getMaxPipeThickness = useCallback(() => {
    let maxDiameter = 0;
    let thicknessForMaxDiameter = 0;
    
    cylinders.forEach(cylinder => {
      const ps = cylinder.pipeSpec;
      if (!ps) return;
      
      let diameter = 0;
      let thickness = 0;
      
      // Для круглых труб
      if (ps.shape === 'round' && ps.diameter) {
        diameter = Number(ps.diameter);
        if (ps.thickness) {
          const thicknessStr = String(ps.thickness).replace('x', '');
          thickness = Number(thicknessStr);
        }
      }
      
      // Для квадратных труб - используем эквивалентный диаметр через периметр
      if (ps.shape === 'square' && ps.profile) {
        const parts = String(ps.profile).split('x');
        if (parts.length === 2) {
          const a = Number(parts[0]) || 0;
          const b = Number(parts[1]) || 0;
          const perimeter = 2 * (a + b);
          diameter = perimeter / Math.PI; // Эквивалентный диаметр
        }
        if (ps.thickness) {
          const thicknessStr = String(ps.thickness);
          thickness = Number(thicknessStr);
        }
      }
      
      // Если это труба с максимальным диаметром, сохраняем её толщину
      if (!isNaN(diameter) && diameter > maxDiameter) {
        maxDiameter = diameter;
        thicknessForMaxDiameter = thickness;
      }
    });
    
    return thicknessForMaxDiameter;
  }, [cylinders]);

  // Автоматический расчет веса всех труб и фитингов
  useEffect(() => {
    let totalWeight = 0;
    
    // Вес труб
    cylinders.forEach(cylinder => {
      totalWeight += calculatePipeWeight(cylinder);
    });
    
    // Вес фитингов (пропускаем скрытые)
    fittings.filter(f => !f.isHidden).forEach(fitting => {
      totalWeight += calculateFittingWeight(fitting);
    });
    
    // Обновляем аннотацию "Вес" с одной цифрой после запятой
    // "кг" выводится на новой строке под цифрой
    const weightText = totalWeight > 0 ? `${totalWeight.toFixed(1)}\nкг` : 'Вес';
    setAnnotations(prev => prev.map(a => 
      a.id === 'anno-init-5' ? { ...a, text: weightText } : a
    ));
  }, [cylinders, fittings, calculatePipeWeight, calculateFittingWeight]);

  // Автоматическое обновление аннотации AISI304 с толщиной
  useEffect(() => {
    const maxThickness = getMaxPipeThickness();
    
    // Если есть трубы, обновляем текст с актуальной толщиной
    // Если труб нет, оставляем просто AISI304 без толщины
    const thicknessText = maxThickness > 0 
      ? `AISI304(${maxThickness}мм)` 
      : 'AISI304';
    
    setAnnotations(prev => prev.map(a => 
      a.id === 'anno-init-6' ? { ...a, text: thicknessText } : a
    ));
  }, [cylinders, getMaxPipeThickness]);
  
  // Автоматическое обновление аннотации с данными цеха (anno-init-9)
  useEffect(() => {
    if (workshopInfo) {
      // Сохраняем количество в metadata для использования в именах файлов IGES/DXF
      setCanvasMetadata(prev => ({
        ...prev,
        quantity: workshopInfo.quantity || 1,
      }));
      
      // Преобразуем дату из формата YYYY-MM-DD в DD.MM.YYYY
      let formattedDeadline = workshopInfo.deadline;
      if (workshopInfo.deadline && workshopInfo.deadline.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Формат YYYY-MM-DD
        const [year, month, day] = workshopInfo.deadline.split('-');
        formattedDeadline = `${day}.${month}.${year}`;
      }
      
      // Формируем текст с данными цеха
      const workshopText = `Сделка: ${workshopInfo.deal}. Крайний срок: ${formattedDeadline}. г.${workshopInfo.city}`;
      
      // Вычисляем подходящий размер шрифта, чтобы текст не выходил за пределы рабочей области
      const workspaceWidth = isLandscape ? 1189 : 841; // Ширина рабочей области A3
      const startX = isLandscape ? 400 : 80;
      const rightMargin = isLandscape ? 10 : 200; // Для вертикальной: большой отступ, т.к. правая граница рабочей области ~640
      const availableWidth = workspaceWidth - startX - rightMargin;
      
      // Примерная ширина символа = fontSize * коэффициент (для Arial italic, эмпирический коэффициент)
      const baseFontSize = isLandscape ? 28 : 20;
      const charWidthCoeff = isLandscape ? 0.4 : 0.5; // Для вертикальной ориентации увеличен коэффициент
      const estimatedTextWidth = workshopText.length * baseFontSize * charWidthCoeff;
      
      let adjustedFontSize = baseFontSize;
      if (estimatedTextWidth > availableWidth) {
        // Уменьшаем шрифт пропорционально
        adjustedFontSize = Math.max(10, Math.floor(baseFontSize * (availableWidth / estimatedTextWidth)));
      }
      
      setAnnotations(prev => {
        // Проверяем, есть ли уже аннотация anno-init-9
        const hasWorkshopAnnotation = prev.some(a => a.id === 'anno-init-9');
        
        if (!hasWorkshopAnnotation) {
          // Добавляем новую аннотацию сверху чертежа
          return [
            ...prev,
            {
              id: 'anno-init-9',
              x: startX,
              y: isLandscape ? 35 : 30,
              text: workshopText,
              fontFamily: 'Arial',
              fontStyle: 'italic',
              fontSize: adjustedFontSize,
              align: 'left',
              fill: '#000000'
            }
          ];
        } else {
          // Обновляем существующую аннотацию (текст, позицию и размер шрифта)
          return prev.map(a =>
            a.id === 'anno-init-9' ? { 
              ...a, 
              text: workshopText,
              x: startX,
              y: isLandscape ? 35 : 30,
              fontSize: adjustedFontSize,
            } : a
          );
        }
      });
      
      // ====================================================================
      // НАСТРОЙКА ЛИНИИ ПОД ТЕКСТОМ ЦЕХА
      // ====================================================================
      // Черная горизонтальная линия под текстом "Сделка: ... Крайний срок: ..."
      // 
      // Координаты для ГОРИЗОНТАЛЬНОЙ ориентации (isLandscape = true):
      const lineY_landscape = 72;      // Y-координата линии (вертикальная позиция)
      const lineX1_landscape = 340;    // Начало линии по X (левая точка)
      const lineX2_landscape = 1567;   // Конец линии по X (правая точка)
      
      // Координаты для ВЕРТИКАЛЬНОЙ ориентации (isLandscape = false):
      const lineY_portrait = 55;       // Y-координата линии (вертикальная позиция)
      const lineX1_portrait = 52;      // Начало линии по X (левая точка)
      const lineX2_portrait = 742;     // Конец линии по X (правая точка)
      
      // Применяем координаты в зависимости от ориентации
      const lineY = isLandscape ? lineY_landscape : lineY_portrait;
      const lineX1 = isLandscape ? lineX1_landscape : lineX1_portrait;
      const lineX2 = isLandscape ? lineX2_landscape : lineX2_portrait;
      // ====================================================================
      
      setWorkshopLine({
        points: [lineX1, lineY, lineX2, lineY],
        stroke: '#000000',
        strokeWidth: 2
      });
    } else {
      // Если workshopInfo == null, удаляем аннотацию и линию
      setAnnotations(prev => prev.filter(a => a.id !== 'anno-init-9'));
      setWorkshopLine(null);
    }
  }, [workshopInfo, isLandscape]);

  // Автоматическое добавление/удаление аннотации с материалом покрытия (anno-init-10)
  // Когда оформляем для цеха, показываем материал вместо "Размер для справок"
  useEffect(() => {
    if (workshopInfo) {
      // Получаем текст покрытия из anno-init-4
      const coatingAnnotation = annotations.find(a => a.id === 'anno-init-4');
      const coatingText = coatingAnnotation?.text || 'Зеркало';
      
      setAnnotations(prev => {
        // Проверяем, есть ли уже аннотация anno-init-10
        const hasWorkshopMaterialAnnotation = prev.some(a => a.id === 'anno-init-10');
        
        // Скрываем anno-init-7 и anno-init-8
        const withoutDimensionAnnotations = prev.filter(a => a.id !== 'anno-init-7' && a.id !== 'anno-init-8');
        
        if (!hasWorkshopMaterialAnnotation) {
          // Добавляем новую аннотацию с покрытием
          return [
            ...withoutDimensionAnnotations,
            {
              id: 'anno-init-10',
              x: isLandscape ? 870 : 290,
              y: isLandscape ? 810 : 885,
              text: coatingText,
              fontFamily: 'Arial',
              fontStyle: 'bold',
              fontSize: isLandscape ? 76 : 64,
              align: 'left',
              fill: '#000000'
            }
          ];
        } else {
          // Обновляем существующую аннотацию
          return withoutDimensionAnnotations.map(a =>
            a.id === 'anno-init-10' ? { 
              ...a, 
              text: coatingText,
              x: isLandscape ? 870 : 290,
              y: isLandscape ? 810 : 885,
              fontSize: isLandscape ? 76 : 64,
            } : a
          );
        }
      });
    } else {
      // Если workshopInfo == null, удаляем anno-init-10 и восстанавливаем anno-init-7/8
      setAnnotations(prev => {
        const withoutMaterialAnnotation = prev.filter(a => a.id !== 'anno-init-10');
        
        // Проверяем, есть ли уже anno-init-7 или anno-init-8
        const hasInitialAnnotations = withoutMaterialAnnotation.some(a => a.id === 'anno-init-7' || a.id === 'anno-init-8');
        
        if (!hasInitialAnnotations) {
          // Восстанавливаем начальные аннотации (обе: anno-init-7 и anno-init-8)
          const initialAnnotations = getInitialAnnotations(
            isLandscape,
            computeNeedsCratingReferenceNote(fittings, canvasMetadata)
          );
          const dimensionAnnotations = initialAnnotations.filter(a => a.id === 'anno-init-7' || a.id === 'anno-init-8');
          
          if (dimensionAnnotations.length > 0) {
            return [...withoutMaterialAnnotation, ...dimensionAnnotations];
          }
        }
        
        return withoutMaterialAnnotation;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopInfo, isLandscape]); // Убираем annotations, getInitialAnnotations, fittings из зависимостей
     
  const { getSnappedPosition } = useSnapping({
    snappingMode,
    gridSize: 50,
    snapThreshold,
    cylinders,
    fittings,
    stageRef,
    zoom,
  }); 

  // Функция для загрузки и парсинга DXF-файла
  const loadDxf = useCallback(
    async (itemKey, x, y, type = 'fitting', fastenerConfig = null) => {
      try {
        let fileName;
        let folder;
  
        if (type === 'fitting') {
          fileName = fittingsData[itemKey].fileName;
          folder = 'fittings';
        } else if (type === 'fastener') {
          fileName = fastenersData[itemKey].fileName;
          folder = 'fastener';
        } else {
          throw new Error(`Неизвестный тип: ${type}`);
        }
  
        const response = await fetch(`/${folder}/${fileName}`);
        if (!response.ok) {
          throw new Error(`Не удалось загрузить DXF-файл: ${response.statusText}`);
        }
        const dxfText = await response.text();
  
        const parser = new DxfParser();
        const dxf = parser.parseSync(dxfText);
  
        // Преобразование сущностей DXF в фигуры Konva
        const shapes = convertDxfToKonvaShapes(dxf);
  
        // Вычисление границ (bounding box) объекта
        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
  
        shapes.forEach((shape) => {
          if (shape.type === 'line' || shape.type === 'polyline') {
            const points = shape.points;
            for (let i = 0; i < points.length; i += 2) {
              const px = points[i];
              const py = points[i + 1];
              if (px < minX) minX = px;
              if (px > maxX) maxX = px;
              if (py < minY) minY = py;
              if (py > maxY) maxY = py;
            }
          } else if (shape.type === 'circle') {
            const x1 = shape.x - shape.radius;
            const x2 = shape.x + shape.radius;
            const y1 = shape.y - shape.radius;
            const y2 = shape.y + shape.radius;
            if (x1 < minX) minX = x1;
            if (x2 > maxX) maxX = x2;
            if (y1 < minY) minY = y1;
            if (y2 > maxY) maxY = y2;
          } else if (shape.type === 'arc') {
            // Для дуги вычисляем bounding box точнее
            // Проверяем крайние точки дуги и углы 0, 90, 180, 270 если они попадают в диапазон
            const { x: cx, y: cy, radius, startAngle, endAngle } = shape;
            
            // Добавляем начальную и конечную точки дуги
            const points = [
              cx + radius * Math.cos(startAngle),
              cy + radius * Math.sin(startAngle),
              cx + radius * Math.cos(endAngle),
              cy + radius * Math.sin(endAngle)
            ];
            
            // Проверяем ключевые углы (0, π/2, π, 3π/2)
            const keyAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
            keyAngles.forEach(angle => {
              // Нормализуем углы в диапазон [0, 2π)
              let normalizedStart = ((startAngle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
              let normalizedEnd = ((endAngle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
              
              // Проверяем, попадает ли угол в диапазон дуги
              let inRange = false;
              if (normalizedStart <= normalizedEnd) {
                inRange = angle >= normalizedStart && angle <= normalizedEnd;
              } else {
                inRange = angle >= normalizedStart || angle <= normalizedEnd;
              }
              
              if (inRange) {
                points.push(cx + radius * Math.cos(angle));
                points.push(cy + radius * Math.sin(angle));
              }
            });
            
            // Обновляем границы
            for (let i = 0; i < points.length; i += 2) {
              const px = points[i];
              const py = points[i + 1];
              if (px < minX) minX = px;
              if (px > maxX) maxX = px;
              if (py < minY) minY = py;
              if (py > maxY) maxY = py;
            }
          }
        });
  
        let objectWidth = maxX - minX;
        let objectHeight = maxY - minY;
  
        // Смещаем фигуры, чтобы начать от (0,0)
        shapes.forEach((shape) => {
          if (shape.type === 'line' || shape.type === 'polyline') {
            shape.points = shape.points.map((coord, index) =>
              index % 2 === 0 ? coord - minX : coord - minY
            );
          } else if (shape.type === 'circle' || shape.type === 'arc') {
            shape.x -= minX;
            shape.y -= minY;
          }
        });
  
        // ДЛЯ КРЕПЕЖЕЙ: Применяем трансформации и пересчитываем границы
        if (type === 'fastener' && fastenerConfig) {
          // Получаем базовые параметры из fastenersData
          const fastenerInfo = fastenersData[itemKey];
          const isFloorFastener = fastenerInfo?.isFloorFastener || false;
          
          if (isFloorFastener) {
            // НАПОЛЬНЫЕ КРЕПЕЖИ: обрезаем/растягиваем по высоте и ширине
            const baseHeight = fastenerInfo?.baseHeight || 555;
            const baseWidth = fastenerInfo?.diameter || 406.4; // Базовая ширина из DXF
            const desiredHeight = fastenerConfig.height || baseHeight;
            const desiredWidth = fastenerConfig.pipeDiameter || baseWidth; // Диаметр трубы = ширина крепежа
            const cropFromTop = fastenerInfo?.floorFastenerCropFromTop || false;
            const stretchFromMiddle = fastenerInfo?.stretchFromMiddle || false;
            const stretchFromSplit = fastenerInfo?.floorFastenerStretchFromSplit || false;
            const fixedBottomHeight = fastenerInfo?.fixedBottomHeight || 0;
            
            // ВАЖНО: Используем реальную высоту из DXF, а не базовую из конфига
            let actualDxfHeight = objectHeight; // Реальная высота из DXF файла
            const actualDxfWidth = objectWidth; // Реальная ширина из DXF файла
            const lowerPipe = Number(fastenerInfo?.lowerPipeLength) || 0;
            const rangeOffset = Number(fastenerConfig.rangeOffset) || 0;
            if (rangeOffset && lowerPipe > 0) {
              // Нижняя труба удлиняется или укорачивается под болтом, дальше обычная обрезка сверху.
              const boltCircles = shapes.filter((shape) => (
                shape.type === 'circle' && shape.radius >= 2.5 && shape.radius <= 16
              ));
              const joint = actualDxfHeight - 85 - lowerPipe;
              const anchorY = boltCircles.length ? Math.max(...boltCircles.map((shape) => shape.y)) : joint;
              const splitY = Math.min(actualDxfHeight - 120, anchorY + 45);
              shapes.forEach((shape) => {
                if (shape.type === 'line' || shape.type === 'polyline') {
                  shape.points = shape.points.map((coord, index) => (
                    index % 2 === 1 && coord > splitY ? coord + rangeOffset : coord
                  ));
                } else if ((shape.type === 'circle' || shape.type === 'arc') && shape.y > splitY) {
                  shape.y += rangeOffset;
                }
              });
              actualDxfHeight += rangeOffset;
              objectHeight = actualDxfHeight;
            }
            
            // Шаг 1: Изменение ширины (по диаметру трубы)
            if (stretchFromMiddle && desiredWidth !== actualDxfWidth) {
              const centerX = actualDxfWidth / 2; // Середина по X
              const differenceX = desiredWidth - actualDxfWidth;
              const halfShiftX = differenceX / 2;
              
              shapes.forEach((shape) => {
                if (shape.type === 'line' || shape.type === 'polyline') {
                  const newPoints = [...shape.points];
                  for (let i = 0; i < newPoints.length; i += 2) {
                    const x = newPoints[i];
                    if (x < centerX) {
                      // Левая половина - сдвигаем влево
                      newPoints[i] = x - halfShiftX;
                    } else if (x > centerX) {
                      // Правая половина - сдвигаем вправо
                      newPoints[i] = x + halfShiftX;
                    }
                  }
                  shape.points = newPoints;
                } else if (shape.type === 'circle' || shape.type === 'arc') {
                  const x = shape.x;
                  if (x < centerX) {
                    shape.x = x - halfShiftX;
                  } else if (x > centerX) {
                    shape.x = x + halfShiftX;
                  }
                }
              });
              
              // Обновляем ширину объекта
              objectWidth = desiredWidth;
            }
            
            // Шаг 2: Изменение высоты
            if (desiredHeight !== actualDxfHeight) {
              if (stretchFromMiddle) {
                // РАСТЯЖЕНИЕ ОТ СЕРЕДИНЫ: раздвигаем верхнюю и нижнюю половины
                const centerY = actualDxfHeight / 2; // Середина по Y
                const difference = desiredHeight - actualDxfHeight;
                const halfShift = difference / 2;
                
                shapes.forEach((shape) => {
                  if (shape.type === 'line' || shape.type === 'polyline') {
                    const newPoints = [...shape.points];
                    for (let i = 1; i < newPoints.length; i += 2) {
                      const y = newPoints[i];
                      if (y < centerY) {
                        // Верхняя половина - сдвигаем вверх (вычитаем)
                        newPoints[i] = y - halfShift;
                      } else if (y > centerY) {
                        // Нижняя половина - сдвигаем вниз (добавляем)
                        newPoints[i] = y + halfShift;
                      }
                      // Точки ровно на centerY остаются без изменений
                    }
                    shape.points = newPoints;
                  } else if (shape.type === 'circle' || shape.type === 'arc') {
                    const y = shape.y;
                    if (y < centerY) {
                      shape.y = y - halfShift;
                    } else if (y > centerY) {
                      shape.y = y + halfShift;
                    }
                  }
                });
                
                // Обновляем высоту объекта
                objectHeight = desiredHeight;
              } else if (stretchFromSplit && desiredHeight > actualDxfHeight) {
                // РАСТЯЖЕНИЕ ОТ ТОЧКИ РАЗДЕЛЕНИЯ: нижняя часть фиксирована, верхняя тянется вверх
                // splitY — Y-координата в DXF где заканчивается верхняя (растягиваемая) часть
                const splitY = actualDxfHeight - fixedBottomHeight;
                const difference = desiredHeight - actualDxfHeight;
                
                shapes.forEach((shape) => {
                  if (shape.type === 'line' || shape.type === 'polyline') {
                    const newPoints = [...shape.points];
                    for (let i = 1; i < newPoints.length; i += 2) {
                      const y = newPoints[i];
                      if (y < splitY) {
                        // Верхняя (растягиваемая) часть — сдвигаем вверх
                        newPoints[i] = y - difference;
                      }
                      // Нижняя (фиксированная) часть — не трогаем
                    }
                    shape.points = newPoints;
                  } else if (shape.type === 'circle' || shape.type === 'arc') {
                    if (shape.y < splitY) {
                      shape.y -= difference;
                    }
                  }
                });
                
                objectHeight = desiredHeight;
              } else if (cropFromTop && desiredHeight < actualDxfHeight) {
                // ОБРЕЗКА СВЕРХУ: удаляем верхнюю часть крепежа
                const cropAmount = actualDxfHeight - desiredHeight;
                const cropLine = cropAmount; // Y-координата линии обрезки (после нормализации к 0,0)
                
                shapes.forEach((shape) => {
                  if (shape.type === 'line' || shape.type === 'polyline') {
                    // Обрезаем линии: обрабатываем пересечения с линией обрезки
                    const newPoints = [];
                    
                    for (let i = 0; i < shape.points.length - 2; i += 2) {
                      const x1 = shape.points[i];
                      const y1 = shape.points[i + 1];
                      const x2 = shape.points[i + 2];
                      const y2 = shape.points[i + 3];
                      
                      const p1Below = y1 >= cropLine;
                      const p2Below = y2 >= cropLine;
                      
                      if (p1Below && p2Below) {
                        // Обе точки ниже линии обрезки - оставляем сегмент полностью
                        if (newPoints.length === 0 || newPoints[newPoints.length - 2] !== x1 || newPoints[newPoints.length - 1] !== y1 - cropAmount) {
                          newPoints.push(x1);
                          newPoints.push(y1 - cropAmount);
                        }
                        newPoints.push(x2);
                        newPoints.push(y2 - cropAmount);
                      } else if (p1Below && !p2Below) {
                        // P1 ниже, P2 выше - добавляем P1 и точку пересечения
                        if (newPoints.length === 0 || newPoints[newPoints.length - 2] !== x1 || newPoints[newPoints.length - 1] !== y1 - cropAmount) {
                          newPoints.push(x1);
                          newPoints.push(y1 - cropAmount);
                        }
                        
                        // Вычисляем точку пересечения с линией обрезки
                        if (y2 !== y1) {
                          const t = (cropLine - y1) / (y2 - y1);
                          const intersectX = x1 + t * (x2 - x1);
                          newPoints.push(intersectX);
                          newPoints.push(0); // После смещения это будет верхняя граница
                        }
                      } else if (!p1Below && p2Below) {
                        // P1 выше, P2 ниже - добавляем точку пересечения и P2
                        if (y2 !== y1) {
                          const t = (cropLine - y1) / (y2 - y1);
                          const intersectX = x1 + t * (x2 - x1);
                          newPoints.push(intersectX);
                          newPoints.push(0); // После смещения это будет верхняя граница
                        }
                        newPoints.push(x2);
                        newPoints.push(y2 - cropAmount);
                      }
                      // Если обе точки выше - сегмент удаляется
                    }
                    
                    shape.points = newPoints;
                  } else if (shape.type === 'circle' || shape.type === 'arc') {
                    // Для кругов и дуг: если центр выше линии обрезки - удаляем, иначе смещаем
                    if (shape.y >= cropLine) {
                      shape.y -= cropAmount;
                    } else {
                      // Помечаем для удаления (обработаем после цикла)
                      shape.toRemove = true;
                    }
                  }
                });
                
                // Удаляем помеченные shapes и shapes с пустыми точками
                const filteredShapes = shapes.filter(s => !s.toRemove && !(s.points && s.points.length === 0));
                shapes.length = 0;
                shapes.push(...filteredShapes);
                
                // Обновляем высоту объекта - теперь это desiredHeight
                objectHeight = desiredHeight;
              } else {
                // РАСТЯЖЕНИЕ: масштабируем по оси Y (для высот > actualDxfHeight)
                const scaleY = desiredHeight / actualDxfHeight;
                
                shapes.forEach((shape) => {
                  if (shape.type === 'line' || shape.type === 'polyline') {
                    shape.points = shape.points.map((coord, index) =>
                      index % 2 === 1 ? coord * scaleY : coord // Масштабируем только Y координаты
                    );
                  } else if (shape.type === 'circle' || shape.type === 'arc') {
                    shape.y *= scaleY;
                    // Радиус НЕ масштабируем для сохранения формы кругов/дуг
                  }
                });
                
                // Обновляем высоту объекта
                objectHeight = objectHeight * scaleY;
              }
            }

            // При отрицательном смещении задвинутая труба 1 1/4" выходит из открытого конца Ø48,3.
            const mufta = fastenerInfo?.noMufta ? 0 : 22;
            const typedHeight = desiredHeight + mufta;
            const floorClearance = Number(fastenerInfo?.pipeFloorClearance) || 85;
            // На нижней границе диапазона выступ равен |смещению| и достаёт до пола при −85.
            // Дальше каждый миллиметр высоты поднимает трубу на миллиметр.
            const stickOut = (Number(fastenerInfo?.minHeight) || 0) - typedHeight;
            if (lowerPipe > 0 && stickOut > 0.5) {
              const verticals = [];
              shapes.forEach((shape) => {
                if ((shape.type !== 'line' && shape.type !== 'polyline') || !shape.points) return;
                const pts = shape.points;
                for (let i = 0; i < pts.length - 2; i += 2) {
                  const x1 = pts[i];
                  const y1 = pts[i + 1];
                  const x2 = pts[i + 2];
                  const y2 = pts[i + 3];
                  if (Math.abs(x1 - x2) > 1.2 || Math.abs(y2 - y1) < 30) continue;
                  verticals.push({
                    x: (x1 + x2) / 2,
                    top: Math.min(y1, y2),
                    bottom: Math.max(y1, y2),
                  });
                }
              });
              const clusters = [];
              verticals.forEach((seg) => {
                const found = clusters.find((item) => Math.abs(item.x - seg.x) < 1.5);
                if (found) {
                  found.top = Math.min(found.top, seg.top);
                  found.bottom = Math.max(found.bottom, seg.bottom);
                  found.x = (found.x * found.n + seg.x) / (found.n + 1);
                  found.n += 1;
                } else {
                  clusters.push({ x: seg.x, top: seg.top, bottom: seg.bottom, n: 1 });
                }
              });
              let outer = null;
              for (let i = 0; i < clusters.length; i += 1) {
                for (let j = i + 1; j < clusters.length; j += 1) {
                  const gap = Math.abs(clusters[i].x - clusters[j].x);
                  if (gap < 46 || gap > 52) continue;
                  const mid = (clusters[i].x + clusters[j].x) / 2;
                  const score = Math.abs(gap - 48.3) + Math.abs(mid - objectWidth / 2) * 0.05;
                  if (!outer || score < outer.score) {
                    outer = { score, mid, bottom: Math.max(clusters[i].bottom, clusters[j].bottom) };
                  }
                }
              }
              if (outer) {
                let floorY = outer.bottom;
                shapes.forEach((shape) => {
                  if ((shape.type === 'line' || shape.type === 'polyline') && shape.points) {
                    for (let i = 1; i < shape.points.length; i += 2) {
                      if (shape.points[i] > floorY) floorY = shape.points[i];
                    }
                  }
                });
                const gap = floorY - outer.bottom;
                const drawn = floorClearance > 0 ? (stickOut / floorClearance) * gap : stickOut;
                const endY = Math.min(outer.bottom + drawn, floorY);
                if (endY - outer.bottom > 0.5) {
                  let frontLeg = null;
                  for (let i = 0; i < clusters.length; i += 1) {
                    for (let j = i + 1; j < clusters.length; j += 1) {
                      const gap = Math.abs(clusters[i].x - clusters[j].x);
                      if (gap < 18 || gap > 23) continue;
                      const mid = (clusters[i].x + clusters[j].x) / 2;
                      if (Math.abs(mid - outer.mid) > 8) continue;
                      const top = Math.min(clusters[i].top, clusters[j].top);
                      const bottom = Math.max(clusters[i].bottom, clusters[j].bottom);
                      if (bottom - top < 40) continue;
                      frontLeg = {
                        left: Math.min(clusters[i].x, clusters[j].x),
                        right: Math.max(clusters[i].x, clusters[j].x),
                        top,
                        bottom,
                      };
                    }
                  }
                  const outsideLeg = (x1, y1, x2, y2) => {
                    if (!frontLeg) return [[x1, y1, x2, y2]];
                    const { left, right, top, bottom } = frontLeg;
                    if (Math.abs(y1 - y2) < 0.2) {
                      const y = y1;
                      if (y <= top || y >= bottom) return [[x1, y1, x2, y2]];
                      const xLo = Math.min(x1, x2);
                      const xHi = Math.max(x1, x2);
                      const parts = [];
                      if (xLo < left - 0.2) parts.push([xLo, y, Math.min(xHi, left), y]);
                      if (xHi > right + 0.2) parts.push([Math.max(xLo, right), y, xHi, y]);
                      return parts.filter((part) => part[2] - part[0] > 0.4);
                    }
                    if (Math.abs(x1 - x2) < 0.2) {
                      const x = x1;
                      if (x <= left || x >= right) return [[x1, y1, x2, y2]];
                      const yLo = Math.min(y1, y2);
                      const yHi = Math.max(y1, y2);
                      const parts = [];
                      if (yLo < top - 0.2) parts.push([x, yLo, x, Math.min(yHi, top)]);
                      if (yHi > bottom + 0.2) parts.push([x, Math.max(yLo, bottom), x, yHi]);
                      return parts.filter((part) => Math.abs(part[3] - part[1]) > 0.4);
                    }
                    return [[x1, y1, x2, y2]];
                  };
                  const left = outer.mid - 21.2;
                  const right = outer.mid + 21.2;
                  const segments = [
                    ...outsideLeg(left, outer.bottom, left, endY),
                    ...outsideLeg(right, outer.bottom, right, endY),
                    ...outsideLeg(left, endY, right, endY),
                  ];
                  segments.forEach((points) => {
                    shapes.push({
                      type: 'line',
                      points,
                      layer: 'FASTENER',
                      scalable: true,
                      scaleOnlyX: false,
                    });
                  });
                }
              }
            }
          } else {
            // НАСТЕННЫЕ КРЕПЕЖИ: используем старую логику с межосевым расстоянием
            const baseAxialDistance = fastenerInfo?.baseAxialDistance || 300;
            const baseFastenerHeight = fastenerInfo?.baseFastenerHeight || 480;
            const baseClampDiameter = fastenerInfo?.baseClampDiameter || 88.9;
            
            // Шаг 1: СНАЧАЛА масштабируем слой CLAMP по высоте (если диаметр изменен)
            // ВАЖНО: Делаем это ПЕРВЫМ, чтобы центры зажимов были на правильных позициях
            if (fastenerConfig.clampDiameter && fastenerConfig.clampDiameter !== baseClampDiameter) {
              const scaledShapes = scaleClampLayer(shapes, fastenerConfig.clampDiameter, baseClampDiameter);
              shapes.length = 0;
              shapes.push(...scaledShapes);
            }
            
            // Шаг 2: ПОТОМ трансформируем shapes (раздвигаем по межосевому расстоянию)
            // Делаем это ВТОРЫМ, чтобы раздвигание применялось к уже масштабированным хомутам
            if (fastenerConfig.axialDistance && fastenerConfig.axialDistance !== baseAxialDistance) {
              const transformedShapes = transformFastenerShapes(
                shapes, 
                fastenerConfig.axialDistance, 
                baseAxialDistance, // базовое межосевое из fastenersData
                baseFastenerHeight  // базовая высота FASTENER слоя из fastenersData
              );
              
              shapes.length = 0;
              shapes.push(...transformedShapes);
            }
          }
          
          // Шаг 3: Пересчитываем границы после всех трансформаций
          const newBounds = calculateFastenerBounds(shapes);
          
          // Шаг 4: Нормализуем трансформированные shapes к (0,0)
          shapes.forEach((shape) => {
            if (shape.type === 'line' || shape.type === 'polyline') {
              shape.points = shape.points.map((coord, index) =>
                index % 2 === 0 ? coord - newBounds.minX : coord - newBounds.minY
              );
            } else if (shape.type === 'circle' || shape.type === 'arc') {
              shape.x -= newBounds.minX;
              shape.y -= newBounds.minY;
            }
          });
          
          objectWidth = newBounds.width;
          objectHeight = newBounds.height;
        }
  
        let initialX = x;
        let initialY = y;
  
        // Привязка к сетке удалена
  
        const config = (type === 'fitting' ? fittingsData[itemKey] : fastenersData[itemKey]) || {};
        let desiredWidthRaw = (typeof config.diameter === 'number' ? config.diameter : undefined);
        let desiredHeightRaw = (config.length !== undefined ? config.length : config.Length);
        
        // Для крепежей используем размеры из параметров
        if (type === 'fastener' && fastenerConfig) {
          // Для крепежей больших труб используем диаметр трубы как ширину
          if (fastenerConfig.pipeDiameter) {
            desiredWidthRaw = fastenerConfig.pipeDiameter;
          }
          // Иначе ширина остается фиксированной из config
          
          // Высота = реальная высота трансформированного крепежа (уже пересчитана)
          desiredHeightRaw = objectHeight;
        }
        
        const isZeroOverride = (Number(desiredWidthRaw) === 0) && (Number(desiredHeightRaw) === 0);
        const desiredWidth = isZeroOverride
          ? objectWidth
          : (typeof desiredWidthRaw === 'number' ? desiredWidthRaw : objectWidth);
        const desiredHeight = isZeroOverride
          ? objectHeight
          : (typeof desiredHeightRaw === 'number' ? desiredHeightRaw : objectHeight);

        // Для крепежей с трансформацией пересчитываем точки привязки на основе реальных координат CLAMP
        let finalSnapPoints = config ? config.snapPoints : undefined;
        if (type === 'fastener') {
          // Для напольных крепежей используем точки из конфига
          if (fastenerConfig?.isFloorFastener && config?.snapPoints) {
            // Используем точки из конфига, они уже правильно настроены
            finalSnapPoints = config.snapPoints;
          } else {
            // Для настенных крепежей рассчитываем на основе shapes
            const { calculateFastenerSnapPointsFromShapes } = require('../utils/fastenerTransform');
            
            // Вычисляем центр X (ширина / 2)
            const centerX = desiredWidth / 2;
            
            // Рассчитываем точки привязки на основе реальных координат трансформированных shapes
            finalSnapPoints = calculateFastenerSnapPointsFromShapes(shapes, centerX);
          }
        }

        const newItem = {
          id: Date.now().toString(),
          x: initialX,
          y: initialY,
          shapes,
          itemKey,
          width: desiredWidth,
          height: desiredHeight,
          originalWidth: objectWidth,
          originalHeight: objectHeight,
          isDiameterFixed: config && typeof config.isDiameterFixed === 'boolean' ? config.isDiameterFixed : (type === 'fitting' ? true : false),
          isLengthAdjustable: config ? !!config.adjustableLength : true,
          snapPoints: finalSnapPoints, // Используем трансформированные точки привязки
          type,
          locked: false,
          // Для крепежей сохраняем дополнительные параметры
          ...(type === 'fastener' && fastenerConfig ? (
            fastenerConfig.isFloorFastener 
              ? {
                  // Напольные крепежи: сохраняем высоту
                  fastenerHeight: fastenerConfig.height,
                  rangeOffset: Number(fastenerConfig.rangeOffset) || 0,
                  isFloorFastener: true,
                  pipeDiameter: fastenerConfig.pipeDiameter, // Диаметр трубы для крепежа больших труб
                  includedInPrice: config?.includedInPrice || false, // Флаг включения в стоимость
                  // Для KRGB рассчитываем вес
                  ...(itemKey === 'KRGB' ? (() => {
                    const density = 0.00000793; // кг/мм³ для AISI 304
                    const krgbHeight = fastenerConfig.height || 400;
                    const krgbDiameter = fastenerConfig.pipeDiameter || 406.4;
                    
                    // Вес 4 профилей 80×40 толщиной 2 мм
                    const profileA = 80, profileB = 40, profileT = 2;
                    const profileCrossSection = (profileA * profileB) - ((profileA - 2*profileT) * (profileB - 2*profileT));
                    const totalProfileWeight = 4 * profileCrossSection * krgbHeight * density;
                    
                    // Вес трубы круглой толщиной 3 мм, длиной 120 мм
                    const pipeD = krgbDiameter, pipeT = 3, pipeL = 120;
                    const piped = pipeD - 2*pipeT;
                    const pipeCrossSection = (Math.PI / 4) * (pipeD * pipeD - piped * piped);
                    const pipeWeight = pipeCrossSection * pipeL * density;
                    
                    return { weight: totalProfileWeight + pipeWeight };
                  })() : {}),
                }
              : {
                  // Настенные крепежи: сохраняем диаметр хомутов и межосевое расстояние
                  clampDiameter: fastenerConfig.clampDiameter,
                  axialDistance: fastenerConfig.axialDistance,
                  overhang: fastenerConfig.overhang,
                  isFloorFastener: false,
                  includedInPrice: config?.includedInPrice || false, // Флаг включения в стоимость
                }
          ) : {}),
        };

        if (type === 'fitting') {
          setFittings((prevFittings) => [...prevFittings, newItem]);
        } else if (type === 'fastener') {
          const fastenerItem = newItem;
          setFittings((prevFittings) => [...prevFittings, fastenerItem]); // Крепежи тоже хранятся как фитинги
          
          // Если это напольный крепеж и нужно добавить муфту сверху
          if (fastenerConfig?.isFloorFastener && fastenerConfig?.addTopMufta) {
            // Сохраняем данные для добавления муфты после рендера крепежа
            const muftaHeight = 22;
            const muftaDiameter = 48;
            
            // Позиция муфты: центрируем по крепежу, размещаем сверху
            const muftaX = initialX + (desiredWidth - muftaDiameter) / 2;
            const muftaY = initialY - muftaHeight;
            
            // Используем существующую функцию loadDxf для загрузки муфты
            setTimeout(() => {
              loadDxf('Mufta_1_1.4', muftaX, muftaY, 'fitting', null).then(() => {
                // После загрузки муфты создаем привязку
                setTimeout(() => {
                  // Находим только что добавленную муфту (последний fitting)
                  const allFittings = fittingsRef.current;
                  const muftaFitting = allFittings[allFittings.length - 1];
                  
                  if (muftaFitting && muftaFitting.itemKey === 'Mufta_1_1.4') {
                    // Принудительно устанавливаем высоту муфты на 22 мм и запрещаем создание IGES-отверстий
                    setFittings((prevFittings) => {
                      const updated = [...prevFittings];
                      const muftaIndex = updated.length - 1;
                      updated[muftaIndex] = {
                        ...updated[muftaIndex],
                        height: muftaHeight,
                        isLengthAdjustable: false,
                        igesCreateHole: false, // Муфта крепежа не должна создавать отверстия в трубах
                      };
                      fittingsRef.current = updated;
                      return updated;
                    });
                    
                    // Создаем привязку между муфтой и крепежом
                    // Point-to-Point: центральная точка крепежа -> нижняя центральная точка муфты
                    const newConstraint = {
                      type: 'point',
                      obj1: {
                        objectId: fastenerItem.id,
                        objectType: 'fitting',
                        pointIndex: 0, // Единственная кастомная точка крепежа (центр)
                      },
                      obj2: {
                        objectId: muftaFitting.id,
                        objectType: 'fitting',
                        pointIndex: 5, // bottom-middle муфты (стандартная точка)
                      },
                    };
                    
                    setObjectConstraints((prevConstraints) => {
                      const updated = [...prevConstraints, newConstraint];
                      objectConstraintsRef.current = updated;
                      return updated;
                    });
                    
                    // Применяем привязку сразу после создания
                    setTimeout(() => {
                      updateConstrainedObjects(fastenerItem.id, 'fitting', new Set());
                      saveState();
                    }, 50);
                  }
                }, 100);
              }).catch(error => {
                console.error('Ошибка при добавлении муфты к крепежу:', error);
              });
            }, 100);
          }
        }
  
        // Сохраняем состояние после добавления объекта
        saveState();
      } catch (error) {
        console.error('Ошибка при загрузке DXF:', error);
      }
  },
  [setFittings, saveState, updateConstrainedObjects] 
  );   

  // Обработка события добавления фитинга
  useEffect(() => {
    if (onAddFittingTrigger) {
      // Начинаем добавление фитинга по центру холста или по координатам курсора
      const initialX = canvasWidth / 2;
      const initialY = canvasHeight / 2;
  
      // loadDxf сам создаст фитинг с правильными originalWidth/originalHeight из DXF
      loadDxf(onAddFittingTrigger, initialX, initialY);
  
      resetAddFittingTrigger();
    }
  }, [onAddFittingTrigger, resetAddFittingTrigger, loadDxf, canvasWidth, canvasHeight]);

  const getSideFromPointIndex = useCallback((idx) => {
    switch (idx) {
      case 4:
        return 'top';
      case 5:
        return 'bottom';
      case 6:
        return 'left';
      case 7:
        return 'right';
      default:
        return null;
    }
  }, []);
  
  // Вспомогательные функции для определения ортогональности (0/90) поворота объектов

  const isAngleOrthogonal = useCallback((deg, tolerance = 0.5) => {
    const mod = ((deg % 90) + 90) % 90;
    return mod <= tolerance || 90 - mod <= tolerance;
  }, []);

  const areElementsOrthogonalObjects = useCallback((el1, el2) => {
    const id1 = el1?.objectId;
    const id2 = el2?.objectId;
    if (!id1 || !id2) return false;
    const r1 = getObjectAbsRotationDegById(id1);
    const r2 = getObjectAbsRotationDegById(id2);
    return isAngleOrthogonal(r1) && isAngleOrthogonal(r2);
  }, [getObjectAbsRotationDegById, isAngleOrthogonal]);
  
  // Определение ориентации размерных линий
  const determineDimensionOrientation = useCallback((elem1, elem2) => {
    const isSide = (el) => !!el?.type;
    const isMidpoint = (el) => typeof el?.idx === 'number';
    const sideNameOf = (el) => (isSide(el) ? el.type : isMidpoint(el) ? getSideFromPointIndex(el.idx) : null);

    // Помощник: ориентация стороны в мировых координатах, если есть points [x1,y1,x2,y2]
    const getWorldSideOrientation = (el) => {
      if (el && Array.isArray(el.points) && el.points.length === 4) {
        const [x1, y1, x2, y2] = el.points;
        const dx = Math.abs(x2 - x1);
        const dy = Math.abs(y2 - y1);
        return dy >= dx ? 'vertical' : 'horizontal';
      }
      const s = sideNameOf(el);
      if (s === 'left' || s === 'right') return 'vertical';
      if (s === 'top' || s === 'bottom') return 'horizontal';
      return null;
    };

    // A) Оба — стороны: ориентируем перпендикулярно их мировой ориентации
    if (elem1?.type && elem2?.type) {
      const o1 = getWorldSideOrientation(elem1);
      const o2 = getWorldSideOrientation(elem2);
      if (o1 && o2) {
        if (o1 === 'vertical' && o2 === 'vertical') return 'horizontal';
        if (o1 === 'horizontal' && o2 === 'horizontal') return 'vertical';
      }
      // если ориентации разные — пойдём дальше (см. координатное правило)
    }

    // B) Смешанно: сторона + средняя точка → перпендикуляр к мировой ориентации стороны
    if ((elem1?.type && isMidpoint(elem2)) || (elem2?.type && isMidpoint(elem1))) {
      const sideEl = elem1?.type ? elem1 : elem2;
      const o = getWorldSideOrientation(sideEl);
      if (o === 'vertical') return 'horizontal';
      if (o === 'horizontal') return 'vertical';
    }

    // B2) Оба — середины разных объектов: используем мировую ориентацию сторон через поворот объекта
    const differentObjects = !!(elem1?.objectId && elem2?.objectId && elem1.objectId !== elem2.objectId);
    const getWorldOrientationForMidpoint = (el) => {
      if (!isMidpoint(el)) return null;
      const localSide = sideNameOf(el);
      if (!localSide) return null;
      const localIsVertical = localSide === 'left' || localSide === 'right';
      const id = el.objectId;
      if (!id || typeof getObjectAbsRotationDegById !== 'function') return localIsVertical ? 'vertical' : 'horizontal';
      let deg = getObjectAbsRotationDegById(id);
      deg = ((deg % 180) + 180) % 180; // 0..180
      const near90 = Math.abs(deg - 90) <= 0.5;
      const worldIsVertical = near90 ? !localIsVertical : localIsVertical;
      return worldIsVertical ? 'vertical' : 'horizontal';
    };
    if (differentObjects && isMidpoint(elem1) && isMidpoint(elem2)) {
      const o1 = getWorldOrientationForMidpoint(elem1);
      const o2 = getWorldOrientationForMidpoint(elem2);
      if (o1 && o2) {
        if (o1 === 'vertical' && o2 === 'vertical') return 'vertical';
        if (o1 === 'horizontal' && o2 === 'horizontal') return 'horizontal';
      }
    }

    // C) Оба имеют мировые координаты — ориентируемся по ним (учитывает повороты)
    if (
      typeof elem1?.x === 'number' &&
      typeof elem1?.y === 'number' &&
      typeof elem2?.x === 'number' &&
      typeof elem2?.y === 'number'
    ) {
      return Math.abs(elem2.x - elem1.x) >= Math.abs(elem2.y - elem1.y)
        ? 'horizontal'
        : 'vertical';
    }

    // D) Оба — средние точки одной и той же стороны (fallback при отсутствии координат)
    if (typeof elem1?.idx === 'number' && typeof elem2?.idx === 'number') {
      const side1 = getSideFromPointIndex(elem1.idx);
      const side2 = getSideFromPointIndex(elem2.idx);
      if (side1 && side1 === side2) {
        if (side1 === 'left' || side1 === 'right') return 'horizontal';
        if (side1 === 'top' || side1 === 'bottom') return 'vertical';
      }
    }

    // E) Один — середина, другой — сторона (fallback при отсутствии координат)
    if (typeof elem1?.idx === 'number' && elem2?.type) {
      const side = elem2.type;
      if (side === 'left' || side === 'right') return 'horizontal';
      if (side === 'top' || side === 'bottom') return 'vertical';
    } else if (elem1?.type && typeof elem2?.idx === 'number') {
      const side = elem1.type;
      if (side === 'left' || side === 'right') return 'horizontal';
      if (side === 'top' || side === 'bottom') return 'vertical';
    }

    // F) Последняя попытка: если есть координаты — по ним, иначе горизонталь
    if (
      typeof elem1?.x === 'number' &&
      typeof elem1?.y === 'number' &&
      typeof elem2?.x === 'number' &&
      typeof elem2?.y === 'number'
    ) {
      return Math.abs(elem2.x - elem1.x) >= Math.abs(elem2.y - elem1.y)
        ? 'horizontal'
        : 'vertical';
    }
    return 'horizontal';
  }, [getSideFromPointIndex, getObjectAbsRotationDegById]);

  const updateAllDimensionLines = useCallback(() => {
    setDimensionLines((prevDimensionLines) =>
      prevDimensionLines.map((dim) => {
        const { elements } = dim;
        
        // Проверяем, что elements существует и содержит минимум 2 элемента
        if (!elements || !Array.isArray(elements) || elements.length < 2) {
          // Если нет привязки к объектам, просто возвращаем размер как есть
          return dim;
        }
        
        const [elem1, elem2] = elements;
        
        // Проверяем, что оба элемента определены
        if (!elem1 || !elem2) {
          return dim;
        }
  
        // Получаем актуальные координаты для элементов
        const getUpdatedCoords = (elem) => {
          if (!elem || !elem.objectType) return null;
          if (elem.objectType === 'view') {
            const view = projectedViewsRef.current.find((item) => item.id === elem.objectId);
            if (!view || !elem.local) return null;
            return { x: view.x + elem.local.x, y: view.y + elem.local.y };
          }
          const z = effectiveZoom || 1;
          let obj;
          if (elem.objectType === 'cylinder') {
            obj = cylindersRef.current.find((c) => c.id === elem.objectId);
          } else if (elem.objectType === 'fitting') {
            obj = fittingsRef.current.find((f) => f.id === elem.objectId);
          } else {
            return null;
          }
          if (!obj) return null;

          const width = obj.width || getFittingWidth(obj);
          const height = obj.height || getFittingHeight(obj);
          const x = obj.x || 0;
          const y = obj.y || 0;
          
          // Для объектов с groupX/groupY и rotation - вычисляем координаты вручную
          const hasGroupTransform = obj.groupX !== undefined || obj.groupY !== undefined || obj.rotation;
          
          // Функция для ручной трансформации точки с учётом rotation, offset, groupX/groupY
          const manualTransformPoint = (localP) => {
            const gx = obj.groupX || 0;
            const gy = obj.groupY || 0;
            const rot = (obj.rotation || 0) * Math.PI / 180;
            const ox = obj.offsetX || 0;
            const oy = obj.offsetY || 0;
            
            // 1. Смещаем относительно offset (точки вращения)
            const px = localP.x - ox;
            const py = localP.y - oy;
            
            // 2. Применяем поворот
            const cos = Math.cos(rot);
            const sin = Math.sin(rot);
            const rx = px * cos - py * sin;
            const ry = px * sin + py * cos;
            
            // 3. Добавляем позицию группы
            return { x: gx + rx, y: gy + ry };
          };

          // Получаем абсолютную трансформацию группы объекта
          const stage = stageRef.current;
          let transformPoint;
          
          if (stage && !hasGroupTransform) {
            let node = stage.findOne(`#${obj.id}`);
            if (node) {
              const group = node.getClassName && node.getClassName() === 'Group' ? node : node.getParent();
              if (group) {
                const t = group.getAbsoluteTransform();
                transformPoint = (p) => {
                  const abs = t.point({ x: p.x, y: p.y });
                  return { x: abs.x / z, y: abs.y / z };
                };
              }
            }
          }
          
          // Если не удалось получить трансформацию через stage или есть groupX/groupY - используем ручной расчёт
          if (!transformPoint) {
            if (hasGroupTransform) {
              transformPoint = manualTransformPoint;
            } else {
              // Fallback: просто возвращаем локальные координаты
              transformPoint = (p) => p;
            }
          }

          if (elem.type) {
            // Это сторона — берём центр соответствующей стороны и трансформируем
            let localPoint;
            switch (elem.type) {
              case 'top':
                localPoint = { x: x + width / 2, y: y };
                break;
              case 'bottom':
                localPoint = { x: x + width / 2, y: y + height };
                break;
              case 'left':
                localPoint = { x: x, y: y + height / 2 };
                break;
              case 'right':
                localPoint = { x: x + width, y: y + height / 2 };
                break;
              default:
                return null;
            }
            return transformPoint(localPoint);
          } else if (typeof elem.idx === 'number') {
            // Это точка — берём локальные точки (с учётом snapPoints) и трансформируем нужную
            let localPoints;
            if (obj.snapPoints && Array.isArray(obj.snapPoints)) {
              // Используем кастомные snapPoints
              localPoints = obj.snapPoints.map(point => {
                if (typeof point === 'function') {
                  return point(x, y, width, height);
                } else if (point.absolute) {
                  return { x: x + (point.x || 0), y: y + (point.y || 0) };
                } else if (point.relative) {
                  return { x: x + width * point.x, y: y + height * point.y };
                } else {
                  return { x: x + (point.x || 0), y: y + (point.y || 0) };
                }
              });
            } else {
              // Стандартные точки
              localPoints = getCylinderPoints({ x, y, width, height });
            }
            const p = localPoints[elem.idx];
            return transformPoint(p);
          }
          return null;
        };        
  
        const coord1 = getUpdatedCoords(elem1);
        const coord2 = getUpdatedCoords(elem2);
  
        if (coord1 && coord2) {
          // Определяем актуальную ориентацию по выбранным элементам
          // Если установлен флаг lockOrientation, сохраняем заданную ориентацию
          let isHorizontalNow;
          if (dim.lockOrientation) {
            isHorizontalNow = dim.isHorizontal;
          } else {
            const orientationNow = determineDimensionOrientation(elem1, elem2);
            isHorizontalNow = orientationNow === 'horizontal';
          }
          const bothObjectsOrth = areElementsOrthogonalObjects(elem1, elem2);
          
          // Для одного объекта проверяем его угол поворота
          const sameObject = elem1.objectId && elem2.objectId && elem1.objectId === elem2.objectId;
          let singleObjectOrth = false;
          if (sameObject) {
            const objRotation = getObjectAbsRotationDegById(elem1.objectId);
            singleObjectOrth = isAngleOrthogonal(objRotation);
          }

          // Поддержка угловых размерных линий (0/45/90/135)
          const angleDegBase = typeof dim.angle === 'number' ? dim.angle : (isHorizontalNow ? 0 : 90);
          let angleDeg = ((angleDegBase % 180) + 180) % 180; // нормализуем 0..179
          // Только если объекты ортогональные ИЛИ это один ортогональный объект - сбрасываем на 0/90
          if (bothObjectsOrth || singleObjectOrth) {
            angleDeg = isHorizontalNow ? 0 : 90;
          }
          const angleRad = (angleDeg * Math.PI) / 180;
          const ux = Math.cos(angleRad);
          const uy = Math.sin(angleRad);
          const nx = -uy;
          const ny = ux;

          const dxw = coord2.x - coord1.x;
          const dyw = coord2.y - coord1.y;
          const projectedLength = Math.abs(dxw * ux + dyw * uy);
          const newValue = dimensionMillimeters(projectedLength);

          const mid = { x: (coord1.x + coord2.x) / 2, y: (coord1.y + coord2.y) / 2 };

          // Обновляем числовые значения размерной линии (для живого обновления текста)
          dim.rawValue = projectedLength;
          dim.value = newValue;

          // Пересчитываем позицию с учётом смещений вдоль/поперёк
          let position;
          if (angleDeg % 90 === 0) {
            // Горизонталь/вертикаль — поддерживаем прежнюю семантику position
            // Размер, привязанный к виду, ездит вместе с видом по обеим осям:
            // линия держит прежний отступ от точки, которая лежит на виде.
            const viewSide = elem1.objectType === 'view' ? 1 : (elem2.objectType === 'view' ? 2 : 0);
            if (viewSide !== 0 && typeof dim.x1 === 'number' && typeof dim.y1 === 'number'
              && typeof dim.x2 === 'number' && typeof dim.y2 === 'number') {
              const oldPoint = viewSide === 1 ? { x: dim.x1, y: dim.y1 } : { x: dim.x2, y: dim.y2 };
              const newPoint = viewSide === 1 ? coord1 : coord2;
              if (isHorizontalNow) {
                position = { x: 0, y: newPoint.y + (dim.position.y - oldPoint.y) };
              } else {
                position = { x: newPoint.x + (dim.position.x - oldPoint.x), y: 0 };
              }
            } else if (isHorizontalNow) {
              const reference = Math.min(coord1.y, coord2.y);
              const offset = dim.position.y - reference;
              position = { x: 0, y: reference + offset };
            } else {
              const reference = Math.max(coord1.x, coord2.x);
              const offset = dim.position.x - reference;
              position = { x: reference + offset, y: 0 };
            }
          } else {
            // Угловые размеры: сохраняем два параметра смещения — вдоль оси (offsetAlong) и перпендикулярно (offsetDistance)
            let offsetAlong = typeof dim.offsetAlong === 'number' ? dim.offsetAlong : undefined;
            let offsetDistance = typeof dim.offsetDistance === 'number' ? dim.offsetDistance : undefined;

            if (typeof offsetAlong !== 'number' || typeof offsetDistance !== 'number') {
              if (dim.position && typeof dim.position.x === 'number' && typeof dim.position.y === 'number') {
                const vx = dim.position.x - mid.x;
                const vy = dim.position.y - mid.y;
                offsetAlong = vx * ux + vy * uy;
                offsetDistance = vx * nx + vy * ny;
              } else {
                offsetAlong = 0;
                offsetDistance = -50; // дефолтный перпендикулярный отступ
              }
            }

            position = {
              x: mid.x + ux * offsetAlong + nx * offsetDistance,
              y: mid.y + uy * offsetAlong + ny * offsetDistance,
            };
            // Сохраняем параметры в размере
            dim = { ...dim, offsetAlong, offsetDistance };
          }

          // Обновляем `textPosition`, если необходимо
          const textPosition = dim.textPosition || { x: 0, y: 0 };
  
          // Пересчитываем выносные линии
          const extensionLines = []; // Начинаем с пустого массива
  
          // Вычисляем позиции текста и пределы
          if (angleDeg % 180 === 0) {
            const textX =
              (coord1.x + coord2.x) / 2 -22 - position.x + (textPosition.x || 0);
            const textWidth = getTextWidth(newValue); // ширина текста для актуального значения
            const leftLimit = Math.min(coord1.x, coord2.x) - position.x;
            const rightLimit = Math.max(coord1.x, coord2.x) - position.x;
  
            const textLeftEdge = textX ;
            const textRightEdge = textX + textWidth ;
  
            if (textLeftEdge < leftLimit) {
              extensionLines.push({
                x1: coord1.x - position.x,
                y1: 0,
                x2: textLeftEdge,
                y2: 0,
              });
            }
  
            if (textX + textWidth / 2 + 100 > rightLimit) {
              extensionLines.push({
                x1: coord2.x - position.x,
                y1: 0,
                x2: textRightEdge,
                y2: 0,
              });
            }
          } else if (angleDeg % 90 === 0) {
            const textY =
              (coord1.y + coord2.y) / 2 +22 - position.y + (textPosition.y || 0);
            const textHeight = getTextHeight(newValue);
            const topLimit = Math.min(coord1.y, coord2.y) - position.y;
            const bottomLimit = Math.max(coord1.y, coord2.y) - position.y;
  
            if (textY + textHeight /2 - 100 < topLimit) {
              extensionLines.push({
                x1: 0,
                y1: coord1.y - position.y,
                x2: 0,
                y2: textY - textHeight * 3,
              });
            }
  
            if (textY + textHeight > bottomLimit) {
              extensionLines.push({
                x1: 0,
                y1: coord2.y - position.y,
                x2: 0,
                y2: textY + textHeight - 15,
              });
            }
          } else {
            // Для угловых 45°/135° пока не строим выносные линии
          }
  
          // Обновляем overrideText для размерных линий диаметра трубы (если толщина изменилась)
          let updatedOverrideEnabled = dim.overrideEnabled;
          let updatedOverrideText = dim.overrideText;
          
          // Проверяем, является ли это размерной линией диаметра трубы
          // (привязана к одному цилиндру с двух противоположных сторон)
          if (elem1.objectType === 'cylinder' && elem2.objectType === 'cylinder' && 
              elem1.objectId === elem2.objectId && dim.overrideEnabled) {
            const cylinder = cylindersRef.current.find((c) => c.id === elem1.objectId);
            if (cylinder && cylinder.pipeSpec) {
              const pipeSpec = cylinder.pipeSpec;
              const shape = pipeSpec.shape || 'round';
              
              // Для квадратных труб обновляем профиль и толщину
              if (shape === 'square' && pipeSpec.profile) {
                const profileThickness = pipeSpec.thickness || '2';
                updatedOverrideText = `${pipeSpec.profile.replace('x', '×')} ×${profileThickness}`;
              } 
              // Для круглых труб обновляем диаметр и толщину
              else if (shape === 'round' && pipeSpec.diameter) {
                const pipeThickness = typeof pipeSpec.thickness === 'string'
                  ? pipeSpec.thickness.replace('x', '')
                  : pipeSpec.thickness || 2;
                updatedOverrideText = `${pipeSpec.diameter} ×${pipeThickness}`;
              }
            }
          }

          return {
            ...dim,
            isHorizontal: isHorizontalNow,
            angle: angleDeg,
            x1: coord1.x,
            y1: coord1.y,
            x2: coord2.x,
            y2: coord2.y,
            value: newValue,
            position,
            extensionLines, // Обновляем выносные линии
            // Обновляем overrideEnabled и overrideText (с учётом изменения толщины)
            overrideEnabled: updatedOverrideEnabled,
            overrideText: updatedOverrideText,
            lowerOverrideEnabled: dim.lowerOverrideEnabled,
            lowerOverrideText: dim.lowerOverrideText,
          };
        }
        return dim;
      })
    );
  }, [effectiveZoom, areElementsOrthogonalObjects, determineDimensionOrientation, getObjectAbsRotationDegById, isAngleOrthogonal]);  // Убрали cylinders и fittings - используем refs 

  useEffect(() => {
    updateAllDimensionLinesRef.current = updateAllDimensionLines;
  }, [updateAllDimensionLines]);

  const undo = useCallback(() => {
    if (undoStack.length > 0) {
      const previousState = undoStack[undoStack.length - 1];
      setRedoStack((prevStack) => [
        ...prevStack,
        {
          lines,
          cylinders,
          annotations,
          balloons,
          dimensionLines,
          fittings,
          patterns,
        },
      ]);
      setLines(previousState.lines);
      setCylinders(previousState.cylinders);
      setAnnotations(previousState.annotations);
      setBalloons(previousState.balloons || []);
      setDimensionLines(previousState.dimensionLines);
      setFittings(previousState.fittings);
      setPatterns(previousState.patterns || []);
      setUndoStack((prevStack) => prevStack.slice(0, prevStack.length - 1));
    }
  }, [undoStack, lines, cylinders, annotations, balloons, dimensionLines, fittings, patterns]);
  
  const redo = useCallback(() => {
    if (redoStack.length > 0) {
      const nextState = redoStack[redoStack.length - 1];
      setUndoStack((prevStack) => [
        ...prevStack,
        {
          lines,
          cylinders,
          annotations,
          balloons,
          dimensionLines,
          fittings,
          patterns,
        },
      ]);
      setLines(nextState.lines);
      setCylinders(nextState.cylinders);
      setAnnotations(nextState.annotations);
      setBalloons(nextState.balloons || []);
      setDimensionLines(nextState.dimensionLines);
      setFittings(nextState.fittings);
      setPatterns(nextState.patterns || []);
      setRedoStack((prevStack) => prevStack.slice(0, prevStack.length - 1));
    }
  }, [redoStack, lines, cylinders, annotations, balloons, dimensionLines, fittings, patterns]); 
  
  const handleMouseDown = (e) => {
    // Средняя кнопка мыши (колёсико) — начало панорамирования
    if (e.evt && e.evt.button === 1) {
      e.evt.preventDefault();
      const stage = e.target.getStage();
      const pos = stage.getPointerPosition();
      if (pos) {
        const z = effectiveZoom || 1;
        middlePanLastRef.current = { x: pos.x / z, y: pos.y / z };
        setIsMiddlePanning(true);
        try { stage.container().style.cursor = 'grabbing'; } catch (_) {}
        saveState();
      }
      return; // Не обрабатываем другие действия во время MMB-нажатия
    }
    if (isLKeyPressed) {
      const clickedOn = e.target;
  
      if (clickedOn.getClassName() === 'Rect') {
        const id = clickedOn.id();
  
        // Проверяем, является ли объект цилиндром
        const isCylinder = cylinders.some((cylinder) => cylinder.id === id);
        if (isCylinder) {
          setCylinders((prevCylinders) =>
            prevCylinders.map((cylinder) =>
              cylinder.id === id ? { ...cylinder, locked: !cylinder.locked } : cylinder
            )
          );
        }
  
        // Проверяем, является ли объект фитингом
        const isFitting = fittings.some((fitting) => fitting.id === id);
        if (isFitting) {
          setFittings((prevFittings) =>
            prevFittings.map((fitting) =>
              fitting.id === id ? { ...fitting, locked: !fitting.locked } : fitting
            )
          );
        }
  
        // Удалить проверку на трубы (isPipe)
      }
      return; // Прерываем дальнейшую обработку
    }

    // Ctrl+клик по объекту: режимы
    // 1) Если активен режим «Позиция» или есть выбранная выноска — привязать к выноске
    // 2) Иначе — мультивыбор объектов для линейного массива
    if (isCtrlPressed && (isBalloonModeEnabled || selectedBalloonId)) {
      // Клик по виду: позиция привязывается к точке контура вида (или к месту клика на виде)
      let viewHit = null;
      let walker = e.target;
      while (walker && typeof walker.getClassName === 'function' && walker.getClassName() !== 'Stage') {
        const walkerId = typeof walker.id === 'function' ? walker.id() : '';
        if (walkerId) {
          const found = projectedViews.find((v) => v.id === walkerId);
          if (found) {
            viewHit = found;
            break;
          }
        }
        walker = walker.getParent();
      }
      if (viewHit) {
        const stageForView = e.target.getStage();
        const pointer = stageForView && stageForView.getPointerPosition();
        const balloonForView = isBalloonModeEnabled
          ? (selectedBalloonId || (balloons.length > 0 ? balloons[balloons.length - 1].id : null))
          : selectedBalloonId;
        if (pointer && balloonForView) {
          const zoomView = effectiveZoom || 1;
          const contentY = 28;
          let anchorX = pointer.x / zoomView - viewHit.x;
          let anchorY = pointer.y / zoomView - viewHit.y;
          // Прилипание к ближайшей линии контура, если она рядом
          let bestDist = 8;
          const polys = (viewHit.scene && viewHit.scene.polylines) || [];
          polys.forEach((poly) => {
            for (let i = 0; i + 3 < poly.length; i += 2) {
              const x1 = poly[i];
              const y1 = poly[i + 1] + contentY;
              const x2 = poly[i + 2];
              const y2 = poly[i + 3] + contentY;
              const dx = x2 - x1;
              const dy = y2 - y1;
              const lenSq = dx * dx + dy * dy;
              const param = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((anchorX - x1) * dx + (anchorY - y1) * dy) / lenSq));
              const px = x1 + param * dx;
              const py = y1 + param * dy;
              const dist = Math.hypot(anchorX - px, anchorY - py);
              if (dist < bestDist) {
                bestDist = dist;
                anchorX = px;
                anchorY = py;
              }
            }
          });
          saveState();
          setBalloons((prev) =>
            prev.map((b) => (b.id === balloonForView
              ? { ...b, targets: [...b.targets, { id: viewHit.id, type: 'view', anchorLocal: { x: anchorX, y: anchorY } }] }
              : b))
          );
        }
        return;
      }
      const clickedOn = e.target;
      // Поднимаемся до ближайшей группы, чтобы получить id объекта
      let nodeForId = clickedOn;
      while (nodeForId && typeof nodeForId.getClassName === 'function' && nodeForId.getClassName() !== 'Group') {
        nodeForId = nodeForId.getParent();
      }
      const targetId = nodeForId && typeof nodeForId.id === 'function' ? nodeForId.id() : (typeof clickedOn.id === 'function' ? clickedOn.id() : null);
      if (targetId) {
        const isCylinder = cylinders.some((c) => c.id === targetId);
        const isFitting = fittings.some((f) => f.id === targetId);
        if (isCylinder || isFitting) {
          const balloonId = isBalloonModeEnabled
            ? (selectedBalloonId || (balloons.length > 0 ? balloons[balloons.length - 1].id : null))
            : selectedBalloonId;
          if (balloonId) {
            // Конвертируем позицию клика в локальные координаты группы объекта (учёт поворота/масштаба)
            const stage = clickedOn.getStage();
            const pos = stage?.getPointerPosition();
            let pointerLocal = null;
            try {
              const stageNode = stageRef.current;
              let node = stageNode ? stageNode.findOne('#' + targetId) : null;
              const group = (node && node.getClassName && node.getClassName() === 'Group') ? node : (node ? node.getParent() : nodeForId);
              const absT = group && group.getAbsoluteTransform ? group.getAbsoluteTransform() : null;
              if (pos && absT) {
                const inv = absT.copy().invert();
                // pos.x/y в координатах Stage; инвертируем к локальным координатам группы
                const pLocal = inv.point({ x: pos.x, y: pos.y });
                pointerLocal = { x: pLocal.x, y: pLocal.y };
              }
            } catch (_) {}

            saveState();
            setBalloons((prev) =>
              prev.map((b) => {
                if (b.id !== balloonId) return b;
                const exists = b.targets.some((t) => t.id === targetId);
                if (exists) return b;

                // Для цилиндров: якорь в точке клика
                if (isCylinder) {
                  let anchorLocal = null;
                  let anchorNormalized = null;
                  if (pointerLocal) {
                    const obj = cylinders.find((c) => c.id === targetId);
                    if (obj && obj.width && obj.height) {
                      anchorLocal = { x: pointerLocal.x - obj.x, y: pointerLocal.y - obj.y };
                      anchorNormalized = {
                        x: Math.max(0, Math.min(1, (pointerLocal.x - obj.x) / obj.width)),
                        y: Math.max(0, Math.min(1, (pointerLocal.y - obj.y) / obj.height)),
                      };
                    }
                  }
                  return {
                    ...b,
                    targets: [
                      ...b.targets,
                      { id: targetId, type: 'cylinder', anchorLocal, anchorNormalized },
                    ],
                  };
                }

                // Для фитингов: якорь в точке клика + режим стороны (top/bottom/local)
                let anchorNormalized = null;
                let anchorLocal = null;
                let anchorMode = 'local';
                if (pointerLocal) {
                  const obj = fittings.find((f) => f.id === targetId);
                  if (obj && obj.width && obj.height) {
                    anchorLocal = { x: pointerLocal.x - obj.x, y: pointerLocal.y - obj.y };
                    anchorNormalized = {
                      x: Math.max(0, Math.min(1, (pointerLocal.x - obj.x) / obj.width)),
                      y: Math.max(0, Math.min(1, (pointerLocal.y - obj.y) / obj.height)),
                    };
                    const dyTop = Math.abs(pointerLocal.y - obj.y);
                    const dyBottom = Math.abs(pointerLocal.y - (obj.y + obj.height));
                    if (dyTop <= 10) anchorMode = 'top';
                    else if (dyBottom <= 10) anchorMode = 'bottom';
                  }
                }

                return {
                  ...b,
                  targets: [
                    ...b.targets,
                    { id: targetId, type: 'fitting', anchorNormalized, anchorLocal, anchorMode },
                  ],
                };
              })
            );
          }
        }
      }
      return;
    }

    if (isCtrlPressed && !isBalloonModeEnabled && !selectedBalloonId) {
      const clickedOn = e.target;
      // Поднимаемся до ближайшей группы, чтобы получить id объекта
      let nodeForId = clickedOn;
      while (nodeForId && typeof nodeForId.getClassName === 'function' && nodeForId.getClassName() !== 'Group') {
        nodeForId = nodeForId.getParent();
      }
      const targetId = nodeForId && typeof nodeForId.id === 'function' ? nodeForId.id() : (typeof clickedOn.id === 'function' ? clickedOn.id() : null);
      if (targetId) {
        const isCylinder = cylinders.some((c) => c.id === targetId);
        const isFitting = fittings.some((f) => f.id === targetId);
        if (isCylinder || isFitting) {
          const key = (isCylinder ? 'cylinder' : 'fitting') + ':' + targetId;
          setSelectedPatternObjects((prev) => {
            let next = [...prev];
            // Если это первая Ctrl-выборка и уже есть одиночный selectedId — добавим его в мультивыбор
            if (next.length === 0 && selectedId && selectedId !== targetId) {
              const selIsCyl = cylinders.some((c) => c.id === selectedId);
              const selIsFit = fittings.some((f) => f.id === selectedId);
              if (selIsCyl || selIsFit) {
                next.push({ type: selIsCyl ? 'cylinder' : 'fitting', id: selectedId });
              }
            }
            const exists = next.some((o) => (o.type + ':' + o.id) === key);
            if (exists) {
              next = next.filter((o) => (o.type + ':' + o.id) !== key);
            } else {
              next.push({ type: isCylinder ? 'cylinder' : 'fitting', id: targetId });
            }
            return next;
          });
        }
      }
      return;
    }
    if (isDimensionLineEnabled && !isObjectDraggingRef.current) {
      if (highlightedSide || highlightedPoint) {
        if (selectedElements.length < 1) {
          setSelectedElements([highlightedSide || highlightedPoint]);
        } else if (selectedElements.length === 1) {
          const elem1 = selectedElements[0];
          const elem2 = highlightedSide || highlightedPoint;

          // Координаты выбранных элементов (в мировых координатах сцены)
          const x1 = elem1.x;
          const y1 = elem1.y;
          const x2 = elem2.x;
          const y2 = elem2.y;

          // Определяем ориентацию размерной линии
          const orientation = determineDimensionOrientation(elem1, elem2);
          const isHorizontal = orientation === 'horizontal';

          // Вычисляем позицию для размещения размерной линии
          let position;
          const offset = 50; // Вы можете настроить это значение по своему усмотрению

          if (isHorizontal) {
            // Для горизонтальных размерных линий позиция по оси Y будет выше или ниже цилиндров
            const y = Math.min(y1, y2) - offset;
            position = { x: 0, y };
          } else {
            // Для вертикальных размерных линий позиция по оси X будет левее или правее цилиндров
            const x = Math.max(x1, x2) + offset;
            position = { x, y: 0 };
          }

          // Создаем новую размерную линию
          // Определим, нужно ли принудительно ставить ортогональный (0/90) угол
          const bothObjectsOrth = areElementsOrthogonalObjects(elem1, elem2);
          
          // Для одного объекта (фитинга/трубы) проверяем его угол поворота
          const sameObject = elem1.objectId && elem2.objectId && elem1.objectId === elem2.objectId;
          let singleObjectOrth = false;
          if (sameObject) {
            const objRotation = getObjectAbsRotationDegById(elem1.objectId);
            singleObjectOrth = isAngleOrthogonal(objRotation);
          }

          // Вычислим угол: если нужно — фиксируем 0/90, иначе снапим к ближайшим 0/45/90/135
          let effectiveAngle;
          // Только если объекты ортогональные ИЛИ это один ортогональный объект - используем 0/90
          if (bothObjectsOrth || singleObjectOrth) {
            effectiveAngle = isHorizontal ? 0 : 90;
          } else {
            // Для повернутых объектов (включая 45°) разрешаем диагональные размеры
            const rawAngleDeg = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
            const normAngle = ((rawAngleDeg % 180) + 180) % 180; // 0..179
            const snappedAngle = Math.round(normAngle / 45) * 45; // кратно 45°
            effectiveAngle = snappedAngle;
          }

          // Для угловых размеров сразу задаём позицию на нормали от середины и инициализируем смещения
          let offsetAlongInit;
          let offsetDistanceInit;
          if (effectiveAngle % 90 !== 0) {
            const radInit = (effectiveAngle * Math.PI) / 180;
            const uxInit = Math.cos(radInit);
            const uyInit = Math.sin(radInit);
            const nxInit = -uyInit;
            const nyInit = uxInit;
            const midXInit = (x1 + x2) / 2;
            const midYInit = (y1 + y2) / 2;
            const defaultOffset = -50; // небольшой вынос перпендикулярно
            position = {
              x: midXInit + nxInit * defaultOffset,
              y: midYInit + nyInit * defaultOffset,
            };
            offsetAlongInit = 0;
            offsetDistanceInit = defaultOffset;
          }

          let value;
          let rawValue;
          if (effectiveAngle % 180 === 0) {
            rawValue = Math.abs(x2 - x1);
            value = dimensionMillimeters(rawValue);
          } else if (effectiveAngle % 90 === 0) {
            rawValue = Math.abs(y2 - y1);
            value = dimensionMillimeters(rawValue);
          } else {
            const rad = (effectiveAngle * Math.PI) / 180;
            const ux = Math.cos(rad);
            const uy = Math.sin(rad);
            rawValue = Math.abs((x2 - x1) * ux + (y2 - y1) * uy);
            value = dimensionMillimeters(rawValue);
          }

          // Рассчитаем offsetDistance от средней точки вдоль нормали, чтобы потом стабильно пересчитывать position
          const midX = (x1 + x2) / 2;
          const midY = (y1 + y2) / 2;
          const rad0 = (effectiveAngle * Math.PI) / 180;
          const ux0 = Math.cos(rad0);
          const uy0 = Math.sin(rad0);
          const nx0 = -uy0;
          const ny0 = ux0;
          const offsetDistance =
            typeof offsetDistanceInit === 'number'
              ? offsetDistanceInit
              : (position.x - midX) * nx0 + (position.y - midY) * ny0;

          const newDimension = {
            x1,
            y1,
            x2,
            y2,
            value,
            rawValue,
            precision: 2,
            textFontFamily: 'Arial',
            textFontStyle: 'italic',
            textColor: '#ff0000',
            elements: [elem1, elem2],
            isHorizontal,
            angle: effectiveAngle,
            position,
            offsetDistance,
            ...(typeof offsetAlongInit === 'number' ? { offsetAlong: offsetAlongInit } : {}),
            textPosition: { x: 0, y: 0 },
            extensionLines: [], // Добавляем свойство для выносных линий
            // Нижний текст: дефолты
            lowerOverrideEnabled: false,
            lowerOverrideText: '',
          };

          setCurrentDimension(newDimension);
          setSelectedElements([]);
        }
      } else if (currentDimension) {
        saveState(); // Сохраняем текущее состояние
        // Закрепляем размерную линию на третье нажатие
        setDimensionLines([...dimensionLines, currentDimension]);
        // Сразу пересчитываем (особенно важно для диагональных)
        setTimeout(() => {
          try {
            updateAllDimensionLines();
          } catch (_) {}
        }, 0);
        setCurrentDimension(null);
      }
      return;
    }

  // Режим Позиция (выноска): создаём новую выноску кликом по пустому месту
  if (isBalloonModeEnabled) {
      const stage = e.target.getStage();
      const pos = stage.getPointerPosition();
      if (pos) {
        const z = effectiveZoom || 1;
        const logical = { x: pos.x / z, y: pos.y / z };
        saveState();
        const id = 'balloon-' + Date.now();
        const newBalloon = {
          id,
          x: logical.x,
          y: logical.y,
          text: 'Позиция',
          targets: [], // массив { type: 'cylinder'|'fitting', id }
        };
        setBalloons((prev) => [...prev, newBalloon]);
        setSelectedBalloonId(id);
      }
      return;
    }

  // Режим Добавить текст: создаём текст кликом по рабочей области
  if (isAddAnnotationModeEnabled) {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (pos) {
      saveState();
      const newAnnotation = {
        id: 'anno-' + Date.now(),
        x: pos.x / (responsiveScale || 1),
        y: pos.y / (responsiveScale || 1),
        text: 'Текст',
        fontFamily: 'Arial',
        fontStyle: 'italic',
        fontSize: 14,
        align: 'left',
        fill: '#000000',
      };
      setAnnotations((prev) => [...prev, newAnnotation]);
      setSelectedAnnotationId(newAnnotation.id);
    }
    return;
  }

    if (!isDrawingEnabled) return;
    setIsDrawing(true);
    const rawPosDown = e.target.getStage().getPointerPosition();
    const x = rawPosDown.x / (effectiveZoom || 1);
    const y = rawPosDown.y / (effectiveZoom || 1);
    const snappedPos = { x, y };

    const { x: snappedX, y: snappedY } = snappedPos;

    setCurrentLine({ points: [snappedX, snappedY] });
    setIsDrawing(true);

    // Добавьте обработку выделения области
    if (e.evt.button === 0 && !isDimensionLineEnabled && !isDrawingEnabled) {
      const { x, y } = e.target.getStage().getPointerPosition();
      setSelectionStart({ x, y });
      setSelectionRect({
        x,
        y,
        width: 0,
        height: 0,
      });
    }
  };

  const handleMouseMove = (e) => {
    // Точки видов подсвечиваются по расстоянию до курсора, независимо от того, какая линия под ним
    if (isDimensionLineEnabled && !isMiddlePanning && !isObjectDraggingRef.current && projectedViews.length > 0) {
      const viewStage = e.target.getStage();
      const viewPointer = viewStage && viewStage.getPointerPosition();
      if (viewPointer) {
        const viewZoom = effectiveZoom || 1;
        const wx = viewPointer.x / viewZoom;
        const wy = viewPointer.y / viewZoom;
        let nearest = null;
        projectedViews.forEach((view) => {
          if (!view.scene) return;
          getViewSnapPoints(view.scene).forEach((point, pointIndex) => {
            const distance = Math.hypot(wx - (view.x + point.x), wy - (view.y + 28 + point.y));
            const reach = point.guide ? 18 : 9;
            if (distance <= reach && (!nearest || distance < nearest.distance)) {
              nearest = { view, point, pointIndex, distance };
            }
          });
        });
        if (nearest) {
          const next = {
            x: nearest.view.x + nearest.point.x,
            y: nearest.view.y + 28 + nearest.point.y,
            objectType: 'view',
            objectId: nearest.view.id,
            pointIndex: nearest.pointIndex,
            local: { x: nearest.point.x, y: 28 + nearest.point.y },
          };
          setHighlightedPoint((prev) => (
            prev && prev.objectId === next.objectId && prev.pointIndex === next.pointIndex ? prev : next
          ));
          setHighlightedSide((prev) => (prev && prev.objectId === nearest.view.id ? null : prev));
        }
      }
    }
    // Панорамирование при зажатом колёсике
    if (isMiddlePanning) {
      const stage = e.target.getStage();
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const z = (effectiveZoom || 1);
      const now = { x: pos.x / z, y: pos.y / z };
      const last = middlePanLastRef.current || now;
      const dx = now.x - last.x;
      const dy = now.y - last.y;
      if (dx !== 0 || dy !== 0) {
        // Сдвиг по сцене -> переводим в локальные координаты для каждого повернутого объекта
        const dxStage = dx * z;
        const dyStage = dy * z;
        const toLocalDelta = (group, dxS, dyS) => {
          try {
            if (!group || !group.getAbsoluteTransform) {
              return { dxLocal: dx, dyLocal: dy };
            }
            const absT = group.getAbsoluteTransform();
            const invT = absT.copy().invert();
            const p0 = invT.point({ x: 0, y: 0 });
            const p1 = invT.point({ x: dxS, y: dyS });
            return { dxLocal: p1.x - p0.x, dyLocal: p1.y - p0.y };
          } catch (_) {
            return { dxLocal: dx, dyLocal: dy };
          }
        };

        setCylinders((prev) => prev.map((c) => {
          const stageNode = stageRef.current;
          let node = stageNode ? stageNode.findOne(`#${c.id}`) : null;
          const group = node && node.getClassName && node.getClassName() === 'Group' ? node : node ? node.getParent() : null;
          const { dxLocal, dyLocal } = toLocalDelta(group, dxStage, dyStage);
          return { ...c, x: c.x + dxLocal, y: c.y + dyLocal };
        }));

        setFittings((prev) => prev.map((f) => {
          const stageNode = stageRef.current;
          let node = stageNode ? stageNode.findOne(`#${f.id}`) : null;
          const group = node && node.getClassName && node.getClassName() === 'Group' ? node : node ? node.getParent() : null;
          const { dxLocal, dyLocal } = toLocalDelta(group, dxStage, dyStage);
          return { ...f, x: f.x + dxLocal, y: f.y + dyLocal };
        }));
        setLines((prev) => prev.map((ln) => ({
          ...ln,
          points: ln.points.map((val, i) => (i % 2 === 0 ? val + dx : val + dy)),
        })));
        // Не двигаем аннотации при панорамировании — они экранные
        // Сдвигаем выноски (позиции) вместе со сценой
        // Выноски, привязанные к виду, двигаются только вместе с видом, а не с главным видом
        setBalloons((prev) => prev.map((b) => (
          (b.targets || []).some((tt) => tt.type === 'view') ? b : { ...b, x: b.x + dx, y: b.y + dy }
        )));
        // Сдвигаем позицию размерных линий по нужной оси, чтобы сохранять относительное положение
        setDimensionLines((prev) => prev.map((d) => {
          // Размеры на видах ездят только вместе с видом
          if ((d.elements || []).some((el) => el && el.objectType === 'view')) return d;
          const angleDegBase = typeof d.angle === 'number' ? d.angle : (d.isHorizontal ? 0 : 90);
          if (angleDegBase % 90 === 0) {
            if (d.isHorizontal) {
              return { ...d, position: { x: d.position.x, y: d.position.y + dy } };
            }
            // вертикальная
            return { ...d, position: { x: d.position.x + dx, y: d.position.y } };
          }
          // диагональные пересчитываются от mid + offsets — position не трогаем
          return d;
        }));
        // Пересчитываем размерные линии, чтобы они остались на тех же относительных местах
        try { updateAllDimensionLines(); } catch (_) {}
        // Обновим позицию курсора для отображения индикатора
        setCursorPosition({ x: now.x, y: now.y });
      }
      middlePanLastRef.current = now;
      e.evt && e.evt.preventDefault();
      return; // Во время панорамирования игнорируем остальную логику
    }
    if (currentDimension) {
      const stage = e.target.getStage();
      const raw = stage.getPointerPosition();
      const point = { x: raw.x / (effectiveZoom || 1), y: raw.y / (effectiveZoom || 1) };

      const { x1, y1, x2, y2, isHorizontal } = currentDimension;

      // Позиция в логических координатах (уже делена на zoom выше)
      const position = isHorizontal
        ? { x: 0, y: point.y }
        : { x: point.x, y: 0 };

      const value = isHorizontal
        ? dimensionMillimeters(Math.abs(x2 - x1))
        : dimensionMillimeters(Math.abs(y2 - y1));

      setCurrentDimension({
        ...currentDimension,
        position,
        value,
      });
    }

    if (!isDrawing || !isDrawingEnabled) return;
    const rawMove = e.target.getStage().getPointerPosition();
    const point = { x: rawMove.x / (effectiveZoom || 1), y: rawMove.y / (effectiveZoom || 1) };
    setCursorPosition({ x: point.x, y: point.y });
    let snappedPos;
    if (snappingMode === 'grid') {
      snappedPos = getSnappedPosition(point.x, point.y);
    } else {
      snappedPos = { x: point.x, y: point.y };
    }

    const { x: snappedX, y: snappedY } = snappedPos;

    setCurrentLine((prevLine) => {
      return {
        ...prevLine,
        points: [...prevLine.points, snappedX, snappedY],
      };
    });

    // Добавьте обработку выделения области
    if (selectionStart) {
      const stage = e.target.getStage();
      const raw = stage.getPointerPosition();
      const point = { x: raw.x / (effectiveZoom || 1), y: raw.y / (effectiveZoom || 1) };
      setSelectionRect({
        x: Math.min(selectionStart.x, point.x),
        y: Math.min(selectionStart.y, point.y),
        width: Math.abs(point.x - selectionStart.x),
        height: Math.abs(point.y - selectionStart.y),
      });
    }
  };

  const handleMouseUp = (e) => {
    // Завершение панорамирования по отпусканию средней кнопки
    if (isMiddlePanning && e.evt && e.evt.button === 1) {
      setIsMiddlePanning(false);
      const stage = e.target.getStage();
      try { stage.container().style.cursor = 'default'; } catch (_) {}
      saveState();
      return;
    }
    if (!isDrawingEnabled) return;
    setIsDrawing(false);
  
    if (currentLine) {
      // Проверяем, замкнута ли фигура
      const points = currentLine.points;
      const distance = Math.hypot(
        points[0] - points[points.length - 2],
        points[1] - points[points.length - 1]
      );
  
      const closeThreshold = 100; // Порог для определения, что фигура замкнута (увеличен для удобства)
  
      if (distance < closeThreshold) {
        // Проверяем, является ли фигура приблизительно прямоугольником
        if (isApproximatelyRectangle(points)) {
          // Создаем цилиндр
          createCylinderFromPoints(points);
        }
      } else {
        // Добавляем линию в массив линий
        setLines([...lines, currentLine]);

        // Сохраняем состояние после добавления линии
        saveState();
      }
      setCurrentLine(null);
    }

    // Добавьте обработку выделения области
    if (selectionRect) {
      // Найдите объекты в области выделения
      const objects = [...cylinders, ...fittings].filter(obj => {
        return (
          obj.x >= selectionRect.x &&
          obj.x + obj.width <= selectionRect.x + selectionRect.width &&
          obj.y >= selectionRect.y &&
          obj.y + obj.height <= selectionRect.y + selectionRect.height
        );
      });

      if (objects.length > 0) {
        setSelectedId(objects[0].id); // Пока выбираем только первый объект
        setRotateTargetId(null);
      }

      setSelectionStart(null);
      setSelectionRect(null);
    }
  };  

  const handleMouseOver = (e) => {
    if (e.target.getClassName() === 'Rect') {
      const rect = e.target;
      const group = rect.getParent();
      const t = group.getAbsoluteTransform();
      const x = rect.x();
      const y = rect.y();
      const width = rect.width();
      const height = rect.height();
      const mousePosRaw = rect.getStage().getPointerPosition();
      const z = effectiveZoom || 1;
      const mousePos = { x: mousePosRaw.x / z, y: mousePosRaw.y / z };
      const objectId = rect.id();
  

      const highlightZone = 10;

      const transformPoint = (p) => {
        const abs = t.point({ x: p.x, y: p.y });
        return { x: abs.x / z, y: abs.y / z };
      };
      const topLeft = transformPoint({ x, y });
      const topRight = transformPoint({ x: x + width, y });
      const bottomLeft = transformPoint({ x, y: y + height });
      const bottomRight = transformPoint({ x: x + width, y: y + height });

      const topCenter = transformPoint({ x: x + width / 2, y });
      const bottomCenter = transformPoint({ x: x + width / 2, y: y + height });
      const leftCenter = transformPoint({ x, y: y + height / 2 });
      const rightCenter = transformPoint({ x: x + width, y: y + height / 2 });

      const distancePointToSegment = (px, py, x1, y1, x2, y2) => {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;
        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;
        if (lenSq !== 0) param = dot / lenSq;
        let xx;
        let yy;
        if (param < 0) {
          xx = x1;
          yy = y1;
        } else if (param > 1) {
          xx = x2;
          yy = y2;
        } else {
          xx = x1 + param * C;
          yy = y1 + param * D;
        }
        const dx = px - xx;
        const dy = py - yy;
        return Math.sqrt(dx * dx + dy * dy);
      };
  
      // Проверяем, является ли это цилиндром или фитингом
      const isCylinder = cylinders.some((cylinder) => cylinder.id === objectId);
      const isFitting = fittings.some((fitting) => fitting.id === objectId);

      let objectType = null;
      if (isCylinder) {
        objectType = 'cylinder';
      } else if (isFitting) {
        objectType = 'fitting';
      } else {
        return; // Если объект не цилиндр и не фитинг, выходим из функции
      }

  
      // Определение, над какой стороной находимся по расстоянию до повёрнутых сегментов
      const candidates = [
        {
          type: 'top',
          p1: topLeft,
          p2: topRight,
          center: topCenter,
        },
        {
          type: 'bottom',
          p1: bottomLeft,
          p2: bottomRight,
          center: bottomCenter,
        },
        {
          type: 'left',
          p1: topLeft,
          p2: bottomLeft,
          center: leftCenter,
        },
        {
          type: 'right',
          p1: topRight,
          p2: bottomRight,
          center: rightCenter,
        },
      ];

      let best = null;
      let bestDist = Infinity;
      candidates.forEach((c) => {
        const d = distancePointToSegment(
          mousePos.x,
          mousePos.y,
          c.p1.x,
          c.p1.y,
          c.p2.x,
          c.p2.y
        );
        if (d < bestDist) {
          bestDist = d;
          best = c;
        }
      });

      if (best && bestDist <= highlightZone) {
        setHighlightedSide({
          type: best.type,
          x: best.center.x,
          y: best.center.y,
          objectType,
          objectId,
          points: [best.p1.x, best.p1.y, best.p2.x, best.p2.y],
        });
      } else {
        setHighlightedSide(null);
      }
    }
  }; 
  
  const handleDimensionDrag = (e, index) => {
    // Если активно панорамирование средней кнопкой - не обрабатываем перетаскивание размерной линии
    if (isMiddlePanning || (e.evt && e.evt.button === 1)) {
      e.target.stopDrag();
      return;
    }
    
    const dim = dimensionLines[index];
    const angleDegBase = typeof dim.angle === 'number' ? dim.angle : (dim.isHorizontal ? 0 : 90);
    let position;
    if (angleDegBase % 90 !== 0) {
      // Для угловых размеров: обновляем offsetAlong/offsetDistance исходя из нового положения
      const dx = e.target.x();
      const dy = e.target.y();
      // Найдём текущие мировые координаты середины между точками
      const dimNow = dim;
      const midX = (dimNow.x1 + dimNow.x2) / 2;
      const midY = (dimNow.y1 + dimNow.y2) / 2;
      const angleRad = (angleDegBase * Math.PI) / 180;
      const ux = Math.cos(angleRad);
      const uy = Math.sin(angleRad);
      const nx = -uy;
      const ny = ux;
      const vx = dx - midX;
      const vy = dy - midY;
      const offsetAlong = vx * ux + vy * uy;
      const offsetDistance = vx * nx + vy * ny;
      position = { x: dx, y: dy };
      const updatedDimensions = dimensionLines.slice();
      updatedDimensions[index] = { ...dimNow, position, offsetAlong, offsetDistance };
      setDimensionLines(updatedDimensions);
      return; // ранний выход, чтобы не затирать обновление ниже
    } else {
      // Для горизонтальных/вертикальных разрешаем свободное перемещение группы,
      // но сохраняем прежнюю семантику вычисления value/extensionLines через updateAllDimensionLines
      position = { x: e.target.x(), y: e.target.y() };
      const updatedDimensions = dimensionLines.slice();
      updatedDimensions[index] = { ...dim, position };
      setDimensionLines(updatedDimensions);
    }
  };

  const handleDragMove = useCallback(
    (e) => {
      if (!e.evt.buttons) return;
      if (!e.target.isDragging()) return;
      
      // Если активно панорамирование средней кнопкой или нажата средняя кнопка - останавливаем перетаскивание объекта
      if (isMiddlePanning || (e.evt && e.evt.button === 1)) {
        e.target.stopDrag();
        return;
      }

      const target = e.target;
      const id = target.id();
      const localX = target.x();
      const localY = target.y();
      const parent = target.getParent();
      const parentAbsT = parent.getAbsoluteTransform();
      const stagePoint = parentAbsT.point({ x: localX, y: localY });

      const movingCylinder = cylinders.find((c) => c.id === id);
      const movingFitting = fittings.find((f) => f.id === id);

      if (movingCylinder) {
        // Во время реального перемещения объектов в режиме размеров — не позволяем выбирать первую точку
        if (isDimensionLineEnabled && isObjectDraggingRef.current) {
          setHighlightedSide(null);
          setHighlightedPoint(null);
          setSelectedElements([]);
        }
        // Обработка перемещения цилиндра
        let snappedStage = snappingMode === 'none'
          ? { x: stagePoint.x, y: stagePoint.y }
          : getSnappedPosition(stagePoint.x, stagePoint.y, id, movingCylinder.width, movingCylinder.height);

        // Переводим обратно в локальные координаты родителя
        const localSnapped = parentAbsT.copy().invert().point({ x: snappedStage.x, y: snappedStage.y });
        let newX = localSnapped.x;
        let newY = localSnapped.y;

        // Проверяем, заблокированы ли оси из-за привязок к заблокированным объектам
        const cylinder = cylindersRef.current.find(c => c.id === id);
        const rotation = cylinder?.rotation || 0;
        const { lockedX: globalLockedX, lockedY: globalLockedY } = getLockedAxes(id, 'cylinder');
        
        // Преобразуем заблокированные ГЛОБАЛЬНЫЕ оси в ЛОКАЛЬНЫЕ с учетом rotation
        let localLockedX = false;
        let localLockedY = false;
        
        if (globalLockedX || globalLockedY) {
          const normalizedRotation = ((rotation % 360) + 360) % 360;
          const rotationSteps = Math.round(normalizedRotation / 90) % 4;
          
          // Карта преобразования глобальных осей в локальные в зависимости от rotation
          if (rotationSteps === 0) {
            // 0° - оси совпадают
            localLockedX = globalLockedX;
            localLockedY = globalLockedY;
          } else if (rotationSteps === 1) {
            // 90° - глобальная X становится локальной Y, глобальная Y становится локальной X (инвертированной)
            localLockedX = globalLockedY;
            localLockedY = globalLockedX;
          } else if (rotationSteps === 2) {
            // 180° - оси совпадают (но инвертированы)
            localLockedX = globalLockedX;
            localLockedY = globalLockedY;
          } else if (rotationSteps === 3) {
            // 270° - глобальная X становится локальной Y (инвертированной), глобальная Y становится локальной X
            localLockedX = globalLockedY;
            localLockedY = globalLockedX;
          }
          
          
          const startPos = dragStartPositionRef.current[id];
          if (startPos) {
            if (localLockedX) {
              newX = startPos.x;
            }
            if (localLockedY) {
              newY = startPos.y;
            }
          }
        }

        setCylinders((prevCylinders) => {
          const index = prevCylinders.findIndex((c) => c.id === id);
          if (index !== -1) {
            const updatedCylinders = [...prevCylinders];
            updatedCylinders[index] = {
              ...updatedCylinders[index],
              x: newX,
              y: newY,
            };
            return updatedCylinders;
          }
          return prevCylinders;
        });

        target.position({ x: newX, y: newY });
        try { if (updateAllDimensionLinesRef.current) updateAllDimensionLinesRef.current(); } catch (_) {}
      } else if (movingFitting) {
        if (isDimensionLineEnabled && isObjectDraggingRef.current) {
          setHighlightedSide(null);
          setHighlightedPoint(null);
          setSelectedElements([]);
        }
        // Обработка перемещения фитинга
        let snappedStage = snappingMode === 'none'
          ? { x: stagePoint.x, y: stagePoint.y }
          : getSnappedPosition(stagePoint.x, stagePoint.y, id, getFittingWidth(movingFitting), getFittingHeight(movingFitting));
        const localSnapped = parentAbsT.copy().invert().point({ x: snappedStage.x, y: snappedStage.y });
        let newX = localSnapped.x;
        let newY = localSnapped.y;

        // Проверяем, заблокированы ли оси из-за привязок к заблокированным объектам
        const fitting = fittingsRef.current.find(f => f.id === id);
        const rotation = fitting?.rotation || 0;
        const { lockedX: globalLockedX, lockedY: globalLockedY } = getLockedAxes(id, 'fitting');
        
        // Преобразуем заблокированные ГЛОБАЛЬНЫЕ оси в ЛОКАЛЬНЫЕ с учетом rotation
        let localLockedX = false;
        let localLockedY = false;
        
        if (globalLockedX || globalLockedY) {
          const normalizedRotation = ((rotation % 360) + 360) % 360;
          const rotationSteps = Math.round(normalizedRotation / 90) % 4;
          
          // Карта преобразования глобальных осей в локальные в зависимости от rotation
          if (rotationSteps === 0) {
            // 0° - оси совпадают
            localLockedX = globalLockedX;
            localLockedY = globalLockedY;
          } else if (rotationSteps === 1) {
            // 90° - глобальная X становится локальной Y, глобальная Y становится локальной X (инвертированной)
            localLockedX = globalLockedY;
            localLockedY = globalLockedX;
          } else if (rotationSteps === 2) {
            // 180° - оси совпадают (но инвертированы)
            localLockedX = globalLockedX;
            localLockedY = globalLockedY;
          } else if (rotationSteps === 3) {
            // 270° - глобальная X становится локальной Y (инвертированной), глобальная Y становится локальной X
            localLockedX = globalLockedY;
            localLockedY = globalLockedX;
          }
          
          
          const startPos = dragStartPositionRef.current[id];
          if (startPos) {
            if (localLockedX) {
              newX = startPos.x;
            }
            if (localLockedY) {
              newY = startPos.y;
            }
          }
        }

        setFittings((prevFittings) => {
          const index = prevFittings.findIndex((f) => f.id === id);
          if (index !== -1) {
            const updatedFittings = [...prevFittings];
            updatedFittings[index] = {
              ...updatedFittings[index],
              x: newX,
              y: newY,
            };
            return updatedFittings;
          }
          return prevFittings;
        });

        target.position({ x: newX, y: newY });
        try { if (updateAllDimensionLinesRef.current) updateAllDimensionLinesRef.current(); } catch (_) {}
      }
    },
    [cylinders, fittings, snappingMode, getSnappedPosition, updateAllDimensionLinesRef, isDimensionLineEnabled, getLockedAxes, isMiddlePanning]
  );

  const isApproximatelyRectangle = (points) => {
    // Простейшая проверка: если в линии 5 точек (начало и конец совпадают), и линии пересекаются под прямыми углами
    if (points.length < 8) return false; // Минимум 4 точки (8 координат)
  
    // Получаем координаты вершин
    const vertices = [];
    for (let i = 0; i < points.length - 1; i += 2) {
      vertices.push({ x: points[i], y: points[i + 1] });
    }
  
    // Проверяем количество вершин (должно быть минимум 4 для прямоугольника)
    if (vertices.length < 4) return false;
  
    // Дополнительная проверка на прямые углы и параллельность сторон может быть добавлена здесь
    // Для простоты будем считать, что любая замкнутая фигура с 4 и более точками является прямоугольником
    return true;
  };

  const createCylinderFromPoints = (points) => {
    // Находим минимальные и максимальные координаты
    let minX = points[0];
    let maxX = points[0];
    let minY = points[1];
    let maxY = points[1];
  
    for (let i = 2; i < points.length; i += 2) {
      const x = points[i];
      const y = points[i + 1];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  
    const width = maxX - minX;
    const height = maxY - minY;
  
    const newCylinder = {
      id: Date.now().toString(),
      x: minX,
      y: minY,
      width: width,
      height: height,
      locked: false,
    };
    setCylinders((prevCylinders) => [...prevCylinders, newCylinder]);

    // Сохраняем состояние после добавления цилиндра
    saveState();
  };  

  const handleClearCanvas = useCallback(() => {
    saveState(); // Сохраняем текущее состояние
    setLines([]);
    setCylinders([]);
    setFittings([]);
    setCanvasMetadata((prev) => {
      if (prev.totalLength == null && prev.separatorTotalHeight == null) return prev;
      const { totalLength, separatorTotalHeight, ...rest } = prev;
      return rest;
    });
    // Сбрасываем аннотации к начальным значениям (пустой холст → короткий вариант)
    setAnnotations(getInitialAnnotations(isLandscape, false));
    setBalloons([]);
    setDimensionLines([]);
    setObjectConstraints([]);
    setHighlightedSide(null);
    setHighlightedPoint(null);
    setSelectedElements([]);
    setCurrentDimension(null);
    setPatterns([]);
    setProjectedViews([]);
    setSelectedProjectedViewId(null);
    setSelectedPatternObjects([]);
    // Сбрасываем состояние таблицы крепежей
    setFastenersTableState(null);
  }, [saveState, isLandscape, getInitialAnnotations]);

  // Экспортируем снапшот объектов наверх через setGetObjectsRef
  useEffect(() => {
    if (typeof setGetObjectsRef === 'function') {
      setGetObjectsRef(() => () => ({
        fittings: (fittings || []).map((f) => ({
          id: f.id,
          itemKey: f.itemKey,
          width: f.width,
          height: f.height,
          x: f.x,
          y: f.y,
          groupX: f.groupX,
          groupY: f.groupY,
          type: f.type || 'fitting', // Используем реальный тип объекта
          isSelfFacing: f.isSelfFacing || false,
          isCenterFitting: f.isCenterFitting || false,
          isHidden: f.isHidden || false,
          igesParentCylinderId: f.igesParentCylinderId,
          flangePipeLength: f.flangePipeLength,
          fittingLength: f.fittingLength,
          overrideEnabled: f.overrideEnabled || false,
          overrideText: f.overrideText,
          key: f.key || f.itemKey,
          // Дополнительные поля для крепежей
          fastenerHeight: f.fastenerHeight,
          clampDiameter: f.clampDiameter,
          axialDistance: f.axialDistance,
          overhang: f.overhang,
          rangeOffset: f.rangeOffset,
          pipeDiameter: f.pipeDiameter,
          includedInPrice: f.includedInPrice,
          isFloorFastener: f.isFloorFastener,
        })),
        cylinders: (cylinders || []).map((c) => ({
          id: c.id,
          width: c.width,
          height: c.height,
          x: c.x,
          y: c.y,
          type: 'cylinder',
          pipeSpec: c.pipeSpec || null,
          isHidden: c.isHidden || false,
          insertionDepth: c.insertionDepth || 0,
        })),
        welds: (() => {
          // Собираем реальные сварки
          const realWelds = (welds || []).map((w) => ({
            id: w.id,
            obj1: w.obj1,
            obj2: w.obj2,
            type: w.type,
            length: w.length,
            diameter: w.diameter,
            displayDiameter: w.displayDiameter,
            x: w.x,
            y: w.y,
            source: w.source, // ВАЖНО: передаём source для определения типа сварки
          }));
          
          // Добавляем виртуальные сварки для KRGB крепежей
          const krgbFasteners = (fittings || []).filter(f => f.type === 'fastener' && f.itemKey === 'KRGB');
          const krgbWelds = [];
          
          krgbFasteners.forEach((f, index) => {
            const pipeDiameter = f.pipeDiameter || f.width || 406.4;
            
            // 1. Прямая сварка диаметра крепежа (1 шт)
            krgbWelds.push({
              id: `krgb-weld-main-${f.id}`,
              type: 'Прямая',
              diameter: pipeDiameter,
              length: pipeDiameter, // Длина = диаметр для круглых сварок
              displayDiameter: pipeDiameter,
              x: f.x,
              y: f.y,
            });
            
            // 2. Сварки профиля 80×40 длиной 240 мм (4 шт)
            // Переводим в эквивалентный диаметр: периметр прямоугольника 80×40 = 2*(80+40) = 240
            // Эквивалентный диаметр круга с таким же периметром: D = 240/π ≈ 76.4
            const profileWeldLength = 240;
            const profileEquivalentDiameter = profileWeldLength / Math.PI; // ≈ 76.4
            for (let i = 0; i < 4; i++) {
              krgbWelds.push({
                id: `krgb-weld-profile-${f.id}-${i}`,
                type: 'Прямая',
                diameter: profileEquivalentDiameter,
                length: profileEquivalentDiameter, // Используем эквивалентный диаметр для расчета
                displayDiameter: '80×40 (240мм)',
                x: f.x,
                y: f.y,
              });
            }
            
            // 3. Сварки швов длиной 80 мм (12 шт)
            // Эквивалентный диаметр: D = 80/π ≈ 25.5
            const seamWeldLength = 80;
            const seamEquivalentDiameter = seamWeldLength / Math.PI; // ≈ 25.5
            for (let i = 0; i < 12; i++) {
              krgbWelds.push({
                id: `krgb-weld-seam-${f.id}-${i}`,
                type: 'Прямая',
                diameter: seamEquivalentDiameter,
                length: seamEquivalentDiameter, // Используем эквивалентный диаметр для расчета
                displayDiameter: 'Шов (80мм)',
                x: f.x,
                y: f.y,
              });
            }
          });
          
          return [...realWelds, ...krgbWelds];
        })(),
        cuts: (() => {
          // Создаем виртуальные резки для KRGB крепежей
          const krgbFasteners = (fittings || []).filter(f => f.type === 'fastener' && f.itemKey === 'KRGB');
          const krgbCuts = [];
          
          krgbFasteners.forEach((f) => {
            // 8 резок диаметром до 168.3 мм (категория "до 168.3")
            const cutDiameter = 168.3; // Максимальный диаметр в категории
            for (let i = 0; i < 8; i++) {
              krgbCuts.push({
                id: `krgb-cut-${f.id}-${i}`,
                diameter: cutDiameter,
                x: f.x,
                y: f.y,
              });
            }
          });
          
          return krgbCuts;
        })(),
      }));
    }
  }, [setGetObjectsRef, fittings, cylinders, welds]);

  // Экспортируем функцию установки метаданных
  useEffect(() => {
    if (typeof setCanvasMetadataRef === 'function') {
      setCanvasMetadataRef(() => (metadata) => {
        setCanvasMetadata(metadata || {});
      });
    }
  }, [setCanvasMetadataRef]);

  // Функция экспорта чертежа (для сохранения)
  const exportDrawing = useCallback(() => {
    return {
      version: '1.0',
      savedAt: new Date().toISOString(),
      canvas: {
        // Сохраняем все данные объектов целиком (глубокое копирование через JSON)
        fittings: JSON.parse(JSON.stringify(fittings)),
        cylinders: JSON.parse(JSON.stringify(cylinders)),
        annotations: JSON.parse(JSON.stringify(annotations)),
        balloons: JSON.parse(JSON.stringify(balloons)),
        dimensionLines: JSON.parse(JSON.stringify(dimensionLines)),
        lines: JSON.parse(JSON.stringify(lines)),
        objectConstraints: JSON.parse(JSON.stringify(objectConstraints)),
        patterns: JSON.parse(JSON.stringify(patterns)),
        welds: JSON.parse(JSON.stringify(welds)),
        projectedViews: JSON.parse(JSON.stringify(projectedViews)),
        metadata: JSON.parse(JSON.stringify(canvasMetadata)),
      },
      settings: {
        isLandscape,
        zoom,
        textZoom,
        positionTextZoom,
      },
    };
  }, [fittings, cylinders, annotations, balloons, dimensionLines, lines, objectConstraints, patterns, welds, projectedViews, canvasMetadata, isLandscape, zoom, textZoom, positionTextZoom]);

  // Функция импорта чертежа (для загрузки)
  // Функция для загрузки shapes из DXF для фитинга
  const loadFittingShapes = useCallback(async (fitting) => {
    // Пропускаем скрытые фитинги (например, фиктивные фитинги для IGES отверстий)
    if (fitting.isHidden) {
      return null;
    }
    
    const fittingKey = fitting.key || fitting.itemKey;
    if (!fittingKey || !fittingsData[fittingKey]) {
      console.warn('Фитинг без key/itemKey или данных:', fitting.id);
      return null;
    }
    
    const fittingInfo = fittingsData[fittingKey];
    
    // Если фитинг помечен как isSelfFacing и есть selfFacing конфиг - используем его
    const useSelfFacing = fitting.isSelfFacing && fittingInfo.selfFacing && fittingInfo.selfFacing.fileName;
    const selfFacingConfig = useSelfFacing ? fittingInfo.selfFacing : null;
    
    const fileName = selfFacingConfig ? selfFacingConfig.fileName : fittingInfo.fileName;
    if (!fileName) {
      console.warn('Нет fileName для фитинга:', fitting.key);
      return null;
    }
    
    try {
      const folder = 'fittings';
      const response = await fetch(`/${folder}/${fileName}`);
      if (!response.ok) {
        throw new Error(`Не удалось загрузить DXF: ${response.statusText}`);
      }
      const dxfText = await response.text();
      const parser = new DxfParser();
      const dxf = parser.parseSync(dxfText);
      const shapes = convertDxfToKonvaShapes(dxf);
      
      // Вычисляем bounding box
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      shapes.forEach((shape) => {
        if (shape.type === 'line' || shape.type === 'polyline') {
          const points = shape.points;
          for (let i = 0; i < points.length; i += 2) {
            const px = points[i];
            const py = points[i + 1];
            if (px < minX) minX = px;
            if (px > maxX) maxX = px;
            if (py < minY) minY = py;
            if (py > maxY) maxY = py;
          }
        } else if (shape.type === 'circle' || shape.type === 'arc') {
          const x1 = shape.x - shape.radius;
          const x2 = shape.x + shape.radius;
          const y1 = shape.y - shape.radius;
          const y2 = shape.y + shape.radius;
          if (x1 < minX) minX = x1;
          if (x2 > maxX) maxX = x2;
          if (y1 < minY) minY = y1;
          if (y2 > maxY) maxY = y2;
        }
      });
      
      const objectWidth = maxX - minX;
      const objectHeight = maxY - minY;
      
      // Нормализуем shapes к 0,0
      shapes.forEach((shape) => {
        if (shape.type === 'line' || shape.type === 'polyline') {
          shape.points = shape.points.map((coord, index) => 
            index % 2 === 0 ? coord - minX : coord - minY
          );
        } else if (shape.type === 'circle' || shape.type === 'arc') {
          shape.x -= minX;
          shape.y -= minY;
        }
      });
      
      // Для selfFacing:
      // - width и height для визуализации берем из selfFacing (круг 29.9×29.9)
      // - реальная длина фитинга (35) будет браться из fittingsData в спецификации
      const resultWidth = selfFacingConfig ? (selfFacingConfig.diameter || objectWidth) : (fittingInfo.diameter || objectWidth);
      const resultHeight = selfFacingConfig ? (selfFacingConfig.length || selfFacingConfig.diameter || objectHeight) : (fittingInfo.length || objectHeight);
      const isDiameterFixed = selfFacingConfig ? (selfFacingConfig.isDiameterFixed !== false) : (fittingInfo.isDiameterFixed !== false);
      const isLengthAdjustable = selfFacingConfig ? (selfFacingConfig.adjustableLength === true) : (fittingInfo.adjustableLength === true);
      
      return {
        shapes,
        originalWidth: objectWidth,
        originalHeight: objectHeight,
        width: resultWidth,
        height: resultHeight,
        isDiameterFixed: isDiameterFixed,
        isLengthAdjustable: isLengthAdjustable,
        snapPoints: fittingInfo.snapPoints || null,
        isSelfFacing: useSelfFacing, // Сохраняем флаг
      };
    } catch (err) {
      console.error('Ошибка загрузки DXF для фитинга:', fitting.key, err);
      return null;
    }
  }, []);

  // Ref для отслеживания фитингов в процессе загрузки shapes
  const loadingFittingShapesRef = useRef(new Set());

  // useEffect для автоматической загрузки shapes для фитингов без них
  useEffect(() => {
    const fittingsWithoutShapes = fittings.filter(f => 
      (!f.shapes || f.shapes.length === 0) && 
      (f.key || f.itemKey) && 
      !f.isHidden && // Пропускаем скрытые фитинги
      !loadingFittingShapesRef.current.has(f.id)
    );
    if (fittingsWithoutShapes.length === 0) return;
    
    // Помечаем фитинги как загружающиеся
    fittingsWithoutShapes.forEach(f => loadingFittingShapesRef.current.add(f.id));
    
    
    Promise.all(fittingsWithoutShapes.map(async (fitting) => {
      const data = await loadFittingShapes(fitting);
      return { fitting, data };
    })).then(results => {
      setFittings(prev => prev.map(f => {
        const result = results.find(r => r.fitting.id === f.id);
        if (result && result.data) {
          // Убираем из загружающихся
          loadingFittingShapesRef.current.delete(f.id);
          
          // Для фитингов с useOriginalSize (бортшайбы, отводы, переходы):
          // используем размеры из fittingsData, но делаем originalWidth = width
          // чтобы масштаб был 1:1 (DXF не масштабируется)
          const finalWidth = f.width || result.data.width;
          const finalHeight = f.height || result.data.height;
          
          return {
            ...f,
            shapes: result.data.shapes,
            // Для useOriginalSize: original = final, чтобы scale = 1:1
            // ИЗМЕНЕНИЕ: Не перезаписываем originalWidth, чтобы сохранить реальные размеры для центрирования
            originalWidth: result.data.originalWidth, 
            originalHeight: result.data.originalHeight,
            width: finalWidth,
            height: finalHeight,
            isDiameterFixed: result.data.isDiameterFixed,
            isLengthAdjustable: result.data.isLengthAdjustable,
            snapPoints: result.data.snapPoints,
          };
        }
        // Убираем из загружающихся даже если не удалось загрузить
        loadingFittingShapesRef.current.delete(f.id);
        return f;
      }));
    }).catch(err => {
      console.error('Ошибка загрузки shapes:', err);
      // Очищаем ref при ошибке
      fittingsWithoutShapes.forEach(f => loadingFittingShapesRef.current.delete(f.id));
    });
  }, [fittings, loadFittingShapes]);

  const importDrawing = useCallback((data, isNewGeneration = false) => {
    if (!data || !data.canvas) {
      console.error('Неверный формат файла чертежа');
      return false;
    }
    
    try {
      // Загружаем объекты
      if (data.canvas.fittings) {
        setFittings(data.canvas.fittings);
      }
      if (data.canvas.cylinders) {
        setCylinders(data.canvas.cylinders);
      }
      if (data.canvas.annotations) {
        setAnnotations(data.canvas.annotations);
      }
      if (data.canvas.balloons) {
        setBalloons(data.canvas.balloons);
      }
      if (data.canvas.dimensionLines) {
        setDimensionLines(data.canvas.dimensionLines);
      }
      if (data.canvas.lines) {
        setLines(data.canvas.lines);
      }
      if (data.canvas.objectConstraints) {
        setObjectConstraints(data.canvas.objectConstraints);
      }
      if (data.canvas.patterns) {
        setPatterns(data.canvas.patterns);
      }
      setProjectedViews(data.canvas.projectedViews || []);
      setSelectedProjectedViewId(null);
      
      // Загружаем метаданные
      if (data.canvas.metadata) {
        setCanvasMetadata(data.canvas.metadata);
      }
      
      // Обновляем аннотацию покрытия ТОЛЬКО при генерации нового чертежа
      // При загрузке сохраненного чертежа аннотация должна оставаться такой, какой была сохранена
      if (isNewGeneration && data.canvas.cylinders && data.canvas.cylinders.length > 0) {
        // Приоритет: сначала берем из metadata (общее покрытие системы), затем из первого цилиндра
        let detectedCoating = null;
        
        // Проверяем metadata.coating (общее покрытие для аннотации)
        if (data.canvas.metadata && data.canvas.metadata.coating) {
          detectedCoating = data.canvas.metadata.coating;
        } 
        // Если нет в metadata, берем из первого цилиндра (для обратной совместимости)
        else {
          const firstCylinder = data.canvas.cylinders[0];
          if (firstCylinder.pipeSpec && firstCylinder.pipeSpec.coating) {
            detectedCoating = firstCylinder.pipeSpec.coating;
          }
        }
        
        if (detectedCoating) {
          // Обновляем аннотацию anno-init-4 после небольшой задержки
          // чтобы убедиться, что аннотации уже загружены
          setTimeout(() => {
            setAnnotations(prev => {
              return prev.map(anno => {
                if (anno.id === 'anno-init-4') {
                  return { ...anno, text: detectedCoating };
                }
                return anno;
              });
            });
          }, 100);
        }
      }
      
      // Сбрасываем выделение
      setSelectedElements([]);
      setSelectedCylinderId(null);
      setSelectedFittingId(null);
      setSelectedAnnotationId(null);
      setSelectedDimensionId(null);
      
      return true;
    } catch (e) {
      console.error('Ошибка при загрузке чертежа:', e);
      return false;
    }
  }, []);

  // Экспортируем функции сохранения/загрузки наверх
  useEffect(() => {
    if (typeof setSaveDrawingRef === 'function') {
      setSaveDrawingRef(exportDrawing);
    }
  }, [setSaveDrawingRef, exportDrawing]);

  useEffect(() => {
    if (typeof setLoadDrawingRef === 'function') {
      setLoadDrawingRef(importDrawing);
    }
  }, [setLoadDrawingRef, importDrawing]);

  // Автозагрузка чертежа при передаче loadDrawingData
  useEffect(() => {
    if (loadDrawingData) {
      // Проверяем, есть ли флаг isNewGeneration
      const isNewGeneration = loadDrawingData.isNewGeneration || false;
      importDrawing(loadDrawingData, isNewGeneration);
    }
  }, [loadDrawingData, importDrawing]);

  // Передаём stageRef наверх для экспорта в PDF
  useEffect(() => {
    if (typeof setStageRef === 'function' && stageRef.current) {
      setStageRef(stageRef.current);
    }
  }, [setStageRef]);

  // Уведомляем верхний уровень об изменениях объектов (для обновления диалога стоимости)
  // Мемоизируем количество объектов, чтобы избежать лишних вызовов
  const fittingsCount = fittings.length;
  const cylindersCount = cylinders.length;
  
  useEffect(() => {
    if (typeof onObjectsChanged === 'function') {
      onObjectsChanged({ fittingsCount, cylindersCount, ts: Date.now() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fittingsCount, cylindersCount]);  // Зависим только от длины массивов

  // Фланцы, отводы или длина изделия > 1 м → блок «Размер для справок» с обрешёткой
  const needsCratingReferenceNote = useMemo(
    () => computeNeedsCratingReferenceNote(fittings, canvasMetadata),
    [fittings, canvasMetadata]
  );

  // Проверяем наличие крепежей
  const hasFasteners = useMemo(() => {
    return fittings.some(f => f.type === 'fastener');
  }, [fittings]);

  // Автоматическое восстановление таблицы крепежей, если она была удалена, но есть крепежи
  useEffect(() => {
    if (hasFasteners && fastenersTableState === null) {
      // Восстанавливаем таблицу с настройками по умолчанию
      setFastenersTableState({});
    }
  }, [hasFasteners, fastenersTableState]);

  // Автоматическое восстановление таблицы цеха, если она была удалена, но есть данные цеха
  useEffect(() => {
    if (workshopInfo && workshopTableState === null) {
      // Восстанавливаем таблицу с настройками по умолчанию
      setWorkshopTableState({});
    }
  }, [workshopInfo, workshopTableState]);

  // Обновление позиции таблицы цеха при смене ориентации
  useEffect(() => {
    // Сбрасываем позицию таблицы цеха к значениям по умолчанию при смене ориентации
    if (workshopTableState !== null) {
      setWorkshopTableState(prev => {
        if (!prev) return prev;
        // Создаем новое состояние без x и y, чтобы использовались значения по умолчанию
        const { x, y, ...rest } = prev;
        return rest;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandscape]); // Только при изменении ориентации

  // Обновляем/добавляем только стартовые тексты при смене ориентации или изменении кода,
  // не трогая остальные аннотации и не перезаписывая их текстовое содержимое
  useEffect(() => {
    const initial = getInitialAnnotations(isLandscape, needsCratingReferenceNote) || [];
    const initialIds = new Set(initial.map((a) => a.id));
    
    setAnnotations((prev) => {
      const prevById = new Map(prev.map((a) => [a.id, a]));
      const updatedInitial = initial.map((initA) => {
        const exist = prevById.get(initA.id);
        if (exist) {
          // Сохраняем набранный пользователем текст, обновляем позицию/оформление
          const updated = {
            ...exist,
            x: initA.x,
            y: initA.y,
            fontFamily: initA.fontFamily,
            fontStyle: initA.fontStyle,
            fontSize: initA.fontSize,
            align: initA.align,
            fill: initA.fill,
          };
          
          // Для anno-init-7 и anno-init-8: обновляем isHidden только если НЕ установлен флаг ручного переключения
          if ((initA.id === 'anno-init-7' || initA.id === 'anno-init-8') && !exist.manuallyToggled) {
            updated.isHidden = initA.isHidden;
          } else if (exist.isHidden !== undefined) {
            // Для других аннотаций или если было ручное переключение - сохраняем существующий isHidden
            updated.isHidden = exist.isHidden;
          } else {
            // Если isHidden не был установлен - используем из initial
            updated.isHidden = initA.isHidden;
          }
          
          return updated;
        }
        return initA;
      });
      
      // Удаляем все начальные аннотации (включая anno-init-7 и anno-init-8)
      // НО сохраняем anno-init-9 (данные цеха) и anno-init-10 (материал для цеха), так как они управляются отдельно
      const others = prev.filter((a) => !initialIds.has(a.id) && a.id !== 'anno-init-7' && a.id !== 'anno-init-8');
      
      // Фильтруем updatedInitial: если есть workshopInfo, не добавляем anno-init-7 и anno-init-8
      const filteredInitial = workshopInfo 
        ? updatedInitial.filter(a => a.id !== 'anno-init-7' && a.id !== 'anno-init-8')
        : updatedInitial;
      
      return [...others, ...filteredInitial];
    });
  }, [isLandscape, needsCratingReferenceNote, getInitialAnnotations, workshopInfo]);

  useEffect(() => {
    const undoAvailable = undoStack.length > 0;
    setCanUndoLocal(undoAvailable);
    if (setCanUndo) {
      setCanUndo(undoAvailable); // Используем функцию из пропсов
    }
  }, [undoStack, setCanUndo]);

  useEffect(() => {
    const redoAvailable = redoStack.length > 0;
    setCanRedoLocal(redoAvailable);
    if (setCanRedo) {
      setCanRedo(redoAvailable); // Используем функцию из пропсов
    }
  }, [redoStack, setCanRedo]); 
  
  useEffect(() => {
    if (setUndoRef) {
      setUndoRef(undo);
    }
    if (setRedoRef) {
      setRedoRef(redo);
    }
  }, [setUndoRef, setRedoRef, undo, redo]);
  
  useEffect(() => {
    if (onClearTrigger) {
      handleClearCanvas();
      resetClearTrigger();
    }
  }, [onClearTrigger, handleClearCanvas, resetClearTrigger]);

  useEffect(() => {
    projectedViewsRef.current = projectedViews;
  }, [projectedViews]);

  const viewJobsRef = useRef({});
  const viewRequestAtMount = useRef(addViewRequest?.count || 0);
  useEffect(() => {
    const requestCount = addViewRequest?.count || 0;
    if (!requestCount || requestCount === viewRequestAtMount.current) return undefined;
    if (!cylinders || cylinders.length === 0) return undefined;
    const kind = ['top', 'bottom', 'left', 'right'].includes(addViewRequest.kind) ? addViewRequest.kind : 'top';
    const labels = { top: 'Вид сверху', bottom: 'Вид снизу', left: 'Вид слева', right: 'Вид справа' };
    const job = (viewJobsRef.current[kind] || 0) + 1;
    viewJobsRef.current[kind] = job;
    const placeholderId = `view-${kind}-${job}`;
    const isOwn = (item) => String(item.id).startsWith(`view-${kind}-`) || (kind === 'top' && String(item.id).startsWith('top-view-'));
    // Зоны листа считаются по размеру рабочей области, а не по чертежу:
    // внизу виды сверху и снизу, справа виды слева и справа.
    // Лист на экране — это canvasWidth x canvasHeight, а чертёж лежит в масштабе effectiveZoom.
    const zoomValue = effectiveZoom || 1;
    const sheetWidthWorld = canvasWidth / zoomValue;
    const sheetHeightWorld = canvasHeight / zoomValue;
    const zones = {
      bottomTop: sheetHeightWorld * 0.69,
      bottomLeft: sheetWidthWorld * 0.06,
      rightLeft: sheetWidthWorld * 0.72,
      rightTop: 36,
    };
    const sheetBottom = sheetHeightWorld - 90 / zoomValue;
    const existing = projectedViewsRef.current.filter((item) => !isOwn(item));
    const sceneSize = (item) => ({ w: item.scene?.width || 0, h: (item.scene?.height || 0) + 28 });
    const place = (scene) => {
      const width = scene ? scene.width : 360;
      const height = scene ? scene.height + 28 : 68;
      const kindOf = (item) => String(item.id).split('-').slice(0, 2).join('-');
      if (kind === 'left' || kind === 'right') {
        const sibling = existing.find((item) => kindOf(item) === (kind === 'right' ? 'view-left' : 'view-right'));
        let x = zones.rightLeft;
        if (kind === 'right' && sibling) x = sibling.x + sceneSize(sibling).w + 30;
        if (kind === 'left' && sibling) x = Math.max(36, sibling.x - width - 30);
        return { x, y: Math.max(36, Math.min(zones.rightTop, sheetBottom - height)) };
      }
      const sibling = existing.find((item) => kindOf(item) === (kind === 'bottom' ? 'view-top' : 'view-bottom')
        || (kind === 'bottom' && String(item.id).startsWith('top-view-')));
      let y = zones.bottomTop;
      if (kind === 'bottom' && sibling) y = sibling.y + sceneSize(sibling).h + 20;
      if (kind === 'top' && sibling) y = Math.max(36, sibling.y - height - 20);
      return { x: zones.bottomLeft, y: Math.max(36, Math.min(y, sheetBottom - height)) };
    };
    const first = place(null);
    setProjectedViews((prev) => [
      ...prev.filter((item) => !isOwn(item)),
      {
        id: placeholderId,
        x: first.x,
        y: first.y,
        scene: {
          width: 360,
          height: 40,
          title: `Строю ${labels[kind].toLowerCase()}…`,
          polylines: [],
          shapes: [],
          edges: [],
          dims: [],
        },
      },
    ]);
    const drawing = {
      cylinders,
      fittings,
      welds,
      metadata: canvasMetadata,
      dimensionLines,
    };
    (async () => {
      try {
        const projected = await projectTopView(drawing, kind);
        if (job !== viewJobsRef.current[kind]) return;
        const scene = layoutProjection(projected.polylines, projected.groups, kind);
        if (!scene) {
          setProjectedViews((prev) => prev.filter((item) => item.id !== placeholderId));
          window.alert(`3D-модель не дала линий: ${labels[kind].toLowerCase()}.`);
          return;
        }
        scene.title = labels[kind];
        scene.viewKind = kind;
        const spot = place(scene);
        setProjectedViews((prev) => prev.map((item) => (
          item.id === placeholderId ? { ...item, x: spot.x, y: spot.y, scene } : item
        )));
      } catch (error) {
        if (job === viewJobsRef.current[kind]) {
          setProjectedViews((prev) => prev.filter((item) => item.id !== placeholderId));
          window.alert(error?.message || 'Не удалось построить вид');
        }
      }
    })();
    return undefined;
  }, [addViewRequest]);

  // Триггер применения линейного массива
  useEffect(() => {
    if (onApplyLinearPatternTrigger && linearPatternParams) {
      try {
        const { count = 1, dx = 0, dy = 0 } = linearPatternParams || {};
        if (count > 0) {
          saveState();
          const idsSet = new Set(selectedPatternObjects.map((o) => `${o.type}:${o.id}`));
          // Если ничего не выбрано через Ctrl, используем одиночный текущий выбор
          const fallbackItem = (() => {
            if (!selectedId) return null;
            const isCyl = cylinders.some((c) => c.id === selectedId);
            const isFit = fittings.some((f) => f.id === selectedId);
            if (isCyl) return { type: 'cylinder', id: selectedId };
            if (isFit) return { type: 'fitting', id: selectedId };
            return null;
          })();

          // Включаем клоны предыдущих паттернов, если они попали в выбор
          const expandWithClones = (arr) => {
            const result = [];
            const pushUnique = (type, id) => {
              const k = `${type}:${id}`;
              if (!result.some((x) => `${x.type}:${x.id}` === k)) result.push({ type, id });
            };
            arr.forEach((it) => {
              pushUnique(it.type, it.id);
              // ИСПРАВЛЕНИЕ: Убираем автоматическое добавление дочерних клонов.
              // Пользователь должен явно выбирать объекты для массива.
              // Если нужно создать массив от клона, в паттерн должен попасть только этот клон,
              // а не все его дочерние клоны (которые были созданы от него ранее).
              // 
              // Старая логика автоматически включала ВСЕ дочерние клоны:
              // if (it.type === 'cylinder') {
              //   const base = cylinders.find((c) => c.id === it.id);
              //   const clones = cylinders.filter((c) => c.isPatternClone && (c.baseId === it.id || c.patternId === base?.patternId));
              //   clones.forEach((c) => pushUnique('cylinder', c.id));
              // } else if (it.type === 'fitting') {
              //   const base = fittings.find((f) => f.id === it.id);
              //   const clones = fittings.filter((f) => f.isPatternClone && (f.baseId === it.id || f.patternId === base?.patternId));
              //   clones.forEach((f) => pushUnique('fitting', f.id));
              // }
            });
            return result;
          };

          const rawItems = idsSet.size > 0
            ? selectedPatternObjects.map((o) => ({ ...o }))
            : (fallbackItem ? [fallbackItem] : []);
          const items = expandWithClones(rawItems);

          if (items.length > 0) {
            const pattern = {
              id: 'pattern-' + Date.now(),
              items,
              count,
              dx,
              dy,
            };
            setPatterns((prev) => [...prev, pattern]);
            // Строим клоны на сцене
            rebuildClonesForPattern(pattern);
            // Откроем панель редактирования сразу
            setSelectedPatternId(pattern.id);
            setPatternEditCount(pattern.count || 1);
            setPatternEditDx(pattern.dx || 0);
            setPatternEditDy(pattern.dy || 0);
          }
        }
      } finally {
        if (typeof resetApplyLinearPatternTrigger === 'function') {
          resetApplyLinearPatternTrigger();
        }
      }
    }
  }, [onApplyLinearPatternTrigger, linearPatternParams, resetApplyLinearPatternTrigger, selectedPatternObjects, selectedId, cylinders, fittings, saveState, rebuildClonesForPattern]);

  const snappingModeRef = useRef(snappingMode);
  
  useEffect(() => {
    snappingModeRef.current = snappingMode;
  }, [snappingMode]);

  // режим добавления текста будет обрабатываться кликом по сцене (см. onMouseDown)

  // Режим «Позиция» управляется кликом; дополнительных вспомогательных состояний не нужно

  // Добавьте новый useEffect для отслеживания изменений в cylinders  
  // Вызываем через ref, чтобы избежать циклических зависимостей
  useEffect(() => {
    if (updateAllDimensionLinesRef.current) {
      updateAllDimensionLinesRef.current();
    }
  }, [cylinders, fittings]);  // Зависим от cylinders/fittings, но вызываем через ref
  
  // Добавьте обработчик события клавиатуры:
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Проверяем, не находится ли фокус на элементе ввода
      const target = e.target;
      const activeEl = document.activeElement;
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.isContentEditable ||
                          (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA'));
      
      // Если фокус на поле ввода, не обрабатываем Delete для объектов Canvas
      if (isInputField) {
        return;
      }

      // Ctrl+Z — отмена, Ctrl+Y / Ctrl+Shift+Z — повтор
      if (e.ctrlKey && (e.code === 'KeyZ' || e.key === 'z' || e.key === 'я')) {
        if (e.shiftKey) {
          e.preventDefault();
          redo();
        } else {
          e.preventDefault();
          undo();
        }
        return;
      }
      if (e.ctrlKey && (e.code === 'KeyY' || e.key === 'y' || e.key === 'н')) {
        e.preventDefault();
        redo();
        return;
      }

      if (e.key === 'Delete') {
        if (selectedProjectedViewId) {
          setProjectedViews((prev) => prev.filter((view) => view.id !== selectedProjectedViewId));
          setSelectedProjectedViewId(null);
          return;
        }
        if (isFastenersTableSelected) {
          saveState();
          setFastenersTableState(null);
          setIsFastenersTableSelected(false);
        } else if (isWorkshopTableSelected) {
          saveState();
          setWorkshopTableState(null);
          setIsWorkshopTableSelected(false);
        } else if (selectedBalloonTarget) {
          saveState();
          setBalloons((prev) => prev.map((b) => {
            if (b.id !== selectedBalloonTarget.balloonId) return b;
            return {
              ...b,
              targets: (b.targets || []).filter((_, idx) => idx !== selectedBalloonTarget.targetIndex),
            };
          }));
          setSelectedBalloonTarget(null);
        } else if (selectedBalloonId) {
          saveState();
          setBalloons((prev) => prev.filter((b) => b.id !== selectedBalloonId));
          setSelectedBalloonId(null);
        } else if (selectedFittingId !== null) {
          saveState(); // Сохраняем состояние перед удалением
          setFittings((prevFittings) =>
            prevFittings.filter((f) => f.id !== selectedFittingId)
          );
          setSelectedFittingId(null);
        } else if (selectedDimensionId !== null) {
          saveState(); // Сохраняем состояние перед удалением
          setDimensionLines((prevDimensions) =>
            prevDimensions.filter((_, index) => index !== selectedDimensionId)
          );
          setSelectedDimensionId(null);
        } else if (selectedAnnotationId) {
          saveState();
          setAnnotations((prev) => prev.filter((a) => a.id !== selectedAnnotationId));
          setSelectedAnnotationId(null);
        } else if (selectedCylinderId) {
          saveState(); // Сохраняем состояние перед удалением
          setCylinders((prevCylinders) =>
            prevCylinders.filter((c) => c.id !== selectedCylinderId)
          );
          setSelectedCylinderId(null);
        }
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedFittingId, selectedCylinderId, selectedDimensionId, selectedBalloonId, selectedBalloonTarget, selectedAnnotationId, selectedProjectedViewId, isFastenersTableSelected, isWorkshopTableSelected, saveState, undo, redo]);

  // Обработчик копирования и вставки (Ctrl+C, Ctrl+V)
  useEffect(() => {
    const handleCopy = () => {
      if (selectedFittingId !== null) {
        const fittingToCopy = fittingsRef.current.find(f => f.id === selectedFittingId);
        if (fittingToCopy) {
          clipboardRef.current = { type: 'fitting', data: { ...fittingToCopy } };
        }
      } else if (selectedCylinderId !== null) {
        const cylinderToCopy = cylindersRef.current.find(c => c.id === selectedCylinderId);
        if (cylinderToCopy) {
          clipboardRef.current = { type: 'cylinder', data: { ...cylinderToCopy } };
        }
      } else if (selectedAnnotationId !== null) {
        const annotationToCopy = annotations.find(a => a.id === selectedAnnotationId);
        if (annotationToCopy) {
          clipboardRef.current = { type: 'annotation', data: { ...annotationToCopy } };
        }
      } else if (selectedBalloonId !== null) {
        const balloonToCopy = balloons.find(b => b.id === selectedBalloonId);
        if (balloonToCopy) {
          clipboardRef.current = { type: 'balloon', data: { ...balloonToCopy } };
        }
      }
    };

    const handlePaste = () => {
      const clipboard = clipboardRef.current;
      if (clipboard) {
        saveState(); // Сохраняем состояние перед вставкой
        
        if (clipboard.type === 'fitting') {
          // Если у объекта есть groupX/groupY (был повернут), используем их как базу для позиции
          // Иначе используем x/y
          const hasGroupCoords = clipboard.data.groupX !== undefined || clipboard.data.groupY !== undefined;
          
          const newFitting = {
            ...clipboard.data,
            id: `fitting_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            // Убираем свойство клона массива - это теперь независимый объект
            isPatternClone: false,
            basePatternId: undefined,
            locked: false, // Убираем блокировку, если была
            // Если объект был повернут (есть groupX/groupY):
            // - groupX/groupY устанавливаем на место клика
            // - x и y обнуляем, т.к. позиция теперь управляется через Group
            // Если объект не повернут, просто ставим x/y в место клика
            ...(hasGroupCoords ? {
              x: 0,
              y: 0,
              groupX: lastClickPosition.x,
              groupY: lastClickPosition.y,
            } : {
              x: lastClickPosition.x,
              y: lastClickPosition.y,
              groupX: undefined,
              groupY: undefined,
            })
          };
          
          setFittings(prev => [...prev, newFitting]);
          setSelectedFittingId(newFitting.id);
          setSelectedCylinderId(null);
          setSelectedAnnotationId(null);
          setSelectedBalloonId(null);
        } else if (clipboard.type === 'cylinder') {
          const hasGroupCoords = clipboard.data.groupX !== undefined || clipboard.data.groupY !== undefined;
          
          const newCylinder = {
            ...clipboard.data,
            id: `cylinder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            // Убираем свойство клона массива - это теперь независимый объект
            isPatternClone: false,
            basePatternId: undefined,
            locked: false, // Убираем блокировку, если была
            ...(hasGroupCoords ? {
              x: 0,
              y: 0,
              groupX: lastClickPosition.x,
              groupY: lastClickPosition.y,
            } : {
              x: lastClickPosition.x,
              y: lastClickPosition.y,
              groupX: undefined,
              groupY: undefined,
            })
          };
          
          setCylinders(prev => [...prev, newCylinder]);
          setSelectedCylinderId(newCylinder.id);
          setSelectedFittingId(null);
          setSelectedAnnotationId(null);
          setSelectedBalloonId(null);
        } else if (clipboard.type === 'annotation') {
          const newAnnotation = {
            ...clipboard.data,
            id: `annotation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            x: lastClickPosition.x,
            y: lastClickPosition.y,
            rotation: clipboard.data.rotation,
          };
          setAnnotations(prev => [...prev, newAnnotation]);
          setSelectedAnnotationId(newAnnotation.id);
          setSelectedFittingId(null);
          setSelectedCylinderId(null);
          setSelectedBalloonId(null);
        } else if (clipboard.type === 'balloon') {
          const offsetX = lastClickPosition.x - clipboard.data.x;
          const offsetY = lastClickPosition.y - clipboard.data.y;
          const newBalloon = {
            ...clipboard.data,
            id: `balloon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            x: lastClickPosition.x,
            y: lastClickPosition.y,
            // Копируем targets с учетом смещения
            targets: (clipboard.data.targets || []).map(t => ({
              ...t,
              x: t.x + offsetX,
              y: t.y + offsetY,
            })),
          };
          setBalloons(prev => [...prev, newBalloon]);
          setSelectedBalloonId(newBalloon.id);
          setSelectedFittingId(null);
          setSelectedCylinderId(null);
          setSelectedAnnotationId(null);
        }
      }
    };

    const handleKeyDown = (e) => {
      // Проверяем, не находится ли фокус на элементе ввода
      const target = e.target;
      const activeEl = document.activeElement;
      
      // Проверяем как target, так и activeElement
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.isContentEditable ||
                          target.closest('input') !== null ||
                          target.closest('textarea') !== null ||
                          (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA'));
      
      // Ctrl+C - копирование (используем e.code вместо e.key для поддержки разных раскладок)
      if (e.ctrlKey && (e.code === 'KeyC' || e.key === 'c' || e.key === 'с')) {
        // Если фокус на поле ввода, не перехватываем
        if (isInputField) {
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        handleCopy();
      }
      
      // Ctrl+V - вставка (используем e.code вместо e.key для поддержки разных раскладок)
      if (e.ctrlKey && (e.code === 'KeyV' || e.key === 'v' || e.key === 'м')) {
        // Если фокус на поле ввода, не перехватываем
        if (isInputField) {
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        handlePaste();
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedFittingId, selectedCylinderId, selectedAnnotationId, selectedBalloonId, lastClickPosition, annotations, balloons, saveState]);   

  // Обработчик колесика мыши для управления масштабом
  const handleWheel = useCallback((e) => {
    // Предотвращаем стандартное поведение прокрутки
    e.evt.preventDefault();
    
    const delta = e.evt.deltaY;
    const scaleFactor = 0.05; // Шаг изменения масштаба
    
    // Определяем направление (вверх = увеличение, вниз = уменьшение)
    const direction = delta > 0 ? -1 : 1;
    
    if (isMKeyPressed) {
      // M (Ь) + колесико = общий масштаб
      setZoom(prevZoom => {
        const newZoom = Math.max(0.1, Math.min(5, prevZoom + direction * scaleFactor));
        return newZoom;
      });
    } else if (isCommaKeyPressed) {
      // Б (,) + колесико = масштаб текста размеров
      setTextZoom(prevZoom => {
        const newZoom = Math.max(0.1, Math.min(5, prevZoom + direction * scaleFactor));
        return newZoom;
      });
    } else if (isDotKeyPressed) {
      // Ю (.) + колесико = масштаб текста позиции
      setPositionTextZoom(prevZoom => {
        const newZoom = Math.max(0.1, Math.min(5, prevZoom + direction * scaleFactor));
        return newZoom;
      });
    }
  }, [isMKeyPressed, isCommaKeyPressed, isDotKeyPressed, setZoom, setTextZoom, setPositionTextZoom]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Проверяем, не находится ли фокус на элементе ввода
      const target = e.target;
      const activeEl = document.activeElement;
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.isContentEditable ||
                          target.closest('input') !== null ||
                          target.closest('textarea') !== null ||
                          (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA'));
      
      // Если фокус на поле ввода, не обрабатываем горячие клавиши
      if (isInputField) {
        return;
      }
      
      if (e.code === 'KeyL') {
        setIsLKeyPressed(true);
      }
      if (e.code === 'KeyP' && !e.repeat) {
        // e.repeat === true когда клавиша зажата, игнорируем такие события
        setPAttachmentMode(prev => {
          let nextMode;
          if (prev === 'off') {
            nextMode = 'points';
          } else if (prev === 'points') {
            nextMode = 'edges';
          } else {
            nextMode = 'off';
            setPointAttachmentMode(null); // Сбрасываем режим при выключении
            // Восстанавливаем размерные линии
            if (setIsDimensionLineEnabled && dimensionLineStateBeforePKeyRef.current !== null) {
              const previousState = dimensionLineStateBeforePKeyRef.current;
              dimensionLineStateBeforePKeyRef.current = null;
              if (previousState) {
                setIsDimensionLineEnabled(true);
              }
            }
            // Восстанавливаем выделение
            if (selectedElementsBeforePKeyRef.current !== null) {
              const previousSelection = selectedElementsBeforePKeyRef.current;
              selectedElementsBeforePKeyRef.current = null;
              if (previousSelection.length > 0) {
                setSelectedElements(previousSelection);
              }
            }
            return nextMode;
          }
          
          // При включении режима (points или edges) сохраняем и отключаем размерные линии
          if (setIsDimensionLineEnabled && dimensionLineStateBeforePKeyRef.current === null) {
            dimensionLineStateBeforePKeyRef.current = isDimensionLineEnabled;
            if (isDimensionLineEnabled) {
              setIsDimensionLineEnabled(false);
            }
          }
          // Сохраняем и очищаем выделение
          if (selectedElementsBeforePKeyRef.current === null) {
            selectedElementsBeforePKeyRef.current = selectedElements.length > 0 ? [...selectedElements] : [];
            if (selectedElements.length > 0) {
              setSelectedElements([]);
            }
          }
          
          return nextMode;
        });
      }
      if (e.key === 'Control') {
        setIsCtrlPressed(true);
      }
      // Обработка клавиш для управления масштабом
      if (e.code === 'KeyM') {
        setIsMKeyPressed(true);
      }
      if (e.code === 'Comma' || e.key === 'б' || e.key === 'Б') {
        setIsCommaKeyPressed(true);
      }
      if (e.code === 'Period' || e.key === 'ю' || e.key === 'Ю') {
        setIsDotKeyPressed(true);
      }
    };
  
    const handleKeyUp = (e) => {
      // Проверяем, не находится ли фокус на элементе ввода
      const target = e.target;
      const activeEl = document.activeElement;
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.isContentEditable ||
                          target.closest('input') !== null ||
                          target.closest('textarea') !== null ||
                          (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA'));
      
      // Если фокус на поле ввода, не обрабатываем
      if (isInputField) {
        return;
      }
      
      if (e.code === 'KeyL') {
        setIsLKeyPressed(false);
      }
      // KeyP больше не обрабатывается в keyup - переключение происходит только по нажатию
      if (e.key === 'Control') {
        setIsCtrlPressed(false);
      }
      // Обработка клавиш для управления масштабом
      if (e.code === 'KeyM') {
        setIsMKeyPressed(false);
      }
      if (e.code === 'Comma' || e.key === 'б' || e.key === 'Б') {
        setIsCommaKeyPressed(false);
      }
      if (e.code === 'Period' || e.key === 'ю' || e.key === 'Ю') {
        setIsDotKeyPressed(false);
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
  
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isDimensionLineEnabled, setIsDimensionLineEnabled, selectedElements]);  

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Проверяем, не находится ли фокус на элементе ввода
      const target = e.target;
      const activeEl = document.activeElement;
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.isContentEditable ||
                          target.closest('input') !== null ||
                          target.closest('textarea') !== null ||
                          (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA'));
      
      // Если фокус на поле ввода, не обрабатываем
      if (isInputField) {
        return;
      }
      
      if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }
    };
  
    const handleKeyUp = (e) => {
      // Проверяем, не находится ли фокус на элементе ввода
      const target = e.target;
      const activeEl = document.activeElement;
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.isContentEditable ||
                          target.closest('input') !== null ||
                          target.closest('textarea') !== null ||
                          (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA'));
      
      // Если фокус на поле ввода, не обрабатываем
      if (isInputField) {
        return;
      }
      
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
  
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Функция для безопасного вычисления математических выражений
  const evaluateMathExpression = (expression) => {
    try {
      // Заменяем запятые на точки
      let expr = String(expression).replace(/,/g, '.');
      
      // Удаляем все пробелы
      expr = expr.replace(/\s/g, '');
      
      // Проверяем, что выражение содержит только разрешенные символы: цифры, точки и операторы +-*/()
      if (!/^[0-9+\-*/.()]+$/.test(expr)) {
        console.warn('⚠️ Недопустимые символы в выражении:', expression);
        return NaN;
      }
      
      // Проверяем на опасные конструкции
      if (expr.includes('..') || /[+\-*/]{2,}/.test(expr)) {
        console.warn('⚠️ Некорректное выражение:', expression);
        return NaN;
      }
      
      // Безопасное вычисление через Function
      // Это безопасно, потому что выше мы валидировали, что expr содержит только цифры и математические операторы
      // eslint-disable-next-line no-new-func
      const result = new Function('return ' + expr)();
      
      return result;
    } catch (error) {
      console.warn('⚠️ Ошибка при вычислении выражения:', expression, error);
      return NaN;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDimensionChange = useCallback(
    (newValue, dimensionIndex) => {
      // ИСПРАВЛЕНИЕ: Вычисляем математическое выражение, если оно есть
      const calculatedValue = evaluateMathExpression(newValue);
      const newLength = calculatedValue;
  
      // Проверяем, является ли введенное значение числом и больше нуля
      if (isNaN(newLength) || newLength <= 0) {
        console.warn('⚠️ Некорректное значение:', newValue, '→', newLength);
        return;
      }
  
      // Создаем копии массивов цилиндров и фитингов из refs
      const updatedCylinders = cylindersRef.current.slice();
      const updatedFittings = fittingsRef.current.slice();
  
      // Создаем копию размерной линии, которую будем обновлять
      const updatedDimensionLine = { ...dimensionLinesRef.current[dimensionIndex] };
  
      // Получаем элементы (точки или стороны), связанные с размерной линией
      const element1 = updatedDimensionLine.elements[0];
      const element2 = updatedDimensionLine.elements[1];

      // Вспомогательные функции: определить сторону и целевую компоненту изменения
      const resolveSide = (el) => {
        if (el?.type) return el.type; // 'top'|'bottom'|'left'|'right'
        if (typeof el?.idx === 'number') return getSideFromPointIndex(el.idx);
        return null;
      };
      const side1 = resolveSide(element1);
      const side2 = resolveSide(element2);
      const affectsWidth =
        (side1 === 'left' || side1 === 'right') &&
        (side2 === 'left' || side2 === 'right');
      const affectsHeight =
        (side1 === 'top' || side1 === 'bottom') &&
        (side2 === 'top' || side2 === 'bottom');
  
      // Функция для получения индекса объекта по его идентификатору и типу
      const getObjectIndexByIdAndType = (id, type) => {
        if (type === 'cylinder') {
          return updatedCylinders.findIndex((cylinder) => cylinder.id === id);
        } else if (type === 'fitting') {
          return updatedFittings.findIndex((fitting) => fitting.id === id);
        }
        return -1;
      };
  
      // Функция для получения объекта по индексу и типу
      const getObjectByIndexAndType = (index, type) => {
        if (type === 'cylinder') {
          return updatedCylinders[index];
        } else if (type === 'fitting') {
          return updatedFittings[index];
        }
        return null;
      };
  
      // Функции для получения ширины и высоты объекта
      const getObjectWidth = (object) => {
        return object.width || getFittingWidth(object);
      };
  
      const getObjectHeight = (object) => {
        return object.height || getFittingHeight(object);
      };
  
      // Находим индексы объектов, связанных с элементами размерной линии
      const objectIndex1 = getObjectIndexByIdAndType(
        element1.objectId,
        element1.objectType
      );
      const objectIndex2 = getObjectIndexByIdAndType(
        element2.objectId,
        element2.objectType
      );
  
      // Проверяем, что оба объекта найдены
      if (objectIndex1 !== -1 && objectIndex2 !== -1) {
        // Получаем сами объекты
        const object1 = getObjectByIndexAndType(
          objectIndex1,
          element1.objectType
        );
        const object2 = getObjectByIndexAndType(
          objectIndex2,
          element2.objectType
        );
  
        // Проверяем, есть ли привязка через P между этими объектами
        // Проверяем наличие ЛЮБОЙ привязки между этими двумя объектами (независимо от точек)
        const constraints = objectConstraintsRef.current;
        const hasConstraint = constraints.some(c => 
          (c.obj1.objectId === object1.id && c.obj2.objectId === object2.id) ||
          (c.obj2.objectId === object1.id && c.obj1.objectId === object2.id)
        );
  
        // Проверяем, находятся ли элементы на одном и том же объекте
        const isSameObject = object1.id === object2.id && element1.objectType === element2.objectType;
        
        if (isSameObject) {
          // Оба элемента находятся на одном объекте - изменяем размер объекта
          
          // ВАЖНО: Запрещаем изменение размеров для крепежей
          if (object1.type === 'fastener') {
            console.warn('⚠️ Изменение размеров крепежей через размерные линии запрещено');
            return;
          }
          
          // Устанавливаем флаг, чтобы блокировать повторные вызовы updateConstrainedObjects
          isProcessingDimensionChangeRef.current = true;
  
          // Инициализируем новые значения координат и размеров объекта
          let newX = object1.x;
          let newY = object1.y;
          let newWidth = getObjectWidth(object1);
          let newHeight = getObjectHeight(object1);
  
          // Определяем цель изменения по сторонам (после поворота стороны остаются актуальными)
          const changeWidth = affectsWidth || (!affectsWidth && !affectsHeight && updatedDimensionLine.isHorizontal);
          const changeHeight = affectsHeight || (!affectsWidth && !affectsHeight && !updatedDimensionLine.isHorizontal);

          // Проверки на фиксированные размеры
          if (changeWidth) {
            if (object1.type === 'pipe' && object1.isWidthFixed) {
              return;
            }
            if (object1.type === 'fitting' && object1.isDiameterFixed) {
              return;
            }
          }
      if (changeHeight) {
        if (object1.type === 'fitting' && object1.isLengthAdjustable === false) {
          return;
        }
      }

          // Применяем изменения
          if (changeWidth) {
            if (side1 === 'right') {
              newX = object1.x + newWidth - newLength; // фиксируем правую сторону
            }
            // если первая сторона левая — фиксируем левую (x без изменений)
            newWidth = newLength;
          }
          if (changeHeight) {
            if (side1 === 'bottom') {
              newY = object1.y + newHeight - newLength; // фиксируем нижнюю сторону
            }
            // если первая сторона верхняя — фиксируем верхнюю (y без изменений)
            newHeight = newLength;
          }

          // Обновляем объект с учётом ограничений
          let updatedObject;
          if (object1.type === 'fitting' && object1.isDiameterFixed) {
            updatedObject = { ...object1, x: newX, y: newY, height: newHeight };
          } else if (object1.type === 'pipe' && object1.isWidthFixed) {
            updatedObject = { ...object1, y: newY, height: newHeight };
          } else {
            updatedObject = { ...object1, x: newX, y: newY, width: newWidth, height: newHeight };
          }

          // Обновляем объект в соответствующем массиве
          if (element1.objectType === 'cylinder') {
            updatedCylinders[objectIndex1] = updatedObject;
          } else if (element1.objectType === 'fitting') {
            updatedFittings[objectIndex1] = updatedObject;
          }

          // Функция для получения фактических координат точки с учётом snapPoints
          const getActualPointCoordinate = (obj, side) => {
            const objX = obj.x;
            const objY = obj.y;
            const objWidth = obj.width;
            const objHeight = obj.height;
            
            // Определяем индекс точки по стороне
            let pointIdx = null;
            if (side === 'top') pointIdx = 4;
            else if (side === 'bottom') pointIdx = 5;
            else if (side === 'left') pointIdx = 6;
            else if (side === 'right') pointIdx = 7;
            
            // Если есть кастомные snapPoints, используем их
            if (obj.snapPoints && Array.isArray(obj.snapPoints) && pointIdx !== null && obj.snapPoints[pointIdx]) {
              const point = obj.snapPoints[pointIdx];
              
              if (point.absolute) {
                // Абсолютные координаты в мм
                return {
                  x: objX + (point.x || 0),
                  y: objY + (point.y || 0)
                };
              } else if (point.relative) {
                // Относительные координаты
                return {
                  x: objX + objWidth * point.x,
                  y: objY + objHeight * point.y
                };
              }
            }
            
            // Стандартный расчёт по умолчанию
            if (side === 'top') return { x: objX + objWidth / 2, y: objY };
            if (side === 'bottom') return { x: objX + objWidth / 2, y: objY + objHeight };
            if (side === 'left') return { x: objX, y: objY + objHeight / 2 };
            if (side === 'right') return { x: objX + objWidth, y: objY + objHeight / 2 };
            
            return { x: objX, y: objY };
          };
          
          // Обновляем координаты размерной линии с учётом snapPoints
          if (changeWidth) {
            const point1 = getActualPointCoordinate(updatedObject, side1);
            const point2 = getActualPointCoordinate(updatedObject, side2);
            updatedDimensionLine.x1 = point1.x;
            updatedDimensionLine.x2 = point2.x;
          }
          if (changeHeight) {
            const point1 = getActualPointCoordinate(updatedObject, side1);
            const point2 = getActualPointCoordinate(updatedObject, side2);
            updatedDimensionLine.y1 = point1.y;
            updatedDimensionLine.y2 = point2.y;
          }

          // Обновляем значение размерной линии
          updatedDimensionLine.rawValue = newLength;
          updatedDimensionLine.value = dimensionMillimeters(newLength);

          // Обновляем состояние размерных линий
          setDimensionLines((prevDimensions) => {
            const newDimensions = [...prevDimensions];
            newDimensions[dimensionIndex] = updatedDimensionLine;
            return newDimensions;
          });

          // ИСПРАВЛЕНИЕ: Вычисляем старые позиции точек ДО обновления размера объекта
          // Используем чистые данные, без обращения к DOM (т.к. React обновляет DOM асинхронно)
          const oldPointPositions = new Map();
          
          
          // Вычисляем старые позиции всех точек (в координатах объекта, без трансформации)
          let oldPoints = [];
          if (element1.objectType === 'fitting') {
            oldPoints = getFittingPoints({
              x: object1.x || 0,
              y: object1.y || 0,
              width: getObjectWidth(object1),
              height: getObjectHeight(object1),
              snapPoints: object1.snapPoints,
            });
          } else if (element1.objectType === 'cylinder') {
            oldPoints = getCylinderPoints({
              x: object1.x || 0,
              y: object1.y || 0,
              width: object1.width,
              height: object1.height,
            });
          }
          
          oldPoints.forEach((point, idx) => {
            oldPointPositions.set(idx, { x: point.x, y: point.y });
          });

          // Обновляем состояния цилиндров и фитингов
          setCylinders(updatedCylinders);
          setFittings(updatedFittings);

          // Обновляем refs сразу, чтобы updateConstrainedObjects видел актуальные данные
          cylindersRef.current = updatedCylinders;
          fittingsRef.current = updatedFittings;

          // ИСПРАВЛЕНИЕ: Вычисляем новые позиции точек ПОСЛЕ обновления и применяем смещение к привязанным объектам
          if (oldPointPositions.size > 0) {
            // Вычисляем новые позиции точек (в координатах объекта, без трансформации)
            let newPoints = [];
            if (element1.objectType === 'fitting') {
              newPoints = getFittingPoints({
                x: updatedObject.x || 0,
                y: updatedObject.y || 0,
                width: getObjectWidth(updatedObject),
                height: getObjectHeight(updatedObject),
                snapPoints: updatedObject.snapPoints,
              });
            } else if (element1.objectType === 'cylinder') {
              newPoints = getCylinderPoints({
                x: updatedObject.x || 0,
                y: updatedObject.y || 0,
                width: updatedObject.width,
                height: updatedObject.height,
              });
            }
            
              
            // Собираем все изменения для всех привязанных объектов
            const fittingUpdates = new Map(); // id -> {dx, dy, pointIndex}
            const cylinderUpdates = new Map(); // id -> {dx, dy, pointIndex}
            
            // Для каждой привязанной точки вычисляем смещение
            const constraints = objectConstraintsRef.current;
            
            constraints.forEach(constraint => {
              let sourcePointIndex = null;
              let targetObjectId = null;
              let targetObjectType = null;
              let targetPointIndex = null;
              
              // Определяем, является ли измененный объект источником привязки
              if (constraint.obj1.objectId === updatedObject.id && constraint.obj1.objectType === element1.objectType) {
                sourcePointIndex = constraint.obj1.pointIndex;
                targetObjectId = constraint.obj2.objectId;
                targetObjectType = constraint.obj2.objectType;
                targetPointIndex = constraint.obj2.pointIndex;
              } else if (constraint.obj2.objectId === updatedObject.id && constraint.obj2.objectType === element1.objectType) {
                sourcePointIndex = constraint.obj2.pointIndex;
                targetObjectId = constraint.obj1.objectId;
                targetObjectType = constraint.obj1.objectType;
                targetPointIndex = constraint.obj1.pointIndex;
              }
              
              if (sourcePointIndex === null) {
                return;
              }
              
              
              // Получаем старую и новую позиции точки
              const oldPos = oldPointPositions.get(sourcePointIndex);
              const newPos = newPoints[sourcePointIndex];
              
              if (!oldPos || !newPos) {
                return;
              }
              
              // Вычисляем смещение в координатах объектов (без zoom, т.к. координаты уже в "логических" единицах)
              const dx = newPos.x - oldPos.x;
              const dy = newPos.y - oldPos.y;
              
              // Игнорируем микросмещения
              if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
                return;
              }
              
              // Сохраняем изменения в Map
              if (targetObjectType === 'fitting') {
                fittingUpdates.set(targetObjectId, { dx, dy, pointIndex: targetPointIndex });
              } else if (targetObjectType === 'cylinder') {
                cylinderUpdates.set(targetObjectId, { dx, dy, pointIndex: targetPointIndex });
              }
            });
            
            
            // ВАЖНО: Применяем изменения к привязанным объектам через setTimeout,
            // чтобы они выполнились ПОСЛЕ того, как React применит изменения размера трубы
            setTimeout(() => {
              // Применяем все изменения ОДНИМ батчем для фитингов
              if (fittingUpdates.size > 0) {
                setFittings(prev => {
                  const updated = prev.map(f => {
                    const update = fittingUpdates.get(f.id);
                    if (update) {
                      
                      let result;
                      if (f.groupX !== undefined || f.groupY !== undefined) {
                        result = { ...f, groupX: (f.groupX || 0) + update.dx, groupY: (f.groupY || 0) + update.dy };
                      } else {
                        result = { ...f, x: f.x + update.dx, y: f.y + update.dy };
                      }
                      
                      return result;
                    }
                    return f;
                  });
                  fittingsRef.current = updated;
                  return updated;
                });
              }
              
              // Применяем все изменения ОДНИМ батчем для цилиндров
              if (cylinderUpdates.size > 0) {
                setCylinders(prev => {
                  const updated = prev.map(c => {
                    const update = cylinderUpdates.get(c.id);
                    if (update) {
                      
                      // Проверяем, есть ли прямая привязка через P
                      const hasDirectConstraint = objectConstraintsRef.current.some(cnst => 
                        (cnst.obj1.objectId === updatedObject.id && cnst.obj2.objectId === c.id) ||
                        (cnst.obj2.objectId === updatedObject.id && cnst.obj1.objectId === c.id)
                      );
                      
                      if (hasDirectConstraint) {
                        // Для цилиндров (труб) проверяем, привязана ли точка на конце
                        const isEndPoint = [4, 5, 6, 7].includes(update.pointIndex);
                        
                        if (isEndPoint) {
                          // Если привязана точка на конце трубы, изменяем размер трубы
                          const isVertical = c.height > c.width;
                          const isHorizontal = c.width > c.height;
                          
                          if (isVertical && (update.pointIndex === 5)) {
                            return { ...c, height: c.height + update.dy };
                          } else if (isVertical && (update.pointIndex === 4)) {
                            return { ...c, y: c.y + update.dy, height: c.height - update.dy };
                          } else if (isHorizontal && (update.pointIndex === 7)) {
                            return { ...c, width: c.width + update.dx };
                          } else if (isHorizontal && (update.pointIndex === 6)) {
                            return { ...c, x: c.x + update.dx, width: c.width - update.dx };
                          }
                        }
                      }
                      
                      // Для других точек или если нет привязки - перемещаем как обычно
                      if (c.groupX !== undefined || c.groupY !== undefined) {
                        return { ...c, groupX: (c.groupX || 0) + update.dx, groupY: (c.groupY || 0) + update.dy };
                      } else {
                        return { ...c, x: c.x + update.dx, y: c.y + update.dy };
                      }
                    }
                    return c;
                  });
                  cylindersRef.current = updated;
                  return updated;
                });
              }
              
              // ИСПРАВЛЕНИЕ: Пересобираем клоны ПОСЛЕ обновления всех привязанных объектов
              if (!updatedObject?.isPatternClone) {
                rebuildClonesForBase(element1.objectType, updatedObject.id);
              }
            }, 0);
          }

          // Сохраняем состояние после изменения
          saveState();
          
          // Сбрасываем флаг после завершения всех обновлений
          setTimeout(() => {
            isProcessingDimensionChangeRef.current = false;
          }, 100);
          } else {
          // Элементы находятся на разных объектах - перемещаем объекты относительно друг друга
          
          // ВАЖНО: Запрещаем изменение расстояний для крепежей
          if (object1.type === 'fastener' || object2.type === 'fastener') {
            console.warn('⚠️ Изменение расстояния с участием крепежей через размерные линии запрещено');
            return;
          }
  
          // Проверяем, зафиксированы ли объекты
          const isObject1Locked = object1.locked || false;
          const isObject2Locked = object2.locked || false;

          // Преобразование мировых смещений в локальные координаты группы (учёт поворота)
          const getGroupByObjectId = (id) => {
            const stage = stageRef.current;
            if (!stage) return null;
            let node = stage.findOne(`#${id}`);
            if (!node) return null;
            return node.getClassName && node.getClassName() === 'Group' ? node : node.getParent();
          };
          const toLocalDelta = (group, dxStage, dyStage) => {
            if (!group || !group.getAbsoluteTransform) {
              return { dxLocal: dxStage, dyLocal: dyStage };
            }
            const absT = group.getAbsoluteTransform();
            const invT = absT.copy().invert();
            const p0 = invT.point({ x: 0, y: 0 });
            const p1 = invT.point({ x: dxStage, y: dyStage });
            return { dxLocal: p1.x - p0.x, dyLocal: p1.y - p0.y };
          };
  
          // Если есть привязка, двигаем только второй объект (не делим delta пополам)
          if (hasConstraint && !isObject1Locked && !isObject2Locked) {
  
            if (updatedDimensionLine.isHorizontal) {
              // Для горизонтальной размерной линии
  
              // Вычисляем текущее расстояние между объектами (в "пользовательских" координатах, деленных на zoom)
              const currentDistance =
                updatedDimensionLine.x2 - updatedDimensionLine.x1;
              // Вычисляем разницу между желаемой и текущей длиной (в "пользовательских" координатах)
              const deltaUser = newLength - Math.abs(currentDistance);
              // ВАЖНО: Умножаем на effectiveZoom, чтобы получить delta в реальных Stage координатах!
              const delta = deltaUser * (effectiveZoom || 1);
              // Определяем направление перемещения
              const direction = currentDistance >= 0 ? 1 : -1;
              // Двигаем только второй объект на полный delta (не делим пополам!)
              const moveXStage = delta * direction;

              // Первый объект НЕ двигаем
              const updatedObject1 = object1;
              
              // Проверяем, повернут ли второй объект
              const isObject2Rotated = (object2.rotation && Math.abs(object2.rotation % 360) > 0.1) || 
                                       (object2.groupX !== undefined || object2.groupY !== undefined);
              
              let updatedObject2;
              if (isObject2Rotated) {
                // Для повернутых объектов groupX/groupY хранятся в логических единицах
                // Нужно разделить Stage смещение на effectiveZoom!
                const moveXLocal = moveXStage / (effectiveZoom || 1);
                updatedObject2 = {
                  ...object2,
                  groupX: (object2.groupX || 0) + moveXLocal,
                  groupY: (object2.groupY || 0), // Y не меняется для горизонтального смещения
                };
              } else {
                // Для неповернутых объектов преобразуем в локальные координаты
                const group2 = getGroupByObjectId(object2.id);
                const { dxLocal: dx2, dyLocal: dy2 } = toLocalDelta(group2, moveXStage, 0);
                updatedObject2 = {
                  ...object2,
                  x: object2.x + dx2,
                  y: object2.y + dy2,
                };
              }
  
              // Обновляем объекты в соответствующих массивах
              if (element1.objectType === 'cylinder') {
                updatedCylinders[objectIndex1] = updatedObject1;
              } else if (element1.objectType === 'fitting') {
                updatedFittings[objectIndex1] = updatedObject1;
              }
  
              if (element2.objectType === 'cylinder') {
                updatedCylinders[objectIndex2] = updatedObject2;
              } else if (element2.objectType === 'fitting') {
                updatedFittings[objectIndex2] = updatedObject2;
              }
  
              // Координаты размерной линии пересчитаются в updateAllDimensionLines
            } else {
              // Для вертикальной размерной линии
  
              const currentDistance =
                updatedDimensionLine.y2 - updatedDimensionLine.y1;
              const deltaUser = newLength - Math.abs(currentDistance);
              // ВАЖНО: Умножаем на effectiveZoom, чтобы получить delta в реальных Stage координатах!
              const delta = deltaUser * (effectiveZoom || 1);
              const direction = currentDistance >= 0 ? 1 : -1;
              // Двигаем только второй объект на полный delta (не делим пополам!)
              const moveYStage = delta * direction;

              // Первый объект НЕ двигаем
              const updatedObject1 = object1;
              
              // Проверяем, повернут ли второй объект
              const isObject2Rotated = (object2.rotation && Math.abs(object2.rotation % 360) > 0.1) || 
                                       (object2.groupX !== undefined || object2.groupY !== undefined);
              
              let updatedObject2;
              if (isObject2Rotated) {
                // Для повернутых объектов groupX/groupY хранятся в логических единицах
                // Нужно разделить Stage смещение на effectiveZoom!
                const moveYLocal = moveYStage / (effectiveZoom || 1);
                updatedObject2 = {
                  ...object2,
                  groupX: (object2.groupX || 0), // X не меняется для вертикального смещения
                  groupY: (object2.groupY || 0) + moveYLocal,
                };
              } else {
                // Для неповернутых объектов преобразуем в локальные координаты
                const group2 = getGroupByObjectId(object2.id);
                const { dxLocal: dx2, dyLocal: dy2 } = toLocalDelta(group2, 0, moveYStage);
                updatedObject2 = {
                  ...object2,
                  x: object2.x + dx2,
                  y: object2.y + dy2,
                };
              }
  
              if (element1.objectType === 'cylinder') {
                updatedCylinders[objectIndex1] = updatedObject1;
              } else if (element1.objectType === 'fitting') {
                updatedFittings[objectIndex1] = updatedObject1;
              }
  
              if (element2.objectType === 'cylinder') {
                updatedCylinders[objectIndex2] = updatedObject2;
              } else if (element2.objectType === 'fitting') {
                updatedFittings[objectIndex2] = updatedObject2;
              }
  
              // Координаты размерной линии пересчитаются в updateAllDimensionLines
            }
          } else if (!hasConstraint && !isObject1Locked && !isObject2Locked) {
            // Оба объекта не зафиксированы И нет привязки - перемещаем оба объекта на половину delta

            if (updatedDimensionLine.isHorizontal) {
              // Для горизонтальной размерной линии
  
              const currentDistance = updatedDimensionLine.x2 - updatedDimensionLine.x1;
              const deltaUser = newLength - Math.abs(currentDistance);
              // ВАЖНО: Умножаем на effectiveZoom, чтобы получить delta в реальных Stage координатах!
              const delta = deltaUser * (effectiveZoom || 1);
              const direction = currentDistance >= 0 ? 1 : -1;
              // Делим delta пополам для обоих объектов
              const moveXStage = (delta / 2) * direction;
              const group1 = getGroupByObjectId(object1.id);
              const group2 = getGroupByObjectId(object2.id);
              const { dxLocal: dx1, dyLocal: dy1 } = toLocalDelta(group1, -moveXStage, 0);
              const { dxLocal: dx2, dyLocal: dy2 } = toLocalDelta(group2, +moveXStage, 0);

              const updatedObject1 = {
                ...object1,
                x: object1.x + dx1,
                y: object1.y + dy1,
              };
              const updatedObject2 = {
                ...object2,
                x: object2.x + dx2,
                y: object2.y + dy2,
              };
  
              if (element1.objectType === 'cylinder') {
                updatedCylinders[objectIndex1] = updatedObject1;
              } else if (element1.objectType === 'fitting') {
                updatedFittings[objectIndex1] = updatedObject1;
              }
  
              if (element2.objectType === 'cylinder') {
                updatedCylinders[objectIndex2] = updatedObject2;
              } else if (element2.objectType === 'fitting') {
                updatedFittings[objectIndex2] = updatedObject2;
              }
  
              // Координаты размерной линии пересчитаются в updateAllDimensionLines
            } else {
              // Для вертикальной размерной линии
  
              const currentDistance = updatedDimensionLine.y2 - updatedDimensionLine.y1;
              const deltaUser = newLength - Math.abs(currentDistance);
              // ВАЖНО: Умножаем на effectiveZoom, чтобы получить delta в реальных Stage координатах!
              const delta = deltaUser * (effectiveZoom || 1);
              const direction = currentDistance >= 0 ? 1 : -1;
              const moveYStage = (delta / 2) * direction;
              const group1 = getGroupByObjectId(object1.id);
              const group2 = getGroupByObjectId(object2.id);
              const { dxLocal: dx1, dyLocal: dy1 } = toLocalDelta(group1, 0, -moveYStage);
              const { dxLocal: dx2, dyLocal: dy2 } = toLocalDelta(group2, 0, +moveYStage);

              const updatedObject1 = {
                ...object1,
                x: object1.x + dx1,
                y: object1.y + dy1,
              };
              const updatedObject2 = {
                ...object2,
                x: object2.x + dx2,
                y: object2.y + dy2,
              };
  
              if (element1.objectType === 'cylinder') {
                updatedCylinders[objectIndex1] = updatedObject1;
              } else if (element1.objectType === 'fitting') {
                updatedFittings[objectIndex1] = updatedObject1;
              }
  
              if (element2.objectType === 'cylinder') {
                updatedCylinders[objectIndex2] = updatedObject2;
              } else if (element2.objectType === 'fitting') {
                updatedFittings[objectIndex2] = updatedObject2;
              }
  
              // Координаты размерной линии пересчитаются в updateAllDimensionLines
            }
          } else if (!isObject1Locked && isObject2Locked) {
            // Первый объект не зафиксирован, второй зафиксирован - перемещаем первый объект
  
            if (updatedDimensionLine.isHorizontal) {
              const currentDistance =
                updatedDimensionLine.x2 - updatedDimensionLine.x1;
              const deltaUser = newLength - Math.abs(currentDistance);
              // ВАЖНО: Умножаем на effectiveZoom, чтобы получить delta в реальных Stage координатах!
              const delta = deltaUser * (effectiveZoom || 1);
              const direction = currentDistance >= 0 ? 1 : -1;
              const moveXStage = delta * direction;
              const group1 = getGroupByObjectId(object1.id);
              const { dxLocal, dyLocal } = toLocalDelta(group1, -moveXStage, 0);

              const updatedObject1 = {
                ...object1,
                x: object1.x + dxLocal,
                y: object1.y + dyLocal,
              };
  
              if (element1.objectType === 'cylinder') {
                updatedCylinders[objectIndex1] = updatedObject1;
              } else if (element1.objectType === 'fitting') {
                updatedFittings[objectIndex1] = updatedObject1;
              }
              // Координаты размерной линии пересчитаются в updateAllDimensionLines
            } else {
              const currentDistance =
                updatedDimensionLine.y2 - updatedDimensionLine.y1;
              const deltaUser = newLength - Math.abs(currentDistance);
              // ВАЖНО: Умножаем на effectiveZoom, чтобы получить delta в реальных Stage координатах!
              const delta = deltaUser * (effectiveZoom || 1);
              const direction = currentDistance >= 0 ? 1 : -1;
              const moveYStage = delta * direction;
              const group1 = getGroupByObjectId(object1.id);
              const { dxLocal, dyLocal } = toLocalDelta(group1, 0, -moveYStage);

              const updatedObject1 = {
                ...object1,
                x: object1.x + dxLocal,
                y: object1.y + dyLocal,
              };
  
              if (element1.objectType === 'cylinder') {
                updatedCylinders[objectIndex1] = updatedObject1;
              } else if (element1.objectType === 'fitting') {
                updatedFittings[objectIndex1] = updatedObject1;
              }
              // Координаты размерной линии пересчитаются в updateAllDimensionLines
            }
          } else if (isObject1Locked && !isObject2Locked) {
            // Первый объект зафиксирован, второй нет - перемещаем второй объект
  
            if (updatedDimensionLine.isHorizontal) {
              const currentDistance =
                updatedDimensionLine.x2 - updatedDimensionLine.x1;
              const deltaUser = newLength - Math.abs(currentDistance);
              // ВАЖНО: Умножаем на effectiveZoom, чтобы получить delta в реальных Stage координатах!
              const delta = deltaUser * (effectiveZoom || 1);
              const direction = currentDistance >= 0 ? 1 : -1;
              const moveXStage = delta * direction;
              const group2 = getGroupByObjectId(object2.id);
              const { dxLocal, dyLocal } = toLocalDelta(group2, +moveXStage, 0);

              const updatedObject2 = {
                ...object2,
                x: object2.x + dxLocal,
                y: object2.y + dyLocal,
              };
  
              if (element2.objectType === 'cylinder') {
                updatedCylinders[objectIndex2] = updatedObject2;
              } else if (element2.objectType === 'fitting') {
                updatedFittings[objectIndex2] = updatedObject2;
              }
              // Координаты размерной линии пересчитаются в updateAllDimensionLines
            } else {
              const currentDistance =
                updatedDimensionLine.y2 - updatedDimensionLine.y1;
              const deltaUser = newLength - Math.abs(currentDistance);
              // ВАЖНО: Умножаем на effectiveZoom, чтобы получить delta в реальных Stage координатах!
              const delta = deltaUser * (effectiveZoom || 1);
              const direction = currentDistance >= 0 ? 1 : -1;
              const moveYStage = delta * direction;
              const group2 = getGroupByObjectId(object2.id);
              const { dxLocal, dyLocal } = toLocalDelta(group2, 0, +moveYStage);

              const updatedObject2 = {
                ...object2,
                x: object2.x + dxLocal,
                y: object2.y + dyLocal,
              };
  
              if (element2.objectType === 'cylinder') {
                updatedCylinders[objectIndex2] = updatedObject2;
              } else if (element2.objectType === 'fitting') {
                updatedFittings[objectIndex2] = updatedObject2;
              }
              // Координаты размерной линии пересчитаются в updateAllDimensionLines
            }
          } else {
            // Оба объекта зафиксированы - не можем изменить расстояние между ними
            return;
          }
        }
  
        // Обновляем значение размерной линии
        updatedDimensionLine.rawValue = newLength;
        updatedDimensionLine.value = dimensionMillimeters(newLength);
  
        // Обновляем состояние размерных линий
        setDimensionLines((prevDimensions) => {
          const newDimensions = [...prevDimensions];
          newDimensions[dimensionIndex] = updatedDimensionLine;
          return newDimensions;
        });
  
        // Обновляем состояния цилиндров и фитингов
        setCylinders(updatedCylinders);
        setFittings(updatedFittings);

        // Обновляем refs сразу, чтобы updateConstrainedObjects видел актуальные данные
        cylindersRef.current = updatedCylinders;
        fittingsRef.current = updatedFittings;

        // Обновляем привязанные объекты и пересобираем массивы ПОСЛЕ всех изменений
        setTimeout(() => {
          if (!hasConstraint) {
            // Если нет привязки между объектами - обновляем оба
            updateConstrainedObjects(object1.id, element1.objectType);
            updateConstrainedObjects(object2.id, element2.objectType);
          } else {
            // Если есть привязка - обновляем только объект, который СДВИНУЛСЯ (object2)
            // Это заставит другие объекты (например, трубу) следовать за ним
            updateConstrainedObjects(object2.id, element2.objectType);
          }

          // ВАЖНО: Пересобираем клоны ПОСЛЕ updateConstrainedObjects,
          // чтобы они создавались с уже обновленными размерами/позициями
          if (!object1?.isPatternClone) {
            rebuildClonesForBase(element1.objectType, object1.id);
          }
          if (!object2?.isPatternClone) {
            rebuildClonesForBase(element2.objectType, object2.id);
          }
        }, 0);

        // Сохраняем состояние после изменения
        saveState();
      } else {
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [saveState, getSideFromPointIndex, rebuildClonesForBase, zoom]  // Убрали cylinders, fittings и dimensionLines из зависимостей - используем refs
  );    

  // Сетка удалена

  // Добавьте обработчик контекстного меню
  const handleContextMenu = (e) => {
    e.evt.preventDefault();
    
    const stage = e.target.getStage();
    const containerRect = stage.container().getBoundingClientRect();
    const pointerPosition = stage.getPointerPosition();
    
    // Находим объект под курсором
    const clickedObject = e.target;
    
    // Ищем ближайшую группу
    let group = clickedObject;
    while (group && group.getClassName() !== 'Group') {
      group = group.getParent();
    }

    if (group) {
      const id = group.id();
      // Определяем тип объекта
      const isCylinder = cylinders.some((c) => c.id === id);
      const isFitting = fittings.some((f) => f.id === id);
      
      // Проверяем наличие selfFacing у фитинга и не повёрнут ли он уже
      let hasSelfFacing = false;
      let isSelfFacing = false;
      if (isFitting) {
        const fit = fittings.find((f) => f.id === id);
        if (fit) {
          isSelfFacing = fit.isSelfFacing === true; // Уже повёрнут на себя
          if (fit.itemKey) {
            const cfg = fittingsData[fit.itemKey];
            hasSelfFacing = !!(cfg && cfg.selfFacing);
          }
        }
      }
      
      setContextMenu({
        x: pointerPosition.x + containerRect.left,
        y: pointerPosition.y + containerRect.top,
        objectId: id,
        objectType: isFitting ? 'fitting' : (isCylinder ? 'cylinder' : null),
        hasSelfFacing: hasSelfFacing,
        isSelfFacing: isSelfFacing, // Передаём флаг что фитинг уже повёрнут
      });
    } else {
      setContextMenu(null);
    }
  };

  // ИСПРАВЛЕНИЕ: Устанавливаем tabindex на контейнер Stage, чтобы он мог получать фокус
  useEffect(() => {
    if (stageRef.current) {
      const container = stageRef.current.container();
      if (container && !container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '0');
      }
    }
  }, []);

  const applySelfFacingToFitting = useCallback((fittingId) => {
    const fit = fittingsRef.current.find((f) => f.id === fittingId);
    if (!fit) return;
    const cfg = fittingsData[fit.itemKey];
    const selfCfg = cfg && cfg.selfFacing;
    if (!selfCfg || !selfCfg.fileName) {
      console.warn('Нет selfFacing конфига для', fit.itemKey);
      return;
    }
    // Загрузим альтернативный DXF и заменим шейпы и базовые размеры
    (async () => {
      try {
        const folder = 'fittings';
        const response = await fetch(`/${folder}/${selfCfg.fileName}`);
        if (!response.ok) throw new Error(`Не удалось загрузить DXF-файл: ${response.statusText}`);
        const dxfText = await response.text();
        const parser = new DxfParser();
        const dxf = parser.parseSync(dxfText);
        const shapes = convertDxfToKonvaShapes(dxf);

        // Вычислим габариты нового DXF
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        shapes.forEach((shape) => {
          if (shape.type === 'line' || shape.type === 'polyline') {
            const pts = shape.points;
            for (let i = 0; i < pts.length; i += 2) {
              const px = pts[i];
              const py = pts[i + 1];
              if (px < minX) minX = px;
              if (px > maxX) maxX = px;
              if (py < minY) minY = py;
              if (py > maxY) maxY = py;
            }
          } else if (shape.type === 'circle' || shape.type === 'arc') {
            const x1 = shape.x - shape.radius;
            const x2 = shape.x + shape.radius;
            const y1 = shape.y - shape.radius;
            const y2 = shape.y + shape.radius;
            if (x1 < minX) minX = x1;
            if (x2 > maxX) maxX = x2;
            if (y1 < minY) minY = y1;
            if (y2 > maxY) maxY = y2;
          }
        });
        const objectWidth = maxX - minX;
        const objectHeight = maxY - minY;
        shapes.forEach((shape) => {
          if (shape.type === 'line' || shape.type === 'polyline') {
            shape.points = shape.points.map((coord, index) => index % 2 === 0 ? coord - minX : coord - minY);
          } else if (shape.type === 'circle' || shape.type === 'arc') {
            shape.x -= minX;
            shape.y -= minY;
          }
        });

        const desiredW = typeof selfCfg.diameter === 'number' ? selfCfg.diameter : objectWidth;
        const desiredH = typeof selfCfg.length === 'number' ? selfCfg.length : (typeof selfCfg.Length === 'number' ? selfCfg.Length : objectHeight);

        setFittings((prev) => prev.map((f) => f.id === fittingId ? {
          ...f,
          shapes,
          width: desiredW,
          height: desiredH,
          originalWidth: objectWidth,
          originalHeight: objectHeight,
          isDiameterFixed: typeof selfCfg.isDiameterFixed === 'boolean' ? selfCfg.isDiameterFixed : f.isDiameterFixed,
          isLengthAdjustable: typeof selfCfg.adjustableLength === 'boolean' ? selfCfg.adjustableLength : f.isLengthAdjustable,
          isSelfFacing: true, // Помечаем что фитинг повёрнут "на себя"
        } : f));
        saveState();
      } catch (err) {
        console.error('Ошибка self-facing DXF:', err);
      }
    })();
  }, [saveState]);  // Убрали fittings - используем fittingsRef

  // Добавьте обработчик включения режима трансформации
  const handleTransformClick = () => {
    if (contextMenu) {
      const targetId = contextMenu.objectId;
      setRotateTargetId(targetId);
      // Сначала сбрасываем выделение
      setSelectedId(null);
      // Затем устанавливаем выделение под трансформацию
      setTimeout(() => {
        setSelectedId(targetId);
        setContextMenu(null);
        const layer = stageRef.current.findOne('Layer');
        if (layer) {
          layer.batchDraw();
        }
      }, 0);
    }
  };

  return (
    <div
      style={{ 
        position: 'absolute',
        inset: 0,
        userSelect: 'none'
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {/* Панель редактирования выбранного паттерна */}
      {selectedPatternId && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'rgba(255,255,255,0.9)',
            border: '1px solid #ccc',
            borderRadius: 4,
            padding: 8,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <span style={{ fontSize: 12 }}>Паттерн:</span>
          <input
            type="number"
            value={patternEditCount}
            min={1}
            style={{ width: 64 }}
            onChange={(e) => setPatternEditCount(Math.max(1, parseInt(e.target.value || '1', 10)))}
            title="Количество"
          />
          <input
            type="number"
            value={patternEditDx}
            style={{ width: 64 }}
            onChange={(e) => setPatternEditDx(parseFloat(e.target.value || '0'))}
            title="Шаг по X (вправо)"
            placeholder="Шаг по X"
          />
          <input
            type="number"
            value={patternEditDy}
            style={{ width: 64 }}
            onChange={(e) => setPatternEditDy(parseFloat(e.target.value || '0'))}
            title="Шаг по Y (вниз)"
            placeholder="Шаг по Y"
          />
          <button
            onClick={() => {
              saveState();
              setPatterns((prev) => prev.map((p) => (
                p.id === selectedPatternId
                  ? { ...p, count: Number(patternEditCount) || 1, dx: Number(patternEditDx) || 0, dy: Number(patternEditDy) || 0 }
                  : p
              )));
              const pat = patterns.find((pp) => pp.id === selectedPatternId);
              if (pat) {
                rebuildClonesForPattern({ ...pat, count: Number(patternEditCount) || 1, dx: Number(patternEditDx) || 0, dy: Number(patternEditDy) || 0 });
              }
            }}
          >
            Применить
          </button>
          <button
            onClick={() => {
              const newDx = -((Number(patternEditDx) || 0));
              const newDy = -((Number(patternEditDy) || 0));
              setPatternEditDx(newDx);
              setPatternEditDy(newDy);
              saveState();
              setPatterns((prev) => prev.map((p) => (
                p.id === selectedPatternId
                  ? { ...p, dx: newDx, dy: newDy }
                  : p
              )));
              const pat = patterns.find((pp) => pp.id === selectedPatternId);
              if (pat) {
                rebuildClonesForPattern({ ...pat, count: Number(patternEditCount) || 1, dx: newDx, dy: newDy });
              }
            }}
          >
            Развернуть
          </button>
          <button onClick={() => setSelectedPatternId(null)}>Скрыть</button>
          <button
            onClick={() => {
              saveState();
              setCylinders((prev) => prev.filter((c) => !(c.isPatternClone && c.patternId === selectedPatternId)));
              setFittings((prev) => prev.filter((f) => !(f.isPatternClone && f.patternId === selectedPatternId)));
              setPatterns((prev) => prev.filter((p) => p.id !== selectedPatternId));
              setSelectedPatternId(null);
              setSelectedPatternObjects([]);
            }}
          >
            Удалить
          </button>
        </div>
      )}
      <Stage
        ref={stageRef}
        width={canvasWidth}
        height={canvasHeight}
        scaleX={effectiveZoom}
        scaleY={effectiveZoom}
        x={0}
        y={0}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        onContextMenu={(e) => {
          // Снимаем выбор с balloon, если клик контекстом по пустому месту
          const clickedOnEmpty = e.target === e.target.getStage();
          if (clickedOnEmpty) setSelectedBalloonId(null);
          handleContextMenu(e);
        }}
        onClick={(e) => {
          const clickedOnEmpty = e.target === e.target.getStage();
          const isTransformer = e.target.getParent()?.className === 'Transformer';

          // Сохраняем позицию клика для вставки объектов
          if (clickedOnEmpty) {
            const stage = e.target.getStage();
            const pointerPos = stage.getPointerPosition();
            if (pointerPos) {
              setLastClickPosition({
                x: pointerPos.x / effectiveZoom,
                y: pointerPos.y / effectiveZoom
              });
            }
          }

          if (clickedOnEmpty) {
            // Полный сброс при клике по пустому месту
            setContextMenu(null);
            setSelectedId(null);
            setRotateTargetId(null);
            setSelectedBalloonId(null);
            setSelectedBalloonTarget(null);
            setSelectedCylinderId(null);
            setSelectedFittingId(null);
            setSelectedDimensionId(null);
            setSelectedProjectedViewId(null);
            setSelectedAnnotationId(null);
            setSelectedPatternId(null);
            setSelectedPatternObjects([]);
            setIsFastenersTableSelected(false);
            setIsWorkshopTableSelected(false);
            return;
          }

          // Клик по объекту: если это не трансформер, не добавляем в мультивыбор
          if (!isTransformer && !isCtrlPressed) {
            // Ничего не делаем здесь — конкретные onSelect внутри объектов выставят selectedId
            // Но сбросим выделения прочих сущностей, чтобы оставить ровно один выбор
            setSelectedBalloonTarget(null);
            setSelectedBalloonId(null);
            setSelectedPatternId(null);
            setSelectedPatternObjects([]);
            setSelectedDimensionId(null);
            setSelectedAnnotationId(null);
          }
        }}
      >
        <Layer>
          {/* Сетка удалена */}

          {backgroundImage && (
            <KonvaImage
              name="sheet-background"
              image={backgroundImage}
              width={canvasWidth}
              height={canvasHeight}
              scaleX={1 / (effectiveZoom || 1)}
              scaleY={1 / (effectiveZoom || 1)}
              listening={false}
            />
          )}
          {lines.map((line, i) => (
            <Line
              key={i}
              points={line.points}
              stroke="#df4b26"
              strokeWidth={2}
              tension={0.5}
              lineCap="round"
              globalCompositeOperation="source-over"
            />
          ))}
          
          {/* Линия под текстом цеха */}
          {workshopLine && (
            <Line
              name="frame-overlay"
              points={workshopLine.points}
              stroke={workshopLine.stroke}
              strokeWidth={workshopLine.strokeWidth}
              scaleX={1 / (zoom || 1)}
              scaleY={1 / (zoom || 1)}
              listening={false}
            />
          )}
          
          {/* Отображение цилиндров */}
          {cylinders.map((cylinder) => (
            <TransformableObject
              key={cylinder.id}
              isSelected={selectedId === cylinder.id}
              showTransformer={false} // Для цилиндров поворот отключен
              onSelect={() => {
                setSelectedId(cylinder.id);
                setRotateTargetId(null);
                if (cylinder.isPatternClone) {
                  setSelectedPatternId(cylinder.patternId);
                  const pat = patterns.find((p) => p.id === cylinder.patternId);
                  if (pat) {
                    setPatternEditCount(pat.count || 1);
                    setPatternEditDx(pat.dx || 0);
                    setPatternEditDy(pat.dy || 0);
                  }
                } else {
                  setSelectedPatternId(null);
                }
              }}
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
              id={cylinder.id}
              rotation={cylinder.rotation}
              offsetX={cylinder.offsetX}
              offsetY={cylinder.offsetY}
              groupX={cylinder.groupX || 0}
              groupY={cylinder.groupY || 0}
              isLocked={cylinder.locked} // Передаем состояние блокировки
              onTransformEnd={(transformData) => {
                // Сохраняем параметры трансформации в state
                setCylinders(prev => {
                  const updated = prev.map(c => {
                    if (c.id === cylinder.id) {
                      return {
                        ...c,
                        rotation: transformData.rotation,
                        groupX: transformData.x,
                        groupY: transformData.y,
                        scaleX: transformData.scaleX,
                        scaleY: transformData.scaleY,
                        offsetX: transformData.offsetX,
                        offsetY: transformData.offsetY,
                      };
                    }
                    return c;
                  });
                  // Обновляем ref сразу
                  cylindersRef.current = updated;
                  return updated;
                });
                updateAllDimensionLines();
                rebuildClonesForBase('cylinder', cylinder.id);
              }}
            >
              <CylinderComponent
                cylinder={cylinder}
                isDimensionLineEnabled={isDimensionLineEnabled}
                highlightedSide={highlightedSide}
                setHighlightedSide={setHighlightedSide}
                highlightedPoint={highlightedPoint}
                setHighlightedPoint={setHighlightedPoint}
                snappingMode={snappingMode}
                isLKeyPressed={isLKeyPressed}
                isPKeyPressed={isPKeyPressed}
                pAttachmentMode={pAttachmentMode}
                handleSnapPointClick={handleSnapPointClick}
                handleEdgeClick={handleEdgeClick}
                pointAttachmentMode={pointAttachmentMode}
                isPointConstrained={isPointConstrained}
                isEdgeConstrained={isEdgeConstrained}
                handleRemoveConstraint={handleRemoveConstraint}
                handleRemoveEdgeConstraint={handleRemoveEdgeConstraint}
                zoom={effectiveZoom}
                setSelectedId={setSelectedId}
                handleMouseOver={handleMouseOver}
                handleResize={handleResize}
                handleResizeStart={handleResizeStart}
                handleResizeEnd={handleResizeEnd}
                handleDragStart={handleDragStart}
                handleDragMove={handleDragMove}
                handleDragEnd={handleDragEnd}
                getSnappedPosition={getSnappedPosition}
                onSelectCylinder={(id) => {
                  setSelectedId(id);
                  setSelectedCylinderId(id);
                  setSelectedFittingId(null);
                  setSelectedBalloonId(null);
                  setSelectedBalloonTarget(null);
                  setSelectedDimensionId(null);
                  const cyl = cylinders.find((c) => c.id === id);
                  if (cyl && cyl.isPatternClone) {
                    setSelectedPatternId(cyl.patternId);
                    const pat = patterns.find((p) => p.id === cyl.patternId);
                    if (pat) {
                      setPatternEditCount(pat.count || 1);
                      setPatternEditDx(pat.dx || 0);
                      setPatternEditDy(pat.dy || 0);
                    }
                  } else {
                    setSelectedPatternId(null);
                  }
                }}
                isPatternSelected={selectedPatternObjects.some((o) => o.type === 'cylinder' && o.id === cylinder.id)}
              />
            </TransformableObject>
          ))}
          {isLKeyPressed && (
            <Text
              x={cursorPosition.x + 10}
              y={cursorPosition.y + 10}
              text="🔒"
              fontSize={24}
              fill="#000000"
              listening={false}
            />
          )}
          {isMiddlePanning && (
            <Text
              x={cursorPosition.x + 10}
              y={cursorPosition.y + 10}
              text="✋"
              fontSize={24}
              fill="#000000"
              listening={false}
            />
          )}
          {annotations.filter(a => !a.isHidden).map((annotation) => (
            <Group key={annotation.id}
              name="frame-overlay"
              x={annotation.x / (zoom || 1)}
              y={annotation.y / (zoom || 1)}
              scaleX={1 / (zoom || 1)}
              scaleY={1 / (zoom || 1)}
              draggable
              onDragStart={handleDragStart}
              onDragMove={(e) => {
                // Если активно панорамирование - останавливаем перетаскивание
                if (isMiddlePanning || (e.evt && e.evt.button === 1)) {
                  e.target.stopDrag();
                  return;
                }
                const node = e.target;
                const newX = node.x() * (zoom || 1);
                const newY = node.y() * (zoom || 1);
                setAnnotations((prev) => prev.map((a) => a.id === annotation.id ? { ...a, x: newX, y: newY } : a));
              }}
              onDragEnd={handleDragEnd}
              onContextMenu={(e) => {
                // Блокируем контекстное меню для аннотаций
                e.cancelBubble = true;
              }}
              onClick={(e) => {
                e.cancelBubble = true;
                setSelectedAnnotationId(annotation.id);
                setSelectedBalloonId(null);
                setSelectedBalloonTarget(null);
                setSelectedCylinderId(null);
                setSelectedFittingId(null);
                setSelectedDimensionId(null);
                setContextMenu(null);
              }}
            >
              {selectedAnnotationId === annotation.id && (() => {
                const fs = (annotation.fontSize || 14);
                const ff = annotation.fontFamily || 'Arial';
                const fst = annotation.fontStyle || 'italic';
                // Для аннотации "Вес" используем меньший межстрочный интервал
                const lh = annotation.id === 'anno-init-5' ? 0.8 : 1.2;
                const text = annotation.text || '';
                const maxLineWidth = Math.max(1, getTextWidth(text, fs, ff, fst));
                const height = Math.max(fs, getTextHeight(text, fs, ff, fst, lh));
                const isInitial = annotation.id === 'anno-init-1' || annotation.id === 'anno-init-2' || annotation.id === 'anno-init-3' || annotation.id === 'anno-init-4' || annotation.id === 'anno-init-5';
                return (
                  <Rect
                    x={isInitial ? -(maxLineWidth / 2) - 4 : -4}
                    y={isInitial ? -(height / 2) - 4 : -4}
                    width={maxLineWidth + 8}
                    height={height + 8}
                    stroke="#3f51b5"
                    strokeWidth={1}
                    opacity={0.3}
                  />
                );
              })()}
              {(() => {
                const fs = (annotation.fontSize || 14);
                const ff = annotation.fontFamily || 'Arial';
                const fst = annotation.fontStyle || 'italic';
                // Для аннотации "Вес" используем меньший межстрочный интервал
                const lh = annotation.id === 'anno-init-5' ? 0.8 : 1.2;
                const isInitial = annotation.id === 'anno-init-1' || annotation.id === 'anno-init-2' || annotation.id === 'anno-init-3' || annotation.id === 'anno-init-4' || annotation.id === 'anno-init-5';
                const textW = getTextWidth(annotation.text || '', fs, ff, fst);
                const textH = getTextHeight(annotation.text || '', fs, ff, fst, lh);
                return (
                  <EditableText
                    x={0}
                    y={0}
                    text={annotation.text}
                    fontSize={fs}
                    fontFamily={ff}
                    fontStyle={fst}
                    align={annotation.align || 'left'}
                    lineHeight={lh}
                    fill={annotation.fill || '#000000'}
                    offsetX={isInitial ? textW / 2 : 0}
                    offsetY={isInitial ? textH / 2 : 0}
                    onChange={(val) => {
                      setAnnotations((prev) => prev.map((a) => a.id === annotation.id ? { ...a, text: val } : a));
                    }}
                  />
                );
              })()}
              {annotation.id === 'anno-init-4' && (() => {
                const fsLocal = (annotation.fontSize || 14);
                const ffLocal = (annotation.fontFamily || 'Arial');
                const fstLocal = (annotation.fontStyle || 'italic');
                const th = getTextHeight('M', fsLocal, ffLocal, fstLocal, 1.2);
                const iconOffsetX = (getTextWidth(annotation.text || '', fsLocal, ffLocal, fstLocal) / 2) + 12; // ближе к тексту
                return (
                  <Group
                    name="toggle-switch"
                    x={iconOffsetX}
                    y={0}
                    onClick={(e) => {
                      e.cancelBubble = true;
                      const current = (annotation.text || '').trim();
                      const lower = current.toLowerCase();
                      const next = lower === 'матовый' ? 'Зеркало' : 'Матовый';
                      
                      // Если переключаем на зеркало, проверяем доступность
                      if (next === 'Зеркало') {
                        // Проверяем наличие квадратных профилей
                        const hasSquareProfiles = cylinders.some(cyl => 
                          cyl.pipeSpec && cyl.pipeSpec.shape === 'square'
                        );
                        
                        if (hasSquareProfiles) {
                          setSquareProfileWarningDialog(true);
                          return; // Не переключаем покрытие
                        }
                        
                        // Собираем все уникальные диаметры труб
                        const pipeDiameters = new Map(); // diameter -> count
                        cylinders.forEach(cyl => {
                          if (cyl.pipeSpec && cyl.pipeSpec.diameter) {
                            const diameter = cyl.pipeSpec.diameter;
                            pipeDiameters.set(diameter, (pipeDiameters.get(diameter) || 0) + 1);
                          }
                        });
                        
                        // Проверяем, какие диаметры не имеют зеркального покрытия
                        const unavailablePipes = [];
                        pipeDiameters.forEach((count, diameter) => {
                          if (!hasZerkaloCoating(diameter)) {
                            unavailablePipes.push({ diameter, count });
                          }
                        });
                        
                        // Если есть трубы без зеркального покрытия, показываем предупреждение
                        if (unavailablePipes.length > 0) {
                          setMirrorWarningDialog({
                            open: true,
                            unavailablePipes,
                          });
                          return; // Не переключаем покрытие
                        }
                      }
                      
                      // Переключаем покрытие
                      setAnnotations((prev) => prev.map((a) => a.id === 'anno-init-4' ? { ...a, text: next } : a));
                      
                      // Если переключаем на матовый, очищаем данные о зеркальных трубах
                      if (next === 'Матовый') {
                        // Сохраняем пустой массив в localStorage для очистки mirrorPolishingItems
                        localStorage.setItem('mirrorPolishingItems', JSON.stringify([]));
                        if (typeof onObjectsChanged === 'function') onObjectsChanged();
                      }
                      
                      // Обновляем покрытие труб
                      setCylinders((prev) => prev.map((c) => {
                        if (c.pipeSpec) {
                          const diameter = c.pipeSpec.diameter;
                          
                          // Если переключаем на зеркало, проверяем доступность для каждой трубы
                          if (next === 'Зеркало' && diameter) {
                            // Если у трубы нет зеркального покрытия, оставляем матовым
                            if (!hasZerkaloCoating(diameter)) {
                              return c; // Не меняем покрытие
                            }
                          }
                          
                          // Если переключаем на матовый, проверяем доступность для каждой трубы
                          if (next === 'Матовый' && diameter) {
                            // Если у трубы нет матового покрытия, оставляем зеркальным
                            if (!hasMatteCoating(diameter)) {
                              return c; // Не меняем покрытие
                            }
                          }
                          
                          return {
                            ...c,
                            pipeSpec: {
                              ...c.pipeSpec,
                              coating: next,
                            },
                          };
                        }
                        return c;
                      }));
                    }}
                  >
                    {/* Верхняя стрелка вправо (уменьшенная) */}
                    <Arrow
                      points={[-8, -3, 8, -3]}
                      stroke="#000000"
                      fill="#000000"
                      strokeWidth={1}
                      pointerLength={4}
                      pointerWidth={4}
                      pointerAtEnding
                    />
                    {/* Нижняя стрелка влево (уменьшенная) */}
                    <Arrow
                      points={[8, 3, -8, 3]}
                      stroke="#000000"
                      fill="#000000"
                      strokeWidth={1}
                      pointerLength={4}
                      pointerWidth={4}
                      pointerAtEnding
                    />
                    {/* Удобная зона клика */}
                    <Rect x={-10} y={-Math.max(6, th/2)} width={20} height={Math.max(12, th)} fill={'rgba(0,0,0,0)'} listening />
                  </Group>
                );
              })()}
              {(annotation.id === 'anno-init-7' || annotation.id === 'anno-init-8') && (() => {
                const fsLocal = (annotation.fontSize || 14);
                const ffLocal = (annotation.fontFamily || 'Arial');
                const fstLocal = (annotation.fontStyle || 'italic');
                const lhLocal = 1.2;
                const th = getTextHeight('M', fsLocal, ffLocal, fstLocal, lhLocal);
                const textLines = (annotation.text || '').split('\n');
                const maxLineWidth = Math.max(...textLines.map(line => getTextWidth(line, fsLocal, ffLocal, fstLocal)));
                const iconOffsetX = maxLineWidth + 12; // справа от текста
                return (
                  <Group
                    name="toggle-switch"
                    x={iconOffsetX}
                    y={0}
                    onClick={(e) => {
                      e.cancelBubble = true;
                      
                      // Переключаем между anno-init-7 и anno-init-8 и устанавливаем флаг ручного переключения
                      setAnnotations((prev) => prev.map((a) => {
                        if (a.id === 'anno-init-7') {
                          return { ...a, isHidden: !a.isHidden, manuallyToggled: true };
                        }
                        if (a.id === 'anno-init-8') {
                          return { ...a, isHidden: !a.isHidden, manuallyToggled: true };
                        }
                        return a;
                      }));
                    }}
                  >
                    {/* Верхняя стрелка вправо (уменьшенная) */}
                    <Arrow
                      points={[-8, -3, 8, -3]}
                      stroke="#000000"
                      fill="#000000"
                      strokeWidth={1}
                      pointerLength={4}
                      pointerWidth={4}
                      pointerAtEnding
                    />
                    {/* Нижняя стрелка влево (уменьшенная) */}
                    <Arrow
                      points={[8, 3, -8, 3]}
                      stroke="#000000"
                      fill="#000000"
                      strokeWidth={1}
                      pointerLength={4}
                      pointerWidth={4}
                      pointerAtEnding
                    />
                    {/* Удобная зона клика */}
                    <Rect x={-10} y={-Math.max(6, th/2)} width={20} height={Math.max(12, th)} fill={'rgba(0,0,0,0)'} listening />
                  </Group>
                );
              })()}
            </Group>
          ))}

          

          {/* Отображение фитингов */}
          {(() => {
            const drawOrder = [
              ...fittings.filter((f) => !f.isHidden && !(f.isSelfFacing && f.side === 'back')),
              ...fittings.filter((f) => !f.isHidden && f.isSelfFacing && f.side === 'back'),
            ];
            // Муфта напольного крепежа рисуется раньше стойки, чтобы Ø48,3 легла поверх неё.
            const muftaUnderStand = (mufta, stand) => {
              if (!mufta || !stand || mufta.itemKey !== 'Mufta_1_1.4' || !stand.isFloorFastener) return false;
              if (!fastenersData[stand.itemKey]?.lowerPipeLength) return false;
              const left = (Number(stand.x) || 0) + (Number(stand.width) || 0) / 2 - 30;
              const right = left + 60;
              const top = (Number(stand.y) || 0) - 24;
              const bottom = (Number(stand.y) || 0) + 30;
              const mx = Number(mufta.x) || 0;
              const my = Number(mufta.y) || 0;
              const mw = Number(mufta.width) || 0;
              const mh = Number(mufta.height) || 0;
              return mx < right && mx + mw > left && my < bottom && my + mh > top;
            };
            drawOrder.sort((a, b) => {
              if (muftaUnderStand(a, b)) return -1;
              if (muftaUnderStand(b, a)) return 1;
              return 0;
            });
            return drawOrder;
          })().map((fitting) => (
            <TransformableObject
              key={fitting.id}
              isSelected={selectedId === fitting.id}
              showTransformer={rotateTargetId === fitting.id}
              onSelect={() => {
                setSelectedId(fitting.id);
                setRotateTargetId(null);
                if (fitting.isPatternClone) {
                  setSelectedPatternId(fitting.patternId);
                  const pat = patterns.find((p) => p.id === fitting.patternId);
                  if (pat) {
                    setPatternEditCount(pat.count || 1);
                    setPatternEditDx(pat.dx || 0);
                    setPatternEditDy(pat.dy || 0);
                  }
                } else {
                  setSelectedPatternId(null);
                }
              }}
              id={fitting.id}
              rotation={fitting.rotation}
              offsetX={fitting.offsetX}
              offsetY={fitting.offsetY}
              groupX={isNaN(fitting.groupX) ? 0 : (fitting.groupX || 0)}
              groupY={isNaN(fitting.groupY) ? 0 : (fitting.groupY || 0)}
              isLocked={fitting.locked} // Передаем состояние блокировки
              onTransformEnd={(transformData) => {
                // Сохраняем параметры трансформации в state
                setFittings(prev => {
                  const updated = prev.map(f => {
                    if (f.id === fitting.id) {
                      return {
                        ...f,
                        rotation: transformData.rotation,
                        groupX: transformData.x,
                        groupY: transformData.y,
                        scaleX: transformData.scaleX,
                        scaleY: transformData.scaleY,
                        offsetX: transformData.offsetX,
                        offsetY: transformData.offsetY,
                      };
                    }
                    return f;
                  });
                  // Обновляем ref сразу
                  fittingsRef.current = updated;
                  return updated;
                });
                updateAllDimensionLines();
                // Обновляем паттерны и для баз, и для клонов, чтобы поворот сразу применялся к потомкам клонов
                rebuildClonesForBase('fitting', fitting.id);
              }}
            >
              <FittingComponent
                fitting={fitting}
                selectedFittingId={selectedFittingId}
                setSelectedFittingId={setSelectedFittingId}
                setSelectedCylinderId={setSelectedCylinderId}
                setSelectedDimensionId={setSelectedDimensionId}
                setSelectedBalloonId={setSelectedBalloonId}

                highlightedSide={highlightedSide}
                setHighlightedSide={setHighlightedSide}
                highlightedPoint={highlightedPoint}
                setHighlightedPoint={setHighlightedPoint}
                isDimensionLineEnabled={isDimensionLineEnabled}
                snappingMode={snappingMode}
                zoom={effectiveZoom}
                isLKeyPressed={isLKeyPressed}
                isPKeyPressed={isPKeyPressed}
                pAttachmentMode={pAttachmentMode}
                handleSnapPointClick={handleSnapPointClick}
                handleEdgeClick={handleEdgeClick}
                pointAttachmentMode={pointAttachmentMode}
                isPointConstrained={isPointConstrained}
                isEdgeConstrained={isEdgeConstrained}
                handleRemoveConstraint={handleRemoveConstraint}
                handleRemoveEdgeConstraint={handleRemoveEdgeConstraint}

                handleMouseOver={handleMouseOver}
                handleDragStart={handleDragStart}
                handleDragMove={handleDragMove}
                handleDragEnd={(e) => { handleDragEnd(e); try { if (updateAllDimensionLinesRef.current) updateAllDimensionLinesRef.current(); } catch (_) {} }}
                handleResizeStart={handleResizeStart}
                handleFittingResize={handleFittingResize}
                handleResizeEnd={handleResizeEnd}
                getSnappedPosition={getSnappedPosition}
                cylinders={cylinders}
                sceneFittings={fittings}
                onSelectFitting={(id) => {
                  setSelectedId(id);
                  const fit = fittings.find((f) => f.id === id);
                  if (fit && fit.isPatternClone) {
                    setSelectedPatternId(fit.patternId);
                    const pat = patterns.find((p) => p.id === fit.patternId);
                    if (pat) {
                      setPatternEditCount(pat.count || 1);
                      setPatternEditDx(pat.dx || 0);
                      setPatternEditDy(pat.dy || 0);
                    }
                  } else {
                    setSelectedPatternId(null);
                  }
                }}
                isPatternSelected={selectedPatternObjects.some((o) => o.type === 'fitting' && o.id === fitting.id)}
              />
            </TransformableObject>
          ))}

          {/* Визуализация сварок */}
          {showWeldMarkers && welds.map((weld) => (
            <Group key={weld.id} name="weld-marker">
              {/* Маркер сварки - красный кружок */}
              <Circle
                x={weld.x}
                y={weld.y}
                radius={5 / effectiveZoom}
                fill="red"
                stroke="orange"
                strokeWidth={2 / effectiveZoom}
                listening={false}
                opacity={0.8}
              />
              {/* Текст с количеством сварок (если несколько в одном месте) */}
              <Text
                x={weld.x + 8 / effectiveZoom}
                y={weld.y - 8 / effectiveZoom}
                text="🔥"
                fontSize={16 / effectiveZoom}
                fill="red"
                listening={false}
              />
            </Group>
          ))}

          {dimensionLines.map((dim, i) => (
            <DimensionLineComponent
              key={i}
              dim={dim}
              index={i}
              dimensionLines={dimensionLines}
              setDimensionLines={setDimensionLines}

              handleDimensionDrag={handleDimensionDrag}
              handleDimensionChange={handleDimensionChange}
              snapThreshold={snapThreshold}
              effectiveZoom={effectiveZoom}
              zoom={zoom}

              updateAllDimensionLines={updateAllDimensionLines}
              saveState={saveState}
              isShiftPressed={isShiftPressed}

              setSelectedDimensionId={setSelectedDimensionId}
              setSelectedCylinderId={setSelectedCylinderId}
              setSelectedFittingId={setSelectedFittingId}
              fittings={fittings}
              textZoom={textZoom}
            />
          ))}
          {/* Клоны линейных массивов теперь — реальные объекты в cylinders/fittings */}
          {currentLine && (
            <Line
              points={currentLine.points}
              stroke="#df4b26"
              strokeWidth={2}
              tension={0.5}
              lineCap="round"
              globalCompositeOperation="source-over"
            />
          )}
          {/* Текущая создаваемая размерная линия */}
          {currentDimension && (
            <Group
              x={currentDimension.isHorizontal ? 0 : currentDimension.position.x}
              y={currentDimension.isHorizontal ? currentDimension.position.y : 0}
            >
              {/* Выносные линии */}
              {currentDimension.isHorizontal ? (
                <>
                  <Line
                    points={[
                      currentDimension.x1 - currentDimension.position.x,
                      currentDimension.y1 - currentDimension.position.y,
                      currentDimension.x1 - currentDimension.position.x,
                      0,
                    ]}
                    stroke="#000000"
                    strokeWidth={1}
                    dash={[4, 4]}
                  />
                  <Line
                    points={[
                      currentDimension.x2 - currentDimension.position.x,
                      currentDimension.y2 - currentDimension.position.y,
                      currentDimension.x2 - currentDimension.position.x,
                      0,
                    ]}
                    stroke="#000000"
                    strokeWidth={1}
                    dash={[4, 4]}
                  />
                  {/* Размерная линия */}
                  <Arrow
                    points={[
                      currentDimension.x1 - currentDimension.position.x,
                      0,
                      currentDimension.x2 - currentDimension.position.x,
                      0,
                    ]}
                    stroke="#000000"
                    strokeWidth={2}
                    pointerLength={10}
                    pointerWidth={10}
                    fill="#000000"
                    pointerAtBeginning={true}
                    pointerAtEnding={true}
                  />
                  {/* Текст */}
                  <EditableText
                    x={
                      (currentDimension.x1 + currentDimension.x2) / 2 -
                      currentDimension.position.x -
                      10
                    }
                    y={-20}
                    text={(Number(currentDimension?.value) || 0)
                      .toFixed(currentDimension?.precision ?? 2)
                      .replace(/\.00$/, '')
                      .replace(/(\.\d)0$/, '$1')}
                    fontSize={16 * (textZoom || 1)}
                    fontFamily={currentDimension?.textFontFamily || 'Arial'}
                    fontStyle={currentDimension?.textFontStyle || 'italic'}
                    fill={currentDimension?.textColor || '#ff0000'}
                    rotation={0}
                  />
                </>
              ) : (
                <>
                  <Line
                    points={[
                      currentDimension.x1 - currentDimension.position.x,
                      currentDimension.y1 - currentDimension.position.y,
                      0,
                      currentDimension.y1 - currentDimension.position.y,
                    ]}
                    stroke="#000000"
                    strokeWidth={1}
                    dash={[4, 4]}
                  />
                  <Line
                    points={[
                      currentDimension.x2 - currentDimension.position.x,
                      currentDimension.y2 - currentDimension.position.y,
                      0,
                      currentDimension.y2 - currentDimension.position.y,
                    ]}
                    stroke="#000000"
                    strokeWidth={1}
                    dash={[4, 4]}
                  />
                  {/* Размерная линия */}
                  <Arrow
                    points={[
                      0,
                      currentDimension.y1 - currentDimension.position.y,
                      0,
                      currentDimension.y2 - currentDimension.position.y,
                    ]}
                    stroke="#000000"
                    strokeWidth={2}
                    pointerLength={10}
                    pointerWidth={10}
                    fill="#000000"
                    pointerAtBeginning={true}
                    pointerAtEnding={true}
                  />
                  {/* Текст */}
                  <EditableText
                    x={-20}
                    y={
                      (currentDimension.y1 + currentDimension.y2) / 2 -
                      currentDimension.position.y -
                      10
                    }
                    text={(Number(currentDimension?.value) || 0)
                      .toFixed(currentDimension?.precision ?? 2)
                      .replace(/\.00$/, '')
                      .replace(/(\.\d)0$/, '$1')}
                    fontSize={16 * (textZoom || 1)}
                    fontFamily={currentDimension?.textFontFamily || 'Arial'}
                    fontStyle={currentDimension?.textFontStyle || 'italic'}
                    fill={currentDimension?.textColor || '#ff0000'}
                    rotation={-90}
                  />
                </>
              )}
            </Group>
          )}
          {/* Выноски (позиции) — перенесены вниз слоя, чтобы быть поверх объектов */}
          {balloons.map((b) => (
            <Group key={b.id}
              x={b.x}
              y={b.y}
              draggable
              onDragStart={handleDragStart}
              onDragMove={(e) => {
                // Если активно панорамирование - останавливаем перетаскивание
                if (isMiddlePanning || (e.evt && e.evt.button === 1)) {
                  e.target.stopDrag();
                  return;
                }
                const node = e.target;
                const newX = node.x();
                const newY = node.y();
                setBalloons((prev) => prev.map((bb) => (bb.id === b.id ? { ...bb, x: newX, y: newY } : bb)));
              }}
              onDragEnd={(e) => {
                const node = e.target;
                const newX = node.x();
                const newY = node.y();
                setBalloons((prev) => prev.map((bb) => (bb.id === b.id ? { ...bb, x: newX, y: newY } : bb)));
                handleDragEnd(e);
              }}
              onClick={(e) => {
                e.cancelBubble = true;
                setSelectedBalloonId(b.id);
                setSelectedBalloonTarget(null);
                setSelectedCylinderId(null);
                setSelectedFittingId(null);
                setSelectedDimensionId(null);
                setSelectedAnnotationId(null);
                setContextMenu(null);
              }}
            >
              {selectedBalloonId === b.id && (() => {
                const scale = (positionTextZoom || 1);
                const fs = (b.fontSize || 16) * scale;
                const ff = b.fontFamily || 'Arial';
                const fst = b.fontStyle || 'italic';
                const textW = getTextWidth(b.text || '', fs, ff, fst);
                const textH = getTextHeight(b.text || '', fs, ff, fst);
                const gap = 6;
                return (
                  <Rect
                    x={-textW / 2 - 4}
                    y={-textH / 2 - gap - 4}
                    width={textW + 8}
                    height={textH + 8}
                    stroke="#FFA500"
                    strokeWidth={1}
                    opacity={0.3}
                  />
                );
              })()}
              {(() => {
                const scale = (positionTextZoom || 1);
                const fs = (b.fontSize || 16) * scale;
                const ff = b.fontFamily || 'Arial';
                const fst = b.fontStyle || 'italic';
                const textW = getTextWidth(b.text || '', fs, ff, fst);
                const textH = getTextHeight(b.text || '', fs, ff, fst);
                return (
                  <EditableText
                    x={0}
                    y={0}
                    text={b.text}
                    fontSize={fs}
                    fontFamily={ff}
                    fontStyle={fst}
                    align={b.textAlign || 'left'}
                    offsetX={textW / 2}
                    offsetY={textH / 2}
                    fill={b.color || '#000000'}
                    onChange={(val) => {
                      setBalloons((prev) => prev.map((bb) => (bb.id === b.id ? { ...bb, text: val } : bb)));
                    }}
                    onClick={(e) => {
                      e.cancelBubble = true;
                      setSelectedBalloonId(b.id);
                      setSelectedBalloonTarget(null);
                      setSelectedCylinderId(null);
                      setSelectedFittingId(null);
                      setSelectedDimensionId(null);
                    }}
                  />
                );
              })()}
              {(() => {
                const scale = (positionTextZoom || 1);
                const fs = (b.fontSize || 16) * scale;
                const textW = getTextWidth(b.text || '', fs, b.fontFamily || 'Arial', b.fontStyle || 'italic');
                const textH = getTextHeight(b.text || '', fs, b.fontFamily || 'Arial', b.fontStyle || 'italic');
                const gap = 6; // фиксированный зазор
                const baseY = textH / 2 + gap;
                return b.targets && b.targets.length >= 1 ? (
                  <Line points={[-textW / 2, baseY, textW / 2, baseY]} stroke="#000000" strokeWidth={1} />
                ) : null;
              })()}
              {b.targets && b.targets.map((t, idx) => {
                const cyl = t.type === 'cylinder' ? cylinders.find((c) => c.id === t.id) : null;
                const fit = t.type === 'fitting' ? fittings.find((f) => f.id === t.id) : null;
                const viewTarget = t.type === 'view' ? projectedViews.find((v) => v.id === t.id) : null;
                const scale = (positionTextZoom || 1);
                const fs = (b.fontSize || 16) * scale;
                const ff0 = b.fontFamily || 'Arial';
                const fst0 = b.fontStyle || 'italic';
                const textW0 = getTextWidth(b.text || '', fs, ff0, fst0);
                const textH0 = getTextHeight(b.text || '', fs, ff0, fst0);
                const gap = 6;
                let localPt = null;
                if (viewTarget && t.anchorLocal) {
                  localPt = { x: viewTarget.x + t.anchorLocal.x, y: viewTarget.y + t.anchorLocal.y };
                } else if (cyl) {
                  if (t.anchorLocal && typeof t.anchorLocal.x === 'number' && typeof t.anchorLocal.y === 'number') {
                    localPt = { x: cyl.x + t.anchorLocal.x, y: cyl.y + t.anchorLocal.y };
                  } else if (t.anchorNormalized && typeof t.anchorNormalized.x === 'number' && typeof t.anchorNormalized.y === 'number') {
                    localPt = { x: cyl.x + t.anchorNormalized.x * cyl.width, y: cyl.y + t.anchorNormalized.y * cyl.height };
                  } else {
                    localPt = { x: cyl.x + cyl.width / 2, y: cyl.y + cyl.height / 2 };
                  }
                } else if (fit) {
                  if (t.anchorLocal && typeof t.anchorLocal.x === 'number' && typeof t.anchorLocal.y === 'number') {
                    const baseY = t.anchorMode === 'top' ? fit.y : t.anchorMode === 'bottom' ? fit.y + fit.height : fit.y + t.anchorLocal.y;
                    localPt = { x: fit.x + t.anchorLocal.x, y: baseY };
                  } else if (t.anchorNormalized && typeof t.anchorNormalized.x === 'number' && typeof t.anchorNormalized.y === 'number') {
                    const baseY = t.anchorMode === 'top' ? fit.y : t.anchorMode === 'bottom' ? fit.y + fit.height : fit.y + t.anchorNormalized.y * fit.height;
                    localPt = { x: fit.x + t.anchorNormalized.x * fit.width, y: baseY };
                  } else {
                    localPt = { x: fit.x + fit.width / 2, y: fit.y + fit.height / 2 };
                  }
                }
                if (!localPt) return null;
                let ox = localPt.x;
                let oy = localPt.y;
                const node = viewTarget ? null : (stageRef.current ? stageRef.current.findOne('#' + t.id) : null);
                if (node && typeof node.getAbsoluteTransform === 'function') {
                  const absT = node.getAbsoluteTransform();
                  const p = absT.point({ x: localPt.x, y: localPt.y });
                  const z = effectiveZoom || 1;
                  ox = p.x / z;
                  oy = p.y / z;
                }
                // Определяем, какая сторона полки ближе к объекту, и используем её как X-якорь
                const leftX0 = b.x - textW0 / 2;
                const rightX0 = b.x + textW0 / 2;
                const leftDist0 = Math.abs(ox - leftX0);
                const rightDist0 = Math.abs(ox - rightX0);
                const anchor = { x: leftDist0 <= rightDist0 ? -textW0 / 2 : textW0 / 2, y: textH0 / 2 + gap };
                const handleX = ox - b.x;
                const handleY = oy - b.y;
                const isSelected = !!(selectedBalloonTarget && selectedBalloonTarget.balloonId === b.id && selectedBalloonTarget.targetIndex === idx);
                return (
                  <Group key={`${b.id}-t-${idx}`}>
                    <Line
                      points={[anchor.x, anchor.y, handleX, handleY]}
                      stroke="#000000"
                      strokeWidth={1}
                    />
                    <Circle
                      x={handleX}
                      y={handleY}
                      radius={3}
                      fill={isSelected ? 'blue' : 'white'}
                      stroke={isSelected ? 'blue' : '#000000'}
                      strokeWidth={2}
                      hitStrokeWidth={10}
                      onClick={(e) => {
                        e.cancelBubble = true;
                        setSelectedBalloonId(b.id);
                        setSelectedBalloonTarget({ balloonId: b.id, targetIndex: idx });
                      }}
                      onTap={(e) => {
                        e.cancelBubble = true;
                        setSelectedBalloonId(b.id);
                        setSelectedBalloonTarget({ balloonId: b.id, targetIndex: idx });
                      }}
                    />
                  </Group>
                );
              })}
            </Group>
          ))}
          {selectionRect && (
            <Rect
              x={selectionRect.x}
              y={selectionRect.y}
              width={selectionRect.width}
              height={selectionRect.height}
              fill="rgba(0,0,255,0.1)"
              stroke="blue"
            />
          )}
          {projectedViews.map((view) => {
            const scene = view.scene;
            if (!scene) return null;
            const selected = view.id === selectedProjectedViewId;
            const font = 16 * (textZoom || 1);
            const viewTitle = scene.title || 'Вид сверху';
            const viewTitleFont = 'Arial';
            const viewTitleStyle = 'italic';
            const viewTitleWidth = getTextWidth(viewTitle, font, viewTitleFont, viewTitleStyle);
            const viewTitleX = font + 14;
            const showMoveCursor = (event) => {
              const stage = event.target.getStage();
              if (stage) stage.container().style.cursor = 'move';
            };
            const resetCursor = (event) => {
              const stage = event.target.getStage();
              if (stage) stage.container().style.cursor = 'default';
            };
            const selectThisView = (event) => {
              event.cancelBubble = true;
              setSelectedProjectedViewId(view.id);
              setSelectedFittingId(null);
              setSelectedCylinderId(null);
              setSelectedDimensionId(null);
            };
            return (
              <Group
                key={view.id}
                id={view.id}
                x={view.x}
                y={view.y}
                draggable
                onDragStart={(event) => {
                  if (!isDimensionLineEnabled) return;
                  const local = event.currentTarget.getRelativePointerPosition();
                  if (local && local.y >= 28) event.currentTarget.stopDrag();
                }}
                onMouseMove={(event) => {
                  const local = event.currentTarget.getRelativePointerPosition();
                  const stage = event.target.getStage();
                  const overHeader = local && local.y < 28;
                  if (stage) stage.container().style.cursor = (!isDimensionLineEnabled || overHeader) ? 'move' : 'default';
                  if (!isDimensionLineEnabled) return;
                  if (overHeader) {
                    setHighlightedSide((prev) => (prev && prev.objectId === view.id ? null : prev));
                    setHighlightedPoint((prev) => (prev && prev.objectId === view.id ? null : prev));
                    return;
                  }
                  const pointer = stage && stage.getPointerPosition();
                  if (!pointer) return;
                  const z = effectiveZoom || 1;
                  const wx = pointer.x / z;
                  const wy = pointer.y / z;
                  const contentY = 28;
                  const segmentDistance = (px, py, x1, y1, x2, y2) => {
                    const dx = x2 - x1;
                    const dy = y2 - y1;
                    const lenSq = dx * dx + dy * dy;
                    const param = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
                    return Math.hypot(px - (x1 + param * dx), py - (y1 + param * dy));
                  };
                  let bestPoint = null;
                  getViewSnapPoints(scene).forEach((point, pointIndex) => {
                    const distance = Math.hypot(wx - (view.x + point.x), wy - (view.y + contentY + point.y));
                    const reach = point.guide ? 18 : 9;
                    if (distance <= reach && (!bestPoint || distance < bestPoint.distance)) {
                      bestPoint = { point, pointIndex, distance };
                    }
                  });
                  if (bestPoint) {
                    setHighlightedSide((prev) => (prev && prev.objectId === view.id ? null : prev));
                    setHighlightedPoint({
                      x: view.x + bestPoint.point.x,
                      y: view.y + contentY + bestPoint.point.y,
                      objectType: 'view',
                      objectId: view.id,
                      pointIndex: bestPoint.pointIndex,
                      local: { x: bestPoint.point.x, y: contentY + bestPoint.point.y },
                    });
                    return;
                  }
                  setHighlightedPoint((prev) => (prev && prev.objectId === view.id ? null : prev));
                  let best = null;
                  (scene.edges || []).forEach((edge) => {
                    const poly = (scene.polylines || [])[edge.polyIndex];
                    let dist = Infinity;
                    if (poly) {
                      for (let i = 0; i + 3 < poly.length; i += 2) {
                        dist = Math.min(dist, segmentDistance(
                          wx,
                          wy,
                          view.x + poly[i],
                          view.y + contentY + poly[i + 1],
                          view.x + poly[i + 2],
                          view.y + contentY + poly[i + 3],
                        ));
                      }
                    }
                    if (dist > (edge.guide ? 40 : 14)) return;
                    const localX = edge.ax ?? (edge.x1 + edge.x2) / 2;
                    const localY = edge.ay ?? (edge.y1 + edge.y2) / 2;
                    const anchorDist = Math.hypot(wx - (view.x + localX), wy - (view.y + contentY + localY));
                    if (!best || dist < best.dist - 0.4 || (Math.abs(dist - best.dist) <= 0.4 && anchorDist < best.anchorDist)) {
                      best = { edge, dist, anchorDist, localX, localY };
                    }
                  });
                  if (!best) {
                    setHighlightedSide((prev) => (prev && prev.objectId === view.id ? null : prev));
                    return;
                  }
                  const { edge, localX, localY } = best;
                  setHighlightedSide({
                    type: edge.type,
                    x: view.x + localX,
                    y: view.y + contentY + localY,
                    objectType: 'view',
                    objectId: view.id,
                    polyIndex: edge.polyIndex,
                    points: [
                      view.x + edge.x1,
                      view.y + contentY + edge.y1,
                      view.x + edge.x2,
                      view.y + contentY + edge.y2,
                    ],
                    local: { x: localX, y: contentY + localY },
                  });
                }}
                onMouseEnter={(event) => {
                  if (!isDimensionLineEnabled) showMoveCursor(event);
                }}
                onMouseLeave={(event) => {
                  resetCursor(event);
                  setHighlightedSide((prev) => (prev && prev.objectId === view.id ? null : prev));
                  setHighlightedPoint((prev) => (prev && prev.objectId === view.id ? null : prev));
                }}
                onClick={(event) => {
                  event.cancelBubble = true;
                  if (isDimensionLineEnabled) return;
                  setSelectedProjectedViewId(view.id);
                  setSelectedFittingId(null);
                  setSelectedCylinderId(null);
                  setSelectedDimensionId(null);
                }}
                onDragMove={(event) => {
                  const node = event.target;
                  const prevView = projectedViewsRef.current.find((item) => item.id === view.id);
                  const moveDx = prevView ? node.x() - prevView.x : 0;
                  const moveDy = prevView ? node.y() - prevView.y : 0;
                  if (moveDx !== 0 || moveDy !== 0) {
                    // Позиции, привязанные к этому виду, едут вместе с ним
                    setBalloons((prev) => prev.map((bb) => (
                      (bb.targets || []).some((tt) => tt.type === 'view' && tt.id === view.id)
                        ? { ...bb, x: bb.x + moveDx, y: bb.y + moveDy }
                        : bb
                    )));
                  }
                  projectedViewsRef.current = projectedViewsRef.current.map((item) => (
                    item.id === view.id ? { ...item, x: node.x(), y: node.y() } : item
                  ));
                  if (updateAllDimensionLinesRef.current) updateAllDimensionLinesRef.current();
                }}
                onDragEnd={(event) => {
                  const node = event.target;
                  setProjectedViews((prev) => prev.map((item) => (
                    item.id === view.id ? { ...item, x: node.x(), y: node.y() } : item
                  )));
                }}
              >
                {selected && (
                  <Rect
                    name="hitbox"
                    x={0}
                    y={0}
                    width={scene.width}
                    height={scene.height + 28}
                    stroke="#1565c0"
                    strokeWidth={1}
                    listening={false}
                  />
                )}
                <Rect
                  name="view-header"
                  x={0}
                  y={0}
                  width={Math.max(scene.width, viewTitleX + viewTitleWidth + 8)}
                  height={28}
                  fill="#1565c0"
                  opacity={0.01}
                  onMouseEnter={showMoveCursor}
                  onMouseLeave={resetCursor}
                  onClick={selectThisView}
                />
                <Text
                  name="hitbox"
                  x={4}
                  y={4}
                  text={'✥'}
                  fontSize={font + 4}
                  fontFamily="Arial"
                  fill="#1565c0"
                  onMouseEnter={showMoveCursor}
                  onMouseLeave={resetCursor}
                  onClick={selectThisView}
                />
                <Text
                  x={viewTitleX}
                  y={6}
                  text={viewTitle}
                  fontSize={font}
                  fontFamily={viewTitleFont}
                  fontStyle={viewTitleStyle}
                  fill="#000000"
                  onMouseEnter={showMoveCursor}
                  onMouseLeave={resetCursor}
                  onClick={selectThisView}
                />
                <Line
                  points={[viewTitleX, 6 + font + 1, viewTitleX + viewTitleWidth, 6 + font + 1]}
                  stroke="#000000"
                  strokeWidth={1}
                  listening={false}
                />
                <Group y={28}>
                  {(scene.polylines || []).map((points, index) => {
                    const hot = highlightedSide && highlightedSide.objectId === view.id && highlightedSide.polyIndex === index;
                    const quiet = (scene.quietLines || []).includes(index);
                    const wall = (scene.wallLines || []).includes(index);
                    if (quiet && !isDimensionLineEnabled) return null;
                    return (
                    <Line
                      key={`p-${index}`}
                      points={points}
                      stroke={hot || wall ? '#d32f2f' : '#000000'}
                      strokeWidth={hot ? 2 : 1}
                      strokeScaleEnabled={false}
                      hitStrokeWidth={isDimensionLineEnabled ? 24 : 8}
                    />
                    );
                  })}
                  {(snappingMode === 'points' || isDimensionLineEnabled) && getViewSnapPoints(scene).map((point, index) => {
                    if (point.guide && !isDimensionLineEnabled) return null;
                    const active = highlightedPoint
                      && highlightedPoint.objectId === view.id
                      && highlightedPoint.pointIndex === index;
                    return (
                      <Circle
                        key={`sp-${index}`}
                        name="snap-point"
                        x={point.x}
                        y={point.y}
                        radius={active ? 5 : 3}
                        fill={active ? '#ff0000' : 'green'}
                        listening={false}
                      />
                    );
                  })}
                  {scene.shapes.map((shape, index) => (
                    shape.kind === 'circle' ? (
                      <Circle
                        key={`s-${index}`}
                        x={shape.x}
                        y={shape.y}
                        radius={shape.r}
                        fill="#ffffff"
                        stroke="#000000"
                        strokeWidth={1}
                        strokeScaleEnabled={false}
                        listening={false}
                      />
                    ) : (
                      <Rect
                        key={`s-${index}`}
                        x={shape.x}
                        y={shape.y}
                        width={shape.w}
                        height={shape.h}
                        fill="#ffffff"
                        stroke="#000000"
                        strokeWidth={1}
                        strokeScaleEnabled={false}
                        listening={false}
                      />
                    )
                  ))}
                  {scene.dims.map((dim, index) => {
                    const top = Math.min(dim.y1, dim.y2);
                    const bottom = Math.max(dim.y1, dim.y2);
                    const mid = (top + bottom) / 2;
                    return (
                      <Group key={`d-${index}`} listening={false}>
                        <Line points={[dim.x, top, dim.x, bottom]} stroke="#212121" strokeWidth={1} listening={false} />
                        <Line points={[dim.x - 5, top, dim.x + 5, top]} stroke="#212121" strokeWidth={1} listening={false} />
                        <Line points={[dim.x - 5, bottom, dim.x + 5, bottom]} stroke="#212121" strokeWidth={1} listening={false} />
                        <Text
                          x={dim.x + 6}
                          y={mid - font / 2}
                          text={dim.text}
                          fontSize={font}
                          fontFamily="Arial"
                          fill="#212121"
                          listening={false}
                        />
                      </Group>
                    );
                  })}
                </Group>
              </Group>
            );
          })}
        </Layer>
        
        {/* Отдельный Layer для таблицы крепежей (без масштабирования) */}
        <Layer
          scaleX={1 / (zoom || 1)}
          scaleY={1 / (zoom || 1)}
        >
          {fastenersTableState !== null && (
            <FastenersTable
              fittings={fittings}
              isLandscape={isLandscape}
              zoom={zoom || 1}
              tableState={fastenersTableState}
              onTableStateChange={setFastenersTableState}
              isSelected={isFastenersTableSelected}
              onSelect={() => {
                setIsFastenersTableSelected(true);
                setSelectedAnnotationId(null);
                setSelectedBalloonId(null);
                setSelectedBalloonTarget(null);
                setSelectedCylinderId(null);
                setSelectedFittingId(null);
                setSelectedDimensionId(null);
                setContextMenu(null);
              }}
              onOpenDrawing={(key, rowItems) => {
                const items = rowItems || fittings.filter((item) => item.type === 'fastener' && item.itemKey === key && !item.includedInPrice);
                const seen = new Set();
                const variants = [];
                items.forEach((item) => {
                  const sig = `${item.clampDiameter ?? ''}|${item.axialDistance ?? ''}|${item.overhang ?? ''}|${item.fastenerHeight ?? ''}|${item.rangeOffset ?? ''}`;
                  if (seen.has(sig)) return;
                  seen.add(sig);
                  variants.push(item);
                });
                if (variants.length === 0) return;
                const params = new URLSearchParams({
                  fastenerSheet: '1',
                  key,
                  sizes: JSON.stringify(variants.map((item) => ({
                    d: item.clampDiameter ?? '',
                    a: item.axialDistance ?? '',
                    v: item.overhang ?? '',
                    h: item.fastenerHeight ?? '',
                    o: item.rangeOffset ?? '',
                  }))),
                });
                window.open(`${window.location.pathname}?${params.toString()}`, '_blank');
              }}
            />
          )}
          {workshopTableState !== null && (
            <WorkshopTable
              workshopInfo={workshopInfo}
              weldingTotal={weldingTotal}
              isLandscape={isLandscape}
              tableState={workshopTableState}
              onTableStateChange={setWorkshopTableState}
              isSelected={isWorkshopTableSelected}
              onSelect={() => {
                setIsWorkshopTableSelected(true);
                setIsFastenersTableSelected(false);
                setSelectedAnnotationId(null);
                setSelectedBalloonId(null);
                setSelectedBalloonTarget(null);
                setSelectedCylinderId(null);
                setSelectedFittingId(null);
                setSelectedDimensionId(null);
                setContextMenu(null);
              }}
            />
          )}
        </Layer>
      </Stage>
    {typeof selectedDimensionId === 'number' && dimensionLines[selectedDimensionId] && (
      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid #ccc',
          borderRadius: 4,
          padding: 8,
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 12 }}>Размер текста:</span>
        <select
          value={(dimensionLines[selectedDimensionId].textFontSize || 16)}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10) || 16;
            setDimensionLines((prev) => prev.map((d, i) => i === selectedDimensionId ? { ...d, textFontSize: v } : d));
          }}
        >
          {[10,12,14,16,18,20,24,28,32].map((s) => (
            <option key={s} value={s}>{s}px</option>
          ))}
        </select>
        <span style={{ fontSize: 12 }}>Точность:</span>
        <select
          value={(dimensionLines[selectedDimensionId].precision ?? 2)}
          onChange={(e) => {
            const p = Math.max(0, Math.min(2, parseInt(e.target.value, 10) || 0));
            setDimensionLines((prev) => prev.map((d, i) => {
              if (i !== selectedDimensionId) return d;
              const raw = d.rawValue != null ? Number(d.rawValue) : Number(d.value);
              const newValue = isNaN(raw) ? d.value : raw; // храним "сырое" число отдельно
              return { ...d, precision: p, rawValue: newValue };
            }));
          }}
        >
          {[0,1,2].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <span style={{ fontSize: 12 }}>Цвет:</span>
        <input type="color" value={dimensionLines[selectedDimensionId].textColor || '#ff0000'} onChange={(e) => {
          const color = e.target.value || '#ff0000';
          setDimensionLines((prev) => prev.map((d, i) => i === selectedDimensionId ? { ...d, textColor: color } : d));
        }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={!!dimensionLines[selectedDimensionId].overrideEnabled}
            onChange={(e) => {
              const checked = !!e.target.checked;
              setDimensionLines((prev) => prev.map((d, i) => i === selectedDimensionId ? { ...d, overrideEnabled: checked } : d));
            }}
          />
          <span style={{ fontSize: 12 }}>Изменённое значение</span>
        </label>
        <input
          type="text"
          disabled={!dimensionLines[selectedDimensionId].overrideEnabled}
          value={dimensionLines[selectedDimensionId].overrideText || ''}
          onChange={(e) => {
            const val = e.target.value;
            setDimensionLines((prev) => prev.map((d, i) => i === selectedDimensionId ? { ...d, overrideText: val } : d));
          }}
          placeholder="Текст/число"
          style={{ width: 120 }}
        />
        {/* Настройки отступов нижнего текста скрыты по просьбе пользователя (настраиваются в коде) */}
        {/* Нижнее значение */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={!!dimensionLines[selectedDimensionId].lowerOverrideEnabled}
            onChange={(e) => {
              const checked = !!e.target.checked;
              setDimensionLines((prev) => prev.map((d, i) => i === selectedDimensionId ? { ...d, lowerOverrideEnabled: checked } : d));
            }}
          />
          <span style={{ fontSize: 12 }}>Нижнее значение</span>
        </label>
        <textarea
          rows={3}
          disabled={!dimensionLines[selectedDimensionId].lowerOverrideEnabled}
          value={dimensionLines[selectedDimensionId].lowerOverrideText || ''}
          onChange={(e) => {
            const val = e.target.value;
            setDimensionLines((prev) => prev.map((d, i) => i === selectedDimensionId ? { ...d, lowerOverrideText: val } : d));
          }}
          placeholder="Нижнее значение"
          style={{ width: 180, resize: 'vertical' }}
        />
        <button onClick={() => setSelectedDimensionId(null)}>Закрыть</button>
      </div>
    )}
    {selectedBalloonId && (() => {
      const b = balloons.find((bb) => bb.id === selectedBalloonId);
      if (!b) return null;
      return (
        <div
          style={{
            position: 'absolute',
            top: 56,
            right: 8,
            background: 'rgba(255,255,255,0.95)',
            border: '1px solid #ccc',
            borderRadius: 4,
            padding: 8,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 12 }}>Размер:</span>
          <select
            value={b.fontSize || 16}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10) || 16;
              setBalloons((prev) => prev.map((bb) => bb.id === b.id ? { ...bb, fontSize: v } : bb));
            }}
          >
            {[10,12,14,16,18,20,24,28,32].map((s) => (
              <option key={s} value={s}>{s}px</option>
            ))}
          </select>
          <span style={{ fontSize: 12 }}>Стиль:</span>
          <select
            value={b.fontStyle || 'italic'}
            onChange={(e) => {
              const v = e.target.value || 'italic';
              setBalloons((prev) => prev.map((bb) => bb.id === b.id ? { ...bb, fontStyle: v } : bb));
            }}
          >
            {['normal','italic','bold','bold italic'].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <span style={{ fontSize: 12 }}>Цвет:</span>
          <input
            type="color"
            value={b.color || '#000000'}
            onChange={(e) => {
              const color = e.target.value || '#000000';
              setBalloons((prev) => prev.map((bb) => bb.id === b.id ? { ...bb, color } : bb));
            }}
          />
          <button onClick={() => setSelectedBalloonId(null)}>Закрыть</button>
        </div>
      );
    })()}
    {selectedAnnotationId && selectedAnnotation && (
      <div
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid #ccc',
          borderRadius: 4,
          padding: 8,
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 12 }}>Размер:</span>
        <select
          value={selectedAnnotation.fontSize || 14}
          onChange={(e) => {
            const newSize = parseInt(e.target.value, 10) || 14;
            setAnnotations((prev) => prev.map((a) => a.id === selectedAnnotationId ? { ...a, fontSize: newSize } : a));
          }}
        >
          {[10,12,14,16,18,20,24,28,32].map((s) => (
            <option key={s} value={s}>{s}px</option>
          ))}
        </select>
        <span style={{ fontSize: 12 }}>Выравнивание:</span>
        <button onClick={() => setAnnotations((prev) => prev.map((a) => a.id === selectedAnnotationId ? { ...a, align: 'left' } : a))}>Влево</button>
        <button onClick={() => setAnnotations((prev) => prev.map((a) => a.id === selectedAnnotationId ? { ...a, align: 'center' } : a))}>Центр</button>
        <button onClick={() => setAnnotations((prev) => prev.map((a) => a.id === selectedAnnotationId ? { ...a, align: 'right' } : a))}>Вправо</button>
        <span style={{ fontSize: 12 }}>Стиль:</span>
        <select
          value={selectedAnnotation.fontStyle || 'italic'}
          onChange={(e) => {
            const v = e.target.value || 'italic';
            setAnnotations((prev) => prev.map((a) => a.id === selectedAnnotationId ? { ...a, fontStyle: v } : a));
          }}
        >
          {['normal','italic','bold','bold italic'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <span style={{ fontSize: 12 }}>Цвет:</span>
        <input type="color" value={selectedAnnotation.fill || '#000000'} onChange={(e) => {
          const color = e.target.value || '#000000';
          setAnnotations((prev) => prev.map((a) => a.id === selectedAnnotationId ? { ...a, fill: color } : a));
        }} />
        <button onClick={() => setSelectedAnnotationId(null)}>Закрыть</button>
      </div>
    )}
      {contextMenu && (
        <div 
          style={{
            position: 'fixed', // Изменим на fixed
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 999
          }}
          onClick={() => setContextMenu(null)} // Закрываем меню при клике вне
        >
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onTransformClick={() => {
              handleTransformClick();
              setContextMenu(null);
            }}
            objectType={contextMenu.objectType}
            objectId={contextMenu.objectId}
            prices={prices}
            hasSelfFacing={contextMenu.hasSelfFacing}
            isSelfFacing={contextMenu.isSelfFacing}
            onRotateAroundAxis={() => {
              // текущий сценарий — просто активирует трансформацию/вращение
              handleTransformClick();
              setContextMenu(null);
            }}
            onRotateSelf={() => {
              if (contextMenu.objectType === 'fitting') {
                applySelfFacingToFitting(contextMenu.objectId);
              }
              setContextMenu(null);
            }}
            onSelectPipe={(payload) => {
              // Привязываем ближайший размер цилиндра к выбранному диаметру, если указан
              const cid = payload && payload.objectId;
              if (!cid) return;
              // Если выбрали покрытие, обновляем переключатель "Зеркало"
              if (payload.coating) {
                // Если переключаем на "Зеркало", проверяем наличие труб с только матовым покрытием
                if (payload.coating === 'Зеркало') {
                  // Проверяем наличие квадратных профилей
                  const hasSquareProfiles = cylinders.some(cyl => 
                    cyl.pipeSpec && cyl.pipeSpec.shape === 'square'
                  );
                  
                  if (hasSquareProfiles) {
                    setSquareProfileWarningDialog(true);
                    setContextMenu(null);
                    return;
                  }
                  
                  // Собираем все уникальные диаметры труб
                  const pipeDiameters = new Set();
                  cylinders.forEach(cyl => {
                    if (cyl.pipeSpec && cyl.pipeSpec.diameter) {
                      pipeDiameters.add(cyl.pipeSpec.diameter);
                    }
                  });
                  
                  // Проверяем, есть ли трубы без зеркального покрытия
                  let hasMatteOnly = false;
                  pipeDiameters.forEach(diameter => {
                    if (!hasZerkaloCoating(diameter)) {
                      hasMatteOnly = true;
                    }
                  });
                  
                  // Если есть трубы с только матовым покрытием, не переключаем аннотацию
                  if (!hasMatteOnly) {
                    setAnnotations((prev) => prev.map((a) => a.id === 'anno-init-4' ? { ...a, text: payload.coating } : a));
                  }
                } else {
                  // Для "Матовый" переключаем без проверки
                  setAnnotations((prev) => prev.map((a) => a.id === 'anno-init-4' ? { ...a, text: payload.coating } : a));
                }
              }
              setCylinders((prev) => prev.map((c) => {
                if (c.id !== cid) return c;
                const prevSpec = c.pipeSpec || {};
                const nextSpec = { ...prevSpec };
                if (payload.shape) nextSpec.shape = payload.shape;
                if (payload.diameter !== undefined) {
                  const nd = Number(payload.diameter);
                  if (Number.isFinite(nd)) nextSpec.diameter = nd;
                }
                if (payload.coating) nextSpec.coating = payload.coating;
                if (payload.type) nextSpec.squareType = payload.type; // profile|sheet
                if (payload.thickness) nextSpec.thickness = payload.thickness; // x2|x3 или 1.5|2
                if (payload.profile) nextSpec.profile = payload.profile; // 20x20, 25x25, etc.

                let newWidth = c.width;
                let newHeight = c.height;
                
                // Для круглых труб используем диаметр
                if (payload.shape === 'round') {
                  const target = (payload.diameter !== undefined) ? Number(payload.diameter) : (nextSpec.diameter !== undefined ? Number(nextSpec.diameter) : NaN);
                  if (Number.isFinite(target)) {
                    const dw = Math.abs((c.width || 0) - target);
                    const dh = Math.abs((c.height || 0) - target);
                    if (dw <= dh) {
                      newWidth = target;
                    } else {
                      newHeight = target;
                    }
                  }
                }
                
                // Для квадратных труб используем displaySize
                if (payload.shape === 'square' && payload.displaySize !== undefined) {
                  const target = Number(payload.displaySize);
                  if (Number.isFinite(target)) {
                    const dw = Math.abs((c.width || 0) - target);
                    const dh = Math.abs((c.height || 0) - target);
                    if (dw <= dh) {
                      newWidth = target;
                    } else {
                      newHeight = target;
                    }
                  }
                }

                return { ...c, width: newWidth, height: newHeight, pipeSpec: nextSpec };
              }));
              
              // Обновляем cylindersRef для синхронизации
              setCylinders((prev) => {
                cylindersRef.current = prev;
                
                // Обновляем линейный массив и привязанные объекты для измененной трубы
                const cylinder = prev.find(c => c.id === cid);
                if (cylinder && !cylinder.isPatternClone) {
                  setTimeout(() => {
                    rebuildClonesForBase('cylinder', cid);
                    // Обновляем привязанные объекты после изменения размера
                    updateConstrainedObjects(cid, 'cylinder');
                  }, 0);
                }
                
                return prev;
              });
              
              try { if (updateAllDimensionLinesRef.current) updateAllDimensionLinesRef.current(); } catch (_) {}
            }}
          />
        </div>
      )}
      
      {/* Диалог предупреждения о недоступности зеркального покрытия */}
      <Dialog
        open={mirrorWarningDialog.open}
        onClose={() => setMirrorWarningDialog({ open: false, unavailablePipes: [] })}
      >
        <DialogTitle>Недоступно зеркальное покрытие</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Следующие трубы не имеют зеркального покрытия в каталоге:
            <br /><br />
            {mirrorWarningDialog.unavailablePipes.map((pipe, index) => (
              <span key={index}>
                • Труба ⌀{pipe.diameter}мм (количество: {pipe.count})
                <br />
              </span>
            ))}
            <br />
            Добавить в стоимость отзеркаливание этих труб?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setMirrorWarningDialog({ open: false, unavailablePipes: [] });
            }}
            color="secondary"
          >
            Нет
          </Button>
          <Button 
            onClick={() => {
              // Переключаем покрытие на зеркало
              setAnnotations((prev) => prev.map((a) => a.id === 'anno-init-4' ? { ...a, text: 'Зеркало' } : a));
              
              // Обновляем покрытие труб (только те, у которых есть зеркало в каталоге)
              setCylinders((prev) => prev.map((c) => {
                if (c.pipeSpec) {
                  const diameter = c.pipeSpec.diameter;
                  
                  // Если у трубы нет зеркального покрытия в каталоге, оставляем матовым
                  if (diameter && !hasZerkaloCoating(diameter)) {
                    return c; // Не меняем покрытие
                  }
                  
                  return {
                    ...c,
                    pipeSpec: {
                      ...c.pipeSpec,
                      coating: 'Зеркало',
                    },
                  };
                }
                return c;
              }));
              
              // Добавляем строки "Слесарка за зеркало" для каждого недоступного диаметра
              // Эти данные будут переданы в PriceDialog через специальное поле
              const mirrorPolishingItems = mirrorWarningDialog.unavailablePipes.map(pipe => ({
                diameter: pipe.diameter,
                hours: 0, // Пользователь сможет ввести количество часов в PriceDialog
              }));
              
              // Сохраняем в localStorage для передачи в PriceDialog
              localStorage.setItem('mirrorPolishingItems', JSON.stringify(mirrorPolishingItems));
              if (typeof onObjectsChanged === 'function') onObjectsChanged();
              
              setMirrorWarningDialog({ open: false, unavailablePipes: [] });
            }}
            color="primary"
            variant="contained"
          >
            Да
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Диалог предупреждения о квадратных профилях */}
      <Dialog
        open={squareProfileWarningDialog}
        onClose={() => setSquareProfileWarningDialog(false)}
      >
        <DialogTitle>Зеркальное покрытие недоступно</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Изделия из квадратного профиля не могут быть изготовлены с зеркальным покрытием.
            <br /><br />
            Зеркальное покрытие применяется только к изделиям из круглых труб.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSquareProfileWarningDialog(false)}
            color="primary"
            variant="contained"
          >
            Понятно
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог настройки крепежа */}
      <FastenerConfigDialog
        open={fastenerDialogOpen}
        onClose={() => {
          setFastenerDialogOpen(false);
          setPendingFastener(null);
        }}
        onConfirm={(config) => {
          setFastenerDialogOpen(false);
          if (pendingFastener) {
            loadDxf(
              pendingFastener.fastenerKey,
              pendingFastener.x,
              pendingFastener.y,
              'fastener',
              config
            );
          }
          setPendingFastener(null);
        }}
        fastenerData={pendingFastener ? fastenersData[pendingFastener.fastenerKey] : null}
      />
    </div>
  );
}

export default Canvas;
